// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Projects
//  ----------------------------------------------------------------------------
//  What you are actually building, grouped by the part of life it belongs to.
//  Reached from the hero's More menu. The efforts are md-project.ts; the ten
//  contexts they sit under are md-domain.ts.
//
//  ── THIS SCREEN IS BUILT FROM THE APP'S OWN GRAMMAR, NOT INVENTED ──────────
//  The first cut of it borrowed md-settings.jsx, which says of itself that it is
//  "NOT designed, and expected to be replaced" — so it inherited the one surface
//  in the app that admits it is below the bar. Everything here now comes from
//  the pieces that ARE the bar: the hero card and the day card.
//
//    • a card is <Glass radius={20} tint={surf(0.07)} stroke={0.18}> with 13/14
//      padding, a 40px bordered tile, a 16px/600 title on ink(1) and 12.5px meta
//      on ink(0.6). Exactly what an event in the day is.
//    • a big number is the countdown's grammar: a tracked .md-label ABOVE at
//      11.5px/400 on ink(0.5), then .md-display at 23px on ink(1), tabular
//      numerals, and in DARK a coloured textShadow. The number is white; the
//      glow carries the hue. Coloured numerals and coloured bars are not in this
//      app's vocabulary and were the loudest thing about the first attempt.
//    • a rule is 1px of ink(light ? 0.07 : 0.09), which is what the hero uses
//      between the countdown and the week row.
//
//  ── THE HERO METRIC IS TIME SPENT ─────────────────────────────────────────
//  Because it cannot fall. A percentage, a completion rate or a streak each
//  eventually greets somebody with a verdict on a hard month; time given only
//  accumulates, so nothing turns red and a quiet season is simply a number that
//  stopped growing for a while. A project with none shows the app's own em-dash
//  for an unknown value, not a zero and not a scolding.
//
//  This is a choice about the hero, not a ban. The doctrine that forbade those
//  three outright was struck on 2026-08-18 (see md-project.ts's metric header);
//  a surface with a reason for a percentage may draw one. This card has none.
//
//  Prayers is not a stopwatch: it is the nominal prayer window times the prayers
//  you marked, and the card says so rather than implying a measurement.
//
//  A DOMAIN WITH NO PROJECTS IS NOT SHOWN. Ten headings with six empty is a
//  progress bar in disguise; an empty context is at rest, not neglected.
//
//  Exposes <ProjectsSpace> — the Workshop. See its own header, further down,
//  for what replaced the domain-grouped card list and why.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useStP, useEffect: useEfP, useMemo: useMeP, useRef: useRefP,
  useLayoutEffect: useLayEfP } = React;
const { useCallback: useCbP } = React;

/* ── D1 · what "too many to draw" means, in one place ──────────────────────
   WS_WINDOW_FLOOR is the count above which a body stops drawing everything and
   starts drawing a window. Below it nothing changes — see the note beside
   `windowed` in WsColumn for why a floor exists at all.

   WS_CARD_SEAT is what one card is worth vertically before it has been
   measured: 58 of card and 6 of the flex gap it sits above. It is an ESTIMATE
   and nothing rests on it being right — the virtualizer measures the real
   nodes as they mount and corrects itself. It only has to be close enough that
   the first paint does not jump.

   The virtualizer itself is @tanstack/react-virtual, published on the window by
   the build (scripts/build.mjs) and already driving six lists in
   md-database.jsx. Same library, same shape, so the two screens next door to
   each other cannot drift apart in how they window. */
const WS_WINDOW_FLOOR = 60;
const WS_CARD_SEAT = 64;
/* The list's two estimates: a row, and the heading that introduces a group. */
const WS_ROW_SEAT = 44;
const WS_GROUP_SEAT = 30;
const useVirtualP = (options) => {
  const hook = window.MDVirtual && window.MDVirtual.useVirtualizer;
  if (!hook) throw new Error('The virtualizer was not loaded before md-projects.jsx.');
  return hook(options);
};
/* The database's chrome, published by W-0 so a second screen is built from the
   same parts rather than a copy of them. md-database.jsx loads first
   (src/index.html), which is what makes this destructure safe.

   BEHAVIOUR, NOT LAYOUT. What is borrowed here is the things that must agree
   across the app — one escape stack, one way of silencing a background, one
   popover — and not the database's stylesheet, whose classes carry that
   screen's own geometry and whose light twins are scoped to its surface. This
   file paints in inline style objects like the rest of it, because in ManDana
   the inline style objects ARE the design system. The single borrowed painted
   thing is the menu, and it carries the `.md-db-light` scope W-2 opened. */
const { DbMenu, DbGlyph, DbCommittedInput, useDbEscapeLayer, useDbInertBackground, useDbNotice,
  dbNotify: dbNotifyP, dbMutationMessage: dbMessageP,
  useMdLayout: useLayoutP } = window;
const { Glass: GlassP, ink: inkP, surf: surfP, mdRgba: rgbaP, mdIsLight: isLightP, MDI: MDIP,
  onAcc: onAccP, LiveMarkdown: LiveMDP, MD_PRAYER_NOMINAL_MIN: NOMINAL_P,
  mdDomainColor: domColorP, mdDomainIcon: domIconP,
  mdDomainTextRead: domTextReadP, mdDomainTextWrite: domTextWriteP,
  mdProjectById: projByIdP, mdProjectCreate: projCreateP, mdProjectDelete: projDeleteP,
  mdProjectTextRead: projTextReadP, mdProjectTextWrite: projTextWriteP,
  mdProjectIndex: projectIndexP, mdProjectsByDomain: byDomainP, mdProjectStats: statsP,
  mdPursuitIndex: pursuitIndexP, mdPursuitById: pursuitByIdP, mdPursuitCreate: pursuitCreateP,
  mdPursuitTextRead: pursuitTextReadP, mdPursuitLinksRead: pursuitLinksReadP,
  mdPursuitTextWrite: pursuitTextWriteP,
  mdProjectRecordById: projectRecordByIdP, mdProjectRecordWrite: projectRecordWriteP } = window;

/* The list's one piece of real CSS. A hover wash cannot be an inline style,
   and this file owns no stylesheet, so it borrows md-database.jsx's trick: one
   guarded injected block, scoped to this surface. The wash COLOR stays a JS
   decision — the shell sets `--ws-hover` inline from inkP — so the sheet holds
   the selector and nothing else. The :focus-visible twin exists because a wash
   only a mouse can summon is invisible to a keyboard. The select rule exists
   because `.md-db-menu select` says `font:inherit` and this surface gives the
   control a 30px box — whoever fixes the height owns a type size that fits.

   Three surfaces take the wash, by three different mechanisms, and the reason
   is where the paint already lives:
     · the LIST row is a <button>, and a button shows a UA face unless someone
       says otherwise — so the sheet says it, in the same rule as the radius.
       Said inline it would outrank its own wash, which is exactly what it did;
     · the RAIL row belongs to the shared timeline (the database draws it too),
       so the rule is scoped to this space and stops at the window's edge;
     · the CARD carries its glass in an inline object (the PP-3 rule put it
       there), and inline beats a stylesheet — so the wash arrives as an
       ::after overlay the card cannot outrank, taking the card's own radius.
   While a gesture is in progress the space wears `data-md-dragging` and all
   three step aside: mid-carry the only thing worth saying is where the thing
   will land.

   Every rule here is paint. It did once carry one that was not: a card said it
   was not text, because a finger held on it for a fifth of a second is, to iOS,
   a request for the selection handles and the callout menu. That rule is gone
   from here — not repealed, promoted. It only ever covered the card, while the
   column head above it, the count beside it and the rail down the side were
   just as grabbable and just as selectable; and this whole space is portalled
   to document.body, so it was never inside the app root the old policy was
   keyed to. Selection now belongs to `html,body` in index.html, where nothing
   can escape it. Do not re-add a local copy. */
if (!document.getElementById('md-projects-styles')) {
  const style = document.createElement('style');
  style.id = 'md-projects-styles';
  style.textContent = `
[data-md-projects-space] [data-md-row]{background:transparent;border-radius:var(--md-radius-control,8px)}
[data-md-projects-space] [data-md-row]:hover{background:var(--ws-hover)}
[data-md-projects-space] [data-md-row]:focus-visible{background:var(--ws-hover)}
[data-md-projects-space] [data-md-rail-row]:hover{background:var(--ws-hover)}
[data-md-projects-space] [data-md-card]:hover::after,
[data-md-projects-space] [data-md-card]:focus-visible::after{content:'';position:absolute;inset:0;border-radius:inherit;background:var(--ws-hover);pointer-events:none}
[data-md-projects-space][data-md-dragging] [data-md-row]:hover,
[data-md-projects-space][data-md-dragging] [data-md-rail-row]:hover{background:transparent}
[data-md-projects-space][data-md-dragging] [data-md-card]:hover::after{display:none}
[data-md-projects-space][data-md-dragging]{cursor:grabbing}
[data-md-projects-space] .md-db-menu select{font-size:12.5px;line-height:1.3}
`;
  document.head.append(style);
}

/* The app's own glyph for a value that is not there yet — the countdown shows
   "—:—:—" while it waits for a location. A project nobody has given time to
   gets the same treatment: quiet, and plainly not a number. "0h" would be a
   score, and "Nothing yet" set at display size reads as a reprimand. */
const MD_NONE = '—';

/* Hours and minutes, never a decimal. "12h 40m" is a quantity of life; "12.67
   hours" is a reading off an instrument, and the difference between how those
   two feel is most of what this screen is for. */
function spentLabel(minutes) {
  const m = Math.max(0, Math.round(minutes || 0));
  if (!m) return MD_NONE;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (!h) return `${rest}m`;
  return rest ? `${h}h ${rest}m` : `${h}h`;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/* The 40px bordered tile every card in this app leads with — a wash of the hue
   at 0.16 inside a 0.75px edge at 0.34. It is where the colour lives, so the
   type never has to carry it. */
function Tile({ color, children, size = 40, radius = 13 }) {
  const light = isLightP();
  return (
    <span style={{ width: size, height: size, borderRadius: radius, flexShrink: 0,
      display: 'grid', placeItems: 'center',
      background: rgbaP(color, light ? 0.12 : 0.16),
      border: `0.75px solid ${rgbaP(color, light ? 0.28 : 0.34)}` }}>{children}</span>);
}

/* A quantity, in the hero countdown's grammar: the label tracked and small above
   it, the number in the display face on full ink, and — in dark only — a soft
   bloom of the hue behind it. That glow is the whole reason the numeral itself
   can stay white and calm. */
function Quantity({ label, value, color, size = 23, dim = false }) {
  const light = isLightP();
  return (
    <>
      <div className="md-label" style={{ color: inkP(0.5), fontSize: 11, fontWeight: 400 }}>{label}</div>
      <div className="md-display" style={{ color: dim ? inkP(0.3) : inkP(1), fontSize: size,
        lineHeight: 1.05, marginTop: 3, fontVariantNumeric: 'tabular-nums', letterSpacing: 0.3,
        textShadow: light || dim ? 'none' : `0 2px 18px ${rgbaP(color, 0.35)}` }}>{value}</div>
    </>);
}

const CHEVRON_P = (c) => MDIP.chevron(c, 10);

/* A hairline, at the two weights the hero card uses. */
function Rule() {
  const light = isLightP();
  return <div aria-hidden="true" style={{ height: 1, background: inkP(light ? 0.07 : 0.09) }} />;
}

/* ── where a project stands ─────────────────────────────────────────────────
   md-project.ts has carried `backlog · active · parked · done` since the field
   was written, and until now `mdProjectSetStatus` had NO CALLER ANYWHERE in the
   app: four words the engine knew and no screen ever said. This is the door.

   THE FIFTH ROW IS NOT A FIFTH WORD. Every project ever made carries
   `undefined` here, which the engine reads as "not saying", and somebody who
   set a status has to be able to unset it. So "Not saying" is a real pickable
   row that CLEARS the field, rather than a word stored to mean the absence of
   one. Clearing also returns the built-in to whatever the app shipped, which is
   the behaviour md-project.ts documents at length.

   SHAPE CARRIES THE MEANING, NOT COLOUR. The four marks are told apart with the
   hue turned off, because a coloured dot is a status only to somebody who can
   see the colour. Alpha carries emphasis on the app's own ink ladder, and the
   domain's hue is spent on exactly one mark — the live one — the same way the
   day card spends it on the hour you are in.

   THE ENGINE'S WORD IS THE ONE THAT SHOWS. An optimistic pill would paint a
   change md-project.ts is entitled to refuse — an agent actor, a full disk —
   and a control showing a state storage does not hold is a button that lies.
   So the pill re-reads after every write and renders whatever came back.

   The picker is a DbMenu, which means it shares the ONE escape stack rather
   than listening for the key itself: Escape closes this and stops, instead of
   taking the screen behind it down too. (W-0, md-database.jsx:262) */
/* THE WORD IS THE WORKFLOW'S, NEVER THIS FILE'S. `flow` is the workflow in
   force where the status is being drawn; omitted, it is the account's default,
   which is the right answer for every surface that resolves to it.

   Absence is not "nothing said" any more — it reads as the workflow's first
   Backlog status, because everything is somewhere (WF-2). A word from a
   vocabulary this workflow does not hold is shown as itself rather than as a
   blank: it is somebody's writing, and the app does not silently unsay it. */
const statusLabel = (value, flow = 'default') => {
  const found = value && window.mdWorkflowStatusById ? window.mdWorkflowStatusById(flow, value) : null;
  if (found) return found.name;
  if (value) return value.charAt(0).toUpperCase() + value.slice(1);
  const inbox = window.mdWorkflowInboxOf ? window.mdWorkflowInboxOf(flow) : null;
  return inbox ? inbox.name : '';
};

/* The category a word stands in, asked of the workflow that holds the word.
   Every mark below takes a category and never a status, so every caller that
   has only a word comes through here first. */
const statusCategoryOf = (value, flow = 'default') => {
  const found = value && window.mdWorkflowStatusById ? window.mdWorkflowStatusById(flow, value) : null;
  return found ? found.category : null;
};

/* A board column names a PLACE; the sheet that begins a project takes a WORD.
   Under spine the column is a category, and the project about to be begun does
   not exist yet to be asked which workflow it reads through — so the word is
   resolved against the default one, which is the workflow it will have the
   moment it does exist. */
const wsSeedStatus = (columnId, spine) => {
  if (!columnId || columnId === 'empty') return null;
  if (!spine) return columnId;
  const found = window.mdWorkflowCategoryLanding
    ? window.mdWorkflowCategoryLanding('default', columnId) : null;
  return found ? found.id : null;
};

/* The other word a project may say about itself. "No priority" rather than
   "Not saying" because the two are different silences: a status nobody has set
   is a project whose position is unstated, and a priority nobody has set is a
   project that is simply not ranked against the others.

   It survived the workflows pass untouched, and deliberately so — a priority is
   not a place on a board's spine, it is a rung. Nothing about it is
   customisable, so nothing about it belongs to a workflow. */
const MD_PRIORITY_NONE = 'No priority';
const MD_PRIORITY_LABELS = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };
const priorityLabel = (value) => (value
  ? MD_PRIORITY_LABELS[value] || (value.charAt(0).toUpperCase() + value.slice(1))
  : MD_PRIORITY_NONE);

/* THE CATEGORY OWNS THE SHAPE; THE USER OWNS THE NAME AND THE HUE. This is the
   line the whole feature stands on. A person may name twelve statuses, and the
   app still draws exactly SIX marks — one per spine category — each told apart
   with the hue turned OFF, which is the accessibility law this app already
   shipped and the reason customisation could be let in at all.

   Six, and the ladder is closed for ever: an empty ring for queued, a ring with
   its decision made for planned, half-filled for underway, two bars set down
   for paused, a tick for finished, a stroke through for let go. Two statuses in
   ONE category are told apart by name and hue, never by shape — a thirteenth
   silhouette is not a thing anybody can read at 13px.

   THE DOTTED RING IS GONE with "Not saying". It drew an absence pretending to
   be a choice, and there is no such state left to draw. A project that has said
   nothing wears the Backlog ring, exactly like the project that chose Backlog
   on purpose — because it is standing in the same place, and drawing the two
   differently would be the app remembering who typed and who did not.

   IT TAKES A CATEGORY, NEVER A STATUS. It cannot resolve a word, so it cannot
   resolve one against the wrong workflow while the label beside it resolves
   against the right one. Every caller has already asked where the thing stands;
   this draws that answer and nothing else. A category from a build with a
   seventh one draws EMPTY rather than borrowing a shape it does not own. */
function StatusMark({ category = null, color = null, size = 13, inline = false }) {
  /* THE HUE IS THE USER'S, ON ALL SIX (WR-5). Only In Progress ever honoured
     `color`, so somebody could set a colour on five of their six statuses and
     watch nothing happen — Phase 5 ruling 3 half-built, which reads as a broken
     control rather than as a decision.

     `color` is the STATUS's own colour and nothing else. It used to arrive as
     the domain accent from several callers, which meant the mark wore a hue
     that said something true about a different question. With no colour the
     mark falls back to today's ink, which is what every status without one has
     always drawn in.

     THE SHAPES DO NOT MOVE. Six silhouettes, told apart with the hue turned
     off — that is the law this decorates inside, and the a11y gate still holds
     it. Stronger ink for the two that mean the work is underway or finished;
     quiet ink for the four that mean it is waiting, set down, or let go. */
  const dim = color || inkP(0.5), strong = color || inkP(0.82);
  const at = category;
  const ring = (stroke) => <circle cx="8" cy="8" r="6" fill="none" stroke={stroke} strokeWidth="1.5" />;
  return <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16"
    style={inline
      ? { display: 'inline-block', verticalAlign: -2, marginInlineEnd: 5 }
      : { flex: 'none', display: 'block' }}>
    {at === 'backlog' && ring(dim)}
    {at === 'planned' && <>{ring(dim)}<circle cx="8" cy="8" r="2.2" fill={dim} /></>}
    {at === 'progress' && <>{ring(strong)}<path d="M8 2.6A5.4 5.4 0 0 1 8 13.4Z" fill={strong} /></>}
    {at === 'paused' && <>{ring(dim)}
      <rect x="5.9" y="5.2" width="1.5" height="5.6" rx="0.75" fill={dim} />
      <rect x="8.6" y="5.2" width="1.5" height="5.6" rx="0.75" fill={dim} /></>}
    {at === 'completed' && <>{ring(strong)}
      <path d="M5.3 8.2 7.2 10.1 10.8 6.2" fill="none" stroke={strong} strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" /></>}
    {at === 'cancelled' && <>{ring(dim)}
      <path d="M4.7 11.3 11.3 4.7" fill="none" stroke={dim} strokeWidth="1.6"
        strokeLinecap="round" /></>}
  </svg>;
}

/* Three bars, and how many of them are lit. Low is one, Medium two, High three,
   and Urgent fills the block — a rung you can read at 12px without a word beside
   it, which is what a board column head and a card cell both need.

   NO RED AND NO SIREN. The palette is ink and the row's own domain colour (the
   manifestation rule: black, white, purple, and the ten hues). An Urgent project
   is one you decided matters most, not an alarm the app is sounding at you. */
function PriorityMark({ priority, color, size = 13 }) {
  const dim = inkP(0.5), faint = inkP(0.26);
  const lit = priority === 'urgent' ? 3 : priority === 'high' ? 3
    : priority === 'medium' ? 2 : priority === 'low' ? 1 : 0;
  const ink = priority === 'urgent' ? color : priority ? dim : faint;
  const bar = (x, top, index) => <rect key={x} x={x} y={top} width="3" height={13 - top} rx="1"
    fill={ink} opacity={index < lit ? 1 : 0.3} />;
  return <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16"
    style={{ flex: 'none', display: 'block' }}>
    {priority === 'urgent'
      /* The one rung drawn as a block rather than a climb: three bars already
         say High, and a fourth bar would say "more than the tallest". */
      ? <rect x="3" y="3" width="10" height="10" rx="2.6" fill={color} opacity="0.9" />
      : <>{bar(2.5, 9, 0)}{bar(6.5, 6, 1)}{bar(10.5, 3, 2)}</>}
  </svg>;
}

/* The mark a group of projects wears, whichever surface draws the group. One
   function serves the board's column heads AND the list's group heads, so the
   same bucket can never wear two different faces. Status groups get the status
   ring; domain groups get the domain's own hue; anything else — a pursuit, the
   empty bucket — gets the quiet neutral dot. */
function wsAxisMark(axis, group, light, accent) {
  /* The column head wears the STATUS's colour (WR-5). The accent is the
     domain's, and a head tinted with it was answering a different question
     from the one the column asks. */
  if (axis === 'status') return <span data-md-group-mark style={{ flex: 'none', display: 'flex' }}>
    <StatusMark category={group.category || null} color={group.color || null} size={12} />
  </span>;
  if (axis === 'priority') return <span data-md-group-mark style={{ flex: 'none', display: 'flex' }}>
    <PriorityMark priority={group.id === 'empty' ? null : group.id} color={rgbaP(accent, 1)} size={12} />
  </span>;
  const color = axis === 'domain' && group.id !== 'empty' ? domColorP(group.id, light) : null;
  return <span data-md-group-mark aria-hidden="true" style={{ flex: 'none', width: 7, height: 7,
    borderRadius: 3, background: color ? rgbaP(color, 0.85) : inkP(0.22) }} />;
}

function ProjectStatus({ project, color = null, readOnly = false, compact = false, onChange = null }) {
  const light = isLightP();
  const accent = color || domColorP(project.domainId, light);
  const [status, setStatus] = useStP(project.status || null);
  const [open, setOpen] = useStP(false);
  /* The row this pill was handed can be replaced under it — a sync landing, a
     parent re-reading the index — and the pill must follow the row rather than
     keep showing the word it last wrote. */
  useEfP(() => { setStatus(project.status || null); }, [project.id, project.status]);

  /* The workflow this project reads through, and the status it RESOLVES to —
     which is a real status even when nothing is stored, because absence is
     Inbox now. The pill draws the resolved one; storage keeps the stored one. */
  const flow = window.mdWorkflowFor ? window.mdWorkflowFor('project', project.id).id : 'default';
  const here = window.mdWorkflowResolveStatus
    ? window.mdWorkflowResolveStatus(flow, status) : null;
  const resolved = here ? here.id : status;
  const label = here ? here.name : statusLabel(status, flow);
  const pick = (next) => {
    window.mdProjectSetStatus(project.id, next);
    const after = projByIdP(project.id);          // the engine's answer, not the click's
    setStatus((after && after.status) || null);
    setOpen(false);
    if (onChange) onChange(after);
  };
  const btn = useRefP(null);

  /* Two things the menu needs that only a database surface used to provide:
     --db-accent for its hover wash, and a LIGHT SCOPE. Without the second, the
     menu keeps its dark glass on a white page — the PP-3 defect, in the one
     place where a borrowed part is most likely to hide it. */
  return <span className={light ? 'md-db-light' : ''}
    style={{ position: 'relative', display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" ref={btn} onClick={() => { if (!readOnly) setOpen(!open); }}
      aria-label={`Status — ${label}`} aria-haspopup="listbox"
      aria-expanded={open ? 'true' : 'false'} aria-disabled={readOnly ? 'true' : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 26,
        padding: compact ? '3px 6px' : '4px 9px 4px 7px',
        border: `1px solid ${inkP(light ? 0.1 : 0.12)}`, borderRadius: 'var(--md-radius-control, 8px)',
        background: 'transparent', color: inkP(0.82), font: 'inherit', fontSize: 12.5,
        cursor: readOnly ? 'default' : 'pointer', transition: 'background .14s, border-color .14s' }}>
      <StatusMark category={here ? here.category : null} color={(here && here.color) || null} />
      {!compact && <span style={{ whiteSpace: 'nowrap' }}>{label}</span>}
    </button>
    {open && <DbMenu title="Status" onClose={() => setOpen(false)} anchor={btn}>
      <div role="listbox" aria-label="Status">
        {/* The project's OWN vocabulary, and no "Not saying" row under it: an
            absence is not a choice, and Inbox is the choice it was pretending
            to be. Picking Inbox stores nothing — md-project.ts sees to that. */}
        {(window.mdProjectStatuses ? window.mdProjectStatuses(project.id) : []).map((row) => {
          const on = resolved === row.id;
          return <button key={row.id} type="button" role="option" aria-selected={on ? 'true' : 'false'}
            className="md-db-menu-row" onClick={() => pick(row.id)}>
            <StatusMark category={row.category} color={row.color || null} />
            <span>{row.name}</span>
            {on ? MDIP.check(inkP(0.55), 13) : null}
          </button>;
        })}
      </div>
    </DbMenu>}
  </span>;
}

/* The priority pill, and it is ProjectStatus with one word changed — same box,
   same 26px minimum, same borrowed menu on the one escape stack, same rule
   about following the row rather than the last click. Written as a twin rather
   than as a `field` parameter on one component because the two differ in every
   label, every glyph and every setter, and a component with four ternaries in
   it reads as neither control. */
function ProjectPriority({ project, color = null, readOnly = false, compact = false, onChange = null }) {
  const light = isLightP();
  const accent = color || domColorP(project.domainId, light);
  const read = (row) => (row && window.mdProjectPriority ? window.mdProjectPriority(row) : null);
  const [priority, setPriority] = useStP(() => read(project));
  const [open, setOpen] = useStP(false);
  useEfP(() => { setPriority(read(project)); }, [project.id, project.priority]);

  const label = priorityLabel(priority);
  const pick = (next) => {
    window.mdProjectSetPriority(project.id, next);
    const after = projByIdP(project.id);          // the engine's answer, not the click's
    setPriority(read(after));
    setOpen(false);
    if (onChange) onChange(after);
  };
  const btn = useRefP(null);

  return <span className={light ? 'md-db-light' : ''}
    style={{ position: 'relative', display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" ref={btn} onClick={() => { if (!readOnly) setOpen(!open); }}
      aria-label={`Priority — ${label}`} aria-haspopup="listbox"
      aria-expanded={open ? 'true' : 'false'} aria-disabled={readOnly ? 'true' : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 26,
        padding: compact ? '3px 6px' : '4px 9px 4px 7px',
        border: `1px solid ${inkP(light ? 0.1 : 0.12)}`, borderRadius: 'var(--md-radius-control, 8px)',
        background: 'transparent', color: inkP(priority ? 0.82 : 0.5), font: 'inherit', fontSize: 12.5,
        cursor: readOnly ? 'default' : 'pointer', transition: 'background .14s, border-color .14s' }}>
      <PriorityMark priority={priority} color={accent} />
      {!compact && <span style={{ whiteSpace: 'nowrap' }}>{label}</span>}
    </button>
    {open && <DbMenu title="Priority" onClose={() => setOpen(false)} anchor={btn}>
      <div role="listbox" aria-label="Priority">
        {[...(window.MD_PROJECT_PRIORITIES || []), null].map((value) => {
          const on = (priority || null) === value;
          return <button key={value || 'none'} type="button" role="option" aria-selected={on ? 'true' : 'false'}
            className="md-db-menu-row" onClick={() => pick(value)}>
            <PriorityMark priority={value} color={accent} />
            <span>{priorityLabel(value)}</span>
            {on ? MDIP.check(inkP(0.55), 13) : null}
          </button>;
        })}
      </div>
    </DbMenu>}
  </span>;
}

function ScopedBoards({ boards, scopeRef, color, onOpen, onNew }) {
  const rows = (boards || []).filter((board) => board &&
    (board.scopeRef === scopeRef || (scopeRef.startsWith('project:') && board.pid === scopeRef.slice(8))));
  return <div style={{ marginTop: 28 }}>
    <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px 8px' }}>
      <span className="md-label" style={{ color: inkP(.5), fontSize: 11, fontWeight: 400 }}>Desks</span>
      <span style={{ color: inkP(.3), fontSize: 11.5, marginInlineStart: 7 }}>{rows.length}</span>
      <span style={{ flex: 1 }} />
      {onNew && <button onClick={() => onNew(scopeRef)} aria-label="New desk"
        style={{ border: 'none', background: 'transparent', color, cursor: 'pointer', padding: 4,
          display: 'flex', borderRadius: 10 }}>{MDIP.plus(color, 17)}</button>}
    </div>
    {rows.map((board) => <button key={board.id} onClick={() => onOpen && onOpen(board.id)} {...pressP}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 11, padding: '11px 4px',
        border: 'none', borderBottom: `1px solid ${inkP(.06)}`, background: 'transparent',
        color: inkP(1), cursor: 'pointer', textAlign: 'start', transition: 'transform .14s ease' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: rgbaP(color, .72), flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 550,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{board.name}</span>
      <span style={{ display: 'flex' }}>{CHEVRON_P(inkP(.24))}</span>
    </button>)}
    {!rows.length && onNew && <button onClick={() => onNew(scopeRef)} style={{ border: 'none',
      background: 'transparent', color: inkP(.48), cursor: 'pointer', padding: '10px 4px',
      fontSize: 13.5 }}>Open a desk for this space</button>}
  </div>;
}

const pressP = {
  onPointerDown: (e) => { e.currentTarget.style.transform = 'scale(0.985)'; },
  onPointerUp: (e) => { e.currentTarget.style.transform = 'scale(1)'; },
  onPointerLeave: (e) => { e.currentTarget.style.transform = 'scale(1)'; },
};

/* ── one project ─────────────────────────────────────────────────────────── */
function ProjectCard({ project, stats, color, onOpen }) {
  const spent = spentLabel(stats.minutes);
  const quiet = stats.minutes === 0;
  /* Where it stands, in ITS workflow's word — resolved once for the mark, the
     line and the label a screen reader hears, so the three cannot disagree. */
  const stood = window.mdProjectStatusOf ? window.mdProjectStatusOf(project.id) : null;
  return (
    <button onClick={onOpen} {...pressP}
      aria-label={`${project.name} — ${project.status && stood ? stood.name + ', ' : ''}${quiet ? 'no time yet' : spent + ' spent'}, ${plural(stats.records, 'record', 'records')}, ${plural(stats.boards, 'desk', 'desks')}`}
      style={{ display: 'block', width: '100%', textAlign: 'start', border: 'none', padding: 0,
        background: 'transparent', cursor: 'pointer', marginBottom: 9,
        transition: 'transform .14s ease' }}>
      <GlassP radius={20} tint={surfP(0.07)} stroke={0.18} style={{ padding: '13px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Tile color={color}>{domIconP(project.domainId, color, 18)}</Tile>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, minWidth: 0, color: inkP(1), fontSize: 16, fontWeight: 600,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.name}</span>
              <span style={{ flexShrink: 0, display: 'flex' }}>{CHEVRON_P(inkP(0.3))}</span>
            </div>

            <div style={{ marginTop: 9 }}>
              <Quantity label="Time spent" value={spent} color={color} dim={quiet} />
            </div>

            {/* The card is itself a button, so the status is a MARK here and a
                control only on the project's own screen — a button inside a
                button is neither. A project that has said nothing shows
                nothing: an absence is not a state worth a line of its own. */}
            {/* Its own line rather than another clause on the counts line. The
                counts line is already long enough to wrap on a narrow card,
                and joining them with a middot leaves the separator stranded at
                the end of a line with nothing after it. Where a project stands
                is a different KIND of fact from how much of it there is. */}
            {project.status && stood && <div style={{ color: inkP(0.6), fontSize: 12.5, marginTop: 7 }}>
              <StatusMark category={stood.category} color={stood.color || null} size={12} inline />
              {stood.name}
            </div>}
            <div style={{ color: inkP(0.6), fontSize: 12.5, marginTop: 7 }}>
              {plural(stats.records, 'record', 'records')} · {plural(stats.boards, 'desk', 'desks')}
              {project.builtin ? ` · at ~${NOMINAL_P || 30} min a prayer` : ''}
            </div>
          </div>
        </div>
      </GlassP>
    </button>);
}

function PursuitCard({ pursuit, projects, ctx, color, onOpen }) {
  const totals = projects.reduce((sum, project) => {
    const stats = statsP(project.id, ctx);
    return { minutes: sum.minutes + stats.minutes, records: sum.records + stats.records };
  }, { minutes: 0, records: 0 });
  return (
    <button onClick={onOpen} {...pressP}
      aria-label={`${pursuit.name} pursuit — ${plural(projects.length, 'project', 'projects')}`}
      style={{ display: 'block', width: '100%', textAlign: 'start', border: 'none', padding: 0,
        background: 'transparent', cursor: 'pointer', marginBottom: 10, transition: 'transform .16s ease' }}>
      <GlassP radius={22} tint={surfP(0.075)} stroke={0.16} style={{ padding: '15px 15px 14px' }}>
        <div style={{ display: 'flex', gap: 13, alignItems: 'center' }}>
          <Tile color={color} size={42} radius={15}>{MDIP.orbit ? MDIP.orbit(color, 19) : domIconP(pursuit.domainId, color, 18)}</Tile>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: inkP(1), fontSize: 16.5, fontWeight: 620,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pursuit.name}</div>
            <div style={{ color: inkP(0.48), fontSize: 12.5, marginTop: 5 }}>
              {plural(projects.length, 'project', 'projects')} · {plural(totals.records, 'record', 'records')}
              {totals.minutes ? ` · ${spentLabel(totals.minutes)} given` : ''}
            </div>
          </div>
          <span style={{ display: 'flex' }}>{CHEVRON_P(inkP(0.28))}</span>
        </div>
      </GlassP>
    </button>);
}

/* ── a sub-screen's header ───────────────────────────────────────────────── */
function SubHeader({ onBack, title, tile, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 0 16px' }}>
      <button onClick={onBack} aria-label="Back"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer',
          width: 32, height: 32, marginInlineStart: -6, display: 'grid', placeItems: 'center',
          transition: 'transform .15s ease' }}
        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.88)'}
        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
        {MDIP.back(inkP(0.7), 21)}
      </button>
      {tile && <Tile color={color} size={30} radius={10}>{tile}</Tile>}
      <span style={{ color: inkP(1), fontSize: 18, fontWeight: 620, minWidth: 0,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
    </div>);
}

/* ── the head every record wears ────────────────────────────────────────────
   W-11. Three levels — project, pursuit, domain — and one shape: what this is,
   what it is called, then a HORIZONTAL ROW OF PROPERTIES, then the writing.

   Before this, a project's facts were scattered across the app and most of them
   could only be read. The pursuit was unclickable text. The domain was settable
   from nowhere at all. The target date lived behind the compose sheet's When
   overlay, two screens away from the project it belonged to. A screen that
   shows you a fact and gives you no way to change it is a screen that sends you
   looking for the place where you can.

   WHAT A LEVEL DOES NOT HAVE, IT DOES NOT SHOW. A pursuit has no status and no
   dates — md-pursuit.ts says so in its first paragraph — so its row simply has
   four fewer things in it. A greyed-out Status on a pursuit would be the app
   claiming a pursuit could stand somewhere and just happens not to.

   EVERY EDIT GOES THROUGH A `Try` DOOR. A refusal arrives with a reason
   (`wsCommit` turns it into words) rather than as a control that quietly does
   nothing, which is the one behaviour that teaches somebody to stop trusting a
   screen. */
function WsProp({ name, label, children }) {
  return <div data-md-prop={name} style={{ display: 'flex', flexDirection: 'column', gap: 3,
    minWidth: 0 }}>
    <span className="md-label" style={{ color: inkP(0.38), fontSize: 10, fontWeight: 500,
      letterSpacing: '.04em', textTransform: 'uppercase' }}>{label}</span>
    {children}
  </div>;
}

/* The value of a property that is one of a list. Painted here rather than
   borrowed: `DbOptionPicker` carries the database's own geometry, and its light
   twins are scoped to `.md-db-surface.is-light` (the PP-3 trap). What IS
   borrowed is the menu — so this shares the one escape stack instead of
   listening for the key itself.

   The menu's last row OPENS what the chip names. It is the only door left to a
   pursuit or a domain now that the Workshop's rows are projects, and it is the
   honest place for it: the control that says which pursuit holds this project
   is the control that takes you to it. */
function WsPick({ label, value, options, onPick, accent, onOpen = null, none = MD_NONE,
  suffix = '', extra = null }) {
  const light = isLightP();
  const [open, setOpen] = useStP(false);
  const [query, setQuery] = useStP('');
  const btn = useRefP(null);
  const shown = options.find((option) => option.id === (value || null));
  /* A chip that names WHERE the value came from (WF-12) says it in a quieter
     ink after a middle dot, and says it again inside the label — a suffix only
     the sighted half of the room can read is half a sentence. */
  const said = shown ? shown.name : none;
  /* Nine pursuits scroll; sixty-five projects do not. The search appears when
     the list stops being scannable, which is the same threshold DbOptionPicker
     answers with — one grammar for "find the one you mean". */
  const needle = query.trim().toLocaleLowerCase();
  const listed = needle
    ? options.filter((option) => option.name.toLocaleLowerCase().includes(needle))
    : options;
  return <span className={light ? 'md-db-light' : ''}
    style={{ position: 'relative', display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" ref={btn} onClick={() => { setQuery(''); setOpen(!open); }} aria-haspopup="listbox"
      aria-expanded={open ? 'true' : 'false'}
      aria-label={suffix ? `${label} — ${said}, ${suffix}` : `${label} — ${said}`}
      style={{ display: 'inline-flex', alignItems: 'center', minHeight: 26,
        maxWidth: suffix ? 260 : 190,
        padding: '3px 7px', marginInlineStart: -7, border: 'none',
        borderRadius: 'var(--md-radius-control, 8px)', background: 'transparent',
        color: inkP(shown ? 0.84 : 0.36), font: 'inherit', fontSize: 13, cursor: 'pointer',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {said}
      {suffix ? <span aria-hidden="true" style={{ color: inkP(0.36), marginInlineStart: 5 }}>
        · {suffix}</span> : null}
    </button>
    {/* Anchored to the chip's START, not its end. `.md-db-menu` opens leftwards
        because in a database it hangs off a toolbar at the right; a property
        chip sits at the surface's leading edge, and a right-aligned menu spills
        off it. `left`/`right` are cleared first so the logical side wins in
        both directions. A long list of projects scrolls rather than growing
        past the window. */}
    {open && <DbMenu title={label} onClose={() => setOpen(false)} anchor={btn}
      style={{ position: 'absolute', top: 30, left: 'auto', right: 'auto', insetInlineStart: -7,
        maxHeight: 320, overflowY: 'auto', zIndex: 40 }}>
      {/* Painted here rather than borrowed: `.md-db-menu-search` fills itself
          with 75% of `--db-accent`, which on a database's one purple reads as a
          tinted field and on a domain's rose reads as an alarm. PP-3 again —
          borrow the behaviour, paint the surface. */}
      {options.length > 10 && <input autoFocus value={query}
        aria-label={`Find a ${label.toLocaleLowerCase()}`} placeholder={`Find a ${label.toLocaleLowerCase()}…`}
        onChange={(event) => setQuery(event.target.value)}
        style={{ width: 'calc(100% - 10px)', height: 32, margin: '2px 5px 6px', border: 'none',
          borderRadius: 'var(--md-radius-control, 8px)', outline: 'none', padding: '0 9px',
          color: inkP(0.9), background: inkP(light ? 0.05 : 0.07), font: 'inherit', fontSize: 12.5 }} />}
      <div role="listbox" aria-label={label}>
        {!listed.length && <div style={{ padding: '8px 10px 10px', color: inkP(0.4), fontSize: 12.5 }}>
          Nothing by that name.</div>}
        {listed.map((option) => {
          const on = (value || null) === option.id;
          return <button key={option.id || 'none'} type="button" role="option"
            aria-selected={on ? 'true' : 'false'} className="md-db-menu-row"
            onClick={() => { onPick(option.id); setOpen(false); }}>
            <span>{option.name}</span>{on ? MDIP.check(inkP(0.55), 13) : null}
          </button>;
        })}
      </div>
      {onOpen && shown && shown.id && <button type="button" className="md-db-menu-row"
        onClick={() => { setOpen(false); onOpen(shown.id); }}>
        <span>Open {shown.name}</span>
      </button>}
      {/* A row that is a GESTURE rather than a value — "Use inherited" clears
          an assignment, and offering it beside the workflows as if it were one
          more workflow would make a menu where picking the same thing twice
          means two different things. */}
      {(extra || []).map((row) => <button key={row.label} type="button" className="md-db-menu-row"
        onClick={() => { setOpen(false); row.run(); }}>
        <span>{row.label}</span>
      </button>)}
    </DbMenu>}
  </span>;
}

/* Which vocabulary this thing speaks — WF-12.

   Settings' Workflows page can say this for everything at once; this chip says
   it for the one thing you are looking at, because the app's own law is that
   the chip which NAMES a thing is the door to it.

   It names the level that answered — "Spiritual Track · from Health" — and
   that second half is the whole reason `mdWorkflowSourceFor` exists rather
   than just `mdWorkflowFor`. Without it, a project carrying its own assignment
   and a project quietly following its domain draw identically, and only part
   ways later, when the domain changes and one of them does not.

   No source is named when the answer came from here: "Spiritual Track · from
   itself" is a sentence nobody needs to read, and the missing suffix is
   precisely what says "this one is mine".

   USE INHERITED CLEARS. Writing the resolved workflow explicitly would look
   the same today and stop following the parent tomorrow — the exact difference
   the chip was built to show. It appears only where there is an assignment to
   clear, because on a node already inheriting it is a row that does nothing.

   A pursuit gets this and still no status: md-pursuit.ts says a pursuit has no
   status by law, and this makes it a CARRIER of a workflow, never a wearer. */
function WsWorkflow({ kind, id, accent, onChange }) {
  const source = window.mdWorkflowSourceFor(kind, id);
  const own = window.mdWorkflowUse(kind, id);
  const parent = source.from === 'domain'
    ? (window.mdDomainById && window.mdDomainById(source.fromId) || {}).label
    : source.from === 'pursuit' && source.fromId !== id
      ? (pursuitByIdP && pursuitByIdP(source.fromId) || {}).name
      : '';
  const again = () => { if (onChange) onChange(); };
  return <WsPick label="Workflow" accent={accent} value={source.workflowId}
    options={(window.mdWorkflowIndex() || []).map((flow) => ({ id: flow.id, name: flow.name }))}
    suffix={parent ? `from ${parent}` : ''}
    extra={own ? [{ label: 'Use inherited',
      run: () => { wsCommit(window.mdWorkflowTrySetUse(kind, id, null)); again(); } }] : null}
    onPick={(next) => { wsCommit(window.mdWorkflowTrySetUse(kind, id, next)); again(); }} />;
}

/* A day, committed rather than live: a half-typed date is not a date, and a
   `change` per keystroke would ask md-project.ts to refuse `2026-0` four times
   on the way to a real one. `DbCommittedInput` is the app's existing answer to
   that and it already restores a draft the engine sent back. */
function WsDay({ label, value, commit }) {
  const light = isLightP();
  /* An empty date input says `dd/mm/yyyy` in the browser's own voice, which is
     the loudest thing on a screen whose word for a value that is not there yet
     is `—`. The field keeps its native picker; the text is simply turned
     invisible while it has nothing to say, and the app's own em-dash sits over
     it. Not a placeholder attribute: `type="date"` ignores those. */
  return <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <DbCommittedInput type="date" aria-label={label} value={value || ''} commit={commit}
      style={{ minHeight: 26, width: 132, padding: '3px 6px', marginInlineStart: -6, border: 'none',
        borderRadius: 'var(--md-radius-control, 8px)', background: 'transparent',
        color: value ? inkP(0.84) : 'transparent', font: 'inherit', fontSize: 13,
        colorScheme: light ? 'light' : 'dark' }} />
    {!value && <span aria-hidden="true" style={{ position: 'absolute', insetInlineStart: 0,
      color: inkP(0.36), fontSize: 13, pointerEvents: 'none' }}>{MD_NONE}</span>}
  </span>;
}

/* ── how far a delete reaches, said before it happens ───────────────────────
   2.6. A confirmation asking "are you sure?" is asking a question it has
   refused to answer: the one fact that decides it is what ELSE goes.

   IT IS COMPUTABLE ONLY BECAUSE CONTAINMENT IS A TREE. md-contain.ts wrote the
   reason down: with two parents the sentence would have to end "…unless
   something else still holds them", which is a delete whose outcome depends on
   state the person cannot see. One parent, one walk, one number.

   THE COUNT AND THE VERB ARE SEPARATE FACTS. The count is the containment
   subtree, and it is the same number whatever a delete does with it. The verb
   is what a delete DOES: a low-level removal may take only its root, while the
   Trash door moves the root and its subtree together. The caller therefore
   supplies `cascade` from the door it is about to use, so the sentence always
   describes what the confirmed action will actually do.

   THE WORDS ARE A PURE FUNCTION, apart from the component, for the reason
   md-transfer.jsx gave an hour ago: this is a sentence somebody reads once,
   under pressure, before doing something they cannot undo, and it should be
   arguable in a test rather than discovered on their screen. */

/* Every kind, in the order a sentence names them — containers first, leaves
   last — and the ONE WORD the app uses for each.

   EVERY LEAF IS A RECORD. A note, a task and a prayer are three values of a
   type field and one word to the person deleting them; counting them as three
   kinds would be the app talking about its own storage at the moment somebody
   is deciding whether to lose work. The spine kinds keep their own names,
   because those are the words on the screen they are standing on. */
const WS_DELETE_KINDS = [
  { bucket: 'pursuits', from: ['pursuit'], one: 'pursuit', many: 'pursuits' },
  { bucket: 'projects', from: ['project'], one: 'project', many: 'projects' },
  { bucket: 'desks', from: ['board'], one: 'desk', many: 'desks' },
  { bucket: 'databases', from: ['database'], one: 'database', many: 'databases' },
  { bucket: 'Crafts', from: ['craft', 'manifest'], one: 'Craft', many: 'Crafts' },
  /* Last, and matching nothing by name: it is where an unknown kind lands, so
     a newer client's record is counted rather than dropped from the total. */
  { bucket: 'records', from: [], one: 'record', many: 'records' },
];

/** What a delete would reach, counted by kind, the thing itself excluded.

    Reads the containment tree through md-parent.ts, which is the only door
    that knows the five places containment is spelled. */
function mdDeleteReach(ref) {
  const walk = window.mdParentSubtree ? window.mdParentSubtree(ref) : [];
  const counts = {};
  /* Root first, always (md-contain.ts:190) — and the root is the thing being
     deleted, never something the delete also reaches. */
  for (const key of (Array.isArray(walk) ? walk.slice(1) : [])) {
    if (typeof key !== 'string') continue;
    const at = key.indexOf(':');
    const type = at > 0 ? key.slice(0, at) : '';
    const kind = WS_DELETE_KINDS.find((row) => row.from.indexOf(type) >= 0)
      || WS_DELETE_KINDS[WS_DELETE_KINDS.length - 1];
    counts[kind.bucket] = (counts[kind.bucket] || 0) + 1;
  }
  return counts;
}

/** The whole confirmation, in words: `{message, reach, linked, held}`.

    `opening` is the kind's own first sentence — the two are said differently
    on purpose, because one of them is reversible and the other is not.
    `counts` is mdDeleteReach's. `links` is how many places point at it.
    `cascade` is whether the delete takes the subtree with it. `kept` is how
    many of its files another account still holds.

    `kept` IS `collected:false`, WHICH IS THE ORDINARY ANSWER. Two accounts
    holding one file is one object with two holds — the whole point of naming
    bytes by their content — so mdBlobRelease answering "not collected" is the
    system working, not failing, and the sentence says the file stays theirs
    rather than reporting a refusal. Nothing supplies the number yet: release
    on delete is story 2.2, and until it lands this stays 0 and the sentence
    stays unsaid rather than guessed at.

    Pure: no store, no clock, no DOM. */
function mdDeleteSay(what) {
  const w = what && typeof what === 'object' ? what : {};
  const counts = w.counts && typeof w.counts === 'object' ? w.counts : {};
  const parts = [];
  let total = 0;
  for (const kind of WS_DELETE_KINDS) {
    const n = Math.max(0, Math.floor(Number(counts[kind.bucket]) || 0));
    if (!n) continue;
    parts.push(`${n} ${n === 1 ? kind.one : kind.many}`);
    total += n;
  }
  /* "4 projects, 1 desk and 37 records" — the last join is a word rather than
     a comma, because this is a sentence and not a table. */
  const list = parts.length > 1
    ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
    : (parts[0] || '');
  /* `qualifier` says what is nevertheless different after a non-cascading
     removal. A cascading Trash move omits it because every named member moves
     with the root and there is no surviving remainder to qualify. */
  const qualifier = typeof w.qualifier === 'string' && w.qualifier ? `, ${w.qualifier}` : '';
  const reach = !list ? ''
    : w.cascade ? `This also deletes ${list}.`
      : `The ${list} under it ${total === 1 ? 'stays where it is' : 'stay where they are'}${qualifier}.`;

  const links = Math.max(0, Math.floor(Number(w.links) || 0));
  const linked = links ? `It is linked from ${links} ${links === 1 ? 'place' : 'places'}.` : '';

  const kept = Math.max(0, Math.floor(Number(w.kept) || 0));
  const held = !kept ? ''
    : kept === 1 ? '1 file is also held by another account and stays theirs.'
      : `${kept} files are also held by another account and stay theirs.`;

  /* THE WINDOW, LAST, BECAUSE IT IS THE REASSURANCE AND NOT THE WARNING. A
     delete is a move to Trash held for thirty days, so the sentence that used
     to end "this cannot be undone" now ends the other way round. Opt-in: a
     built-in project is hidden rather than deleted and has no such window, and
     a sentence promising one there would be false. */
  const buffer = w.buffer ? `You have ${window.MD_TRASH_DAYS || 30} days to change your mind.` : '';

  const opening = typeof w.opening === 'string' ? w.opening : '';
  return { message: [opening, reach, linked, held, buffer].filter(Boolean).join(' '),
    reach, linked, held, buffer };
}

/* ── the actions a record has ───────────────────────────────────────────────
   W-12. Closing, reopening and deleting were things md-project.ts and
   md-pursuit.ts had always been able to do and had never once offered — seven
   mutators with no caller anywhere in the app. Phase 3.5 ruled that a button
   which lies is a defect; this is that ruling from the other side.

   They live behind `…` rather than in the properties row because they are not
   properties: closing is not a fact about the project, it is something you do
   to it, and a destructive one belongs where a hand does not land by accident.
   A DESTRUCTIVE DOOR ASKS FIRST, through the app's own ConfirmDialog. */
function WsActions({ label, accent, items }) {
  const light = isLightP();
  const [open, setOpen] = useStP(false);
  const [asking, setAsking] = useStP(null);
  const btn = useRefP(null);
  const rows = items.filter(Boolean);
  if (!rows.length) return null;
  return <span className={light ? 'md-db-light' : ''}
    style={{ position: 'relative', display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" ref={btn} aria-label={label} aria-haspopup="menu"
      aria-expanded={open ? 'true' : 'false'} onClick={() => setOpen(!open)}
      style={{ width: 30, height: 30, display: 'grid', placeItems: 'center', border: 'none',
        borderRadius: 'var(--md-radius-control, 8px)', background: 'transparent',
        color: inkP(0.6), cursor: 'pointer', font: 'inherit', fontSize: 16 }}>⋯</button>
    {open && <DbMenu title={label} onClose={() => setOpen(false)} anchor={btn}
      style={{ position: 'absolute', top: 34, insetInlineEnd: 0, zIndex: 40 }}>
      {rows.map((item) => <button key={item.label} type="button" className="md-db-menu-row"
        onClick={() => { setOpen(false); if (item.confirm) setAsking(item); else item.run(); }}>
        <span style={item.danger ? { color: light ? '#E11D48' : '#F43F5E' } : undefined}>{item.label}</span>
      </button>)}
    </DbMenu>}
    {/* The dialog belongs to the viewport, not to the `…` it was opened from,
        and it takes itself there now: it hosts on <body> at its registry plane
        (md-theme.jsx). This used to hand it a fixed full-screen box because it
        painted `inset:0` where it was mounted, which inside this relative span
        was a 30px scrim. Kept, that box would draw nothing and swallow every
        pointer on the screen. */}
    {asking && window.ConfirmDialog &&
      <window.ConfirmDialog light={light} accent={accent}
        title={asking.confirm.title} message={asking.confirm.message} confirmLabel={asking.label}
        onConfirm={() => { setAsking(null); asking.run(); }}
        onCancel={() => setAsking(null)} />}
  </span>;
}

/* Where one spine record stands (5.2), from the documents it actually lives
   in: its index row, and its page when somebody has written one.

   THE PAGE ONLY COUNTS ONCE IT EXISTS. mdProjectWriting hands back the key
   whether or not there is a document at it — `at` is how each of the three
   says "nothing is written there" — and an absent key reads 'local', which
   would mark every project nobody has written on "This device only". That is
   the same rule md-librarian.ts's writingOf applies for the same reason. */
function wsWhere(indexKey, writing) {
  if (!window.mdWhereRead) return null;
  const keys = [];
  if (indexKey) keys.push(indexKey);
  if (writing && writing.key && writing.at > 0) keys.push(writing.key);
  return keys.length ? window.mdWhereRead({ keys }) : null;
}

function RecordHead({ onBack, kind, tile, color, name, nameLabel, rename = null, actions = null,
  where = null, children }) {
  const light = isLightP();
  const Mark = window.MdWhereMark;
  return <div style={{ paddingBottom: 18, borderBottom: `1px solid ${inkP(light ? 0.07 : 0.06)}`,
    marginBottom: 22 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 0 12px' }}>
      <button onClick={onBack} aria-label="Back"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', width: 32, height: 32,
          marginInlineStart: -6, display: 'grid', placeItems: 'center' }}>
        {MDIP.back(inkP(0.7), 21)}
      </button>
      <span className="md-label" style={{ color: inkP(0.38), fontSize: 10.5, fontWeight: 400 }}>{kind}</span>
      {/* Beside what kind of record this is, because that is the line already
          saying what the thing IS rather than what it holds. No global chrome
          indicator anywhere — md-sync.js:1589 argued that a dot in the corner
          of the app speaks about "the account", which is not what anybody is
          looking at, and that position stands. */}
      {where && Mark && <Mark where={where} />}
      <span style={{ flex: 1 }} />
      {actions}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
      {tile && <Tile color={color} size={30} radius={10}>{tile}</Tile>}
      {rename
        ? <DbCommittedInput dir="auto" aria-label={nameLabel} value={name} commit={rename}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent',
              borderRadius: 'var(--md-radius-control, 8px)', padding: '2px 6px',
              marginInlineStart: -6, color: inkP(1), font: 'inherit', fontSize: 20,
              fontWeight: 630 }} />
        : <span dir="auto" style={{ color: inkP(1), fontSize: 20, fontWeight: 630, minWidth: 0,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '14px 26px',
      padding: '18px 0 0' }}>{children}</div>
  </div>;
}

/* ── a written record, at whatever altitude ─────────────────────────────────
   The same editor the day is written in. No placeholder and no greyed hints —
   an open record and a caret, which is md-compose.jsx's rule and the reason a
   domain with nothing in it reads as at rest rather than unfilled.

   `seed` is what a record with nothing in it OPENS WITH, and it is deliberately
   not the same thing as what it stores: it is put in the editor and left there
   unsaved until the first keystroke. That is what lets every project that
   already exists show the template the first time it is opened — with no
   migration, and with no mass write pushing headings to every device — while a
   project opened once and abandoned stays genuinely empty.

   A seeded record does NOT autofocus. The caret would land at position 0, above
   the first heading, inviting the writer to type in the one place the text does
   not want them. An empty one still does, because there a caret is the whole
   invitation. */
function WrittenPage({ read, readLinks, write, accent, ariaLabel, seed, sourceRef,
  wikiRecords = [], onWikiOpen = null, onWikiCreate = null, tagOptions = [], minHeight = 240 }) {
  const [text, setText] = useStP(() => read() || seed || '');
  const [links, setLinks] = useStP(() => readLinks ? readLinks() : []);
  const [fresh] = useStP(() => !read());
  const touched = useRefP(false);
  const change = (next) => {touched.current = true;setText(next);};
  const changeLinks = (next) => {touched.current = true;setLinks(next);};
  useEfP(() => {if (touched.current) write(text, links);}, [text, links]);
  /* The one place in this window that does not repaint from `tick`: the text is
     seeded into state once, so a paragraph taken back would stay on screen
     after storage no longer held it.

     ONLY for an undo or a redo. A push is this page's own typing, and re-seeding
     mid-burst would throw the caret to the end of the line on every keystroke —
     which is why the history says what KIND of change it just made.

     And in two steps rather than one, which is not ceremony. The listener runs
     DURING the key press, before React has re-rendered anything, so `seed` is
     still the one built from the old project — reading it there put a stale
     name back on a page whose rename had just been undone. Noting that a
     re-read is owed and doing it in an effect means it happens after the
     window has repainted, with the props this page actually has now. */
  const [restamp, setRestamp] = useStP(0);
  useEfP(() => {
    const hear = (event) => {
      const kind = event && event.detail && event.detail.kind;
      if (kind !== 'undo' && kind !== 'redo') return;
      setRestamp((n) => n + 1);
    };
    window.addEventListener('mdhistorychange', hear);
    return () => window.removeEventListener('mdhistorychange', hear);
  }, []);
  useEfP(() => {
    if (!restamp) return;
    /* `touched` is cleared with the same breath, so the re-read does not
       immediately write itself back out as an edit nobody made. And the
       fallback is the first render's: a page nobody has written on yet shows
       SEEDED headings that are not stored, so reading storage alone would
       blank it on any undo at all. */
    touched.current = false;
    setText(read() || seed || '');
    if (readLinks) setLinks(readLinks());
  }, [restamp]);
  /* The same embeds a record has. A link on a line of its own is a card
     wherever the writing is — this is one editor and one grammar, and a video
     that plays in a record but not in a project's page would be two. */
  const light = isLightP();
  return LiveMDP
    ? <LiveMDP value={text} onChange={change} accent={accent} autoFocus={fresh && !seed}
        wikiLinks={links}
        wikiRecords={(wikiRecords || []).filter((row) => window.mdWikiTargetKey(row.ref) !== window.mdWikiTargetKey(sourceRef))}
        onWikiLinksChange={changeLinks} onWikiOpen={onWikiOpen} onWikiCreate={onWikiCreate}
        tagOptions={tagOptions}
        onCreateDatabase={() => {
          const db = window.mdDatabaseCreate && window.mdDatabaseCreate('Untitled database');
          return db && window.mdDatabaseLine ? window.mdDatabaseLine(db.id) : '';
        }}
        fontSize={15.5} minHeight={minHeight} ariaLabel={ariaLabel}
        blockOf={(line) => (window.mdDatabaseBlockOf && window.mdDatabaseBlockOf(line))
          || (window.mdEmbedBlockOf && window.mdEmbedBlockOf(line))}
        blockFormats={window.mdEmbedFormatsOf} formatBlock={window.mdEmbedFormat}
        renderBlock={({ key, line, width, height, active, edit }) => {
          const raw = text.split('\n')[line];
          const databaseId = window.mdDatabaseIdFromLine ? window.mdDatabaseIdFromLine(raw) : '';
          if (databaseId) return <window.DatabaseBlock key={key} databaseId={databaseId}
            width={width} height={height} accent={accent} onRemove={() => edit('')}/>;
          const e = window.mdEmbedLine ? window.mdEmbedLine(raw) : null;
          if (!e) return null;
          return (
            <window.EmbedCard key={key} embed={e} raw={raw} light={light}
              accent={accent} active={active} width={width} height={height}
              onEdit={edit} />);
        }} />
    : null;
}

/* ── one domain ──────────────────────────────────────────────────────────── */
function DomainView({ id, ctx, accent, onBack, onOpenProject, onOpenPursuit, onNewPursuit, onNewProject, wikiRecords = [],
  wikiBacklinksFor = null, onWikiOpen = null, onWikiCreate = null, tagOptions = [], onAgain = null }) {
  const light = isLightP();
  const domain = window.mdDomainById(id);
  /* This view had nothing writable on it until the label became a document
     field, which is why it never asked for a refresh before. */
  const again = () => { if (onAgain) onAgain(); };
  if (!domain) return null;
  const col = domColorP(id, light);
  const group = byDomainP({ open: true }).find((gr) => gr.domain.id === id);
  const pursuits = pursuitIndexP ? pursuitIndexP({ open: true, domainId: id }) : [];
  const projects = group ? group.projects : [];
  const loose = projects.filter((project) => !project.pursuitId);

  return (
    <>
      {/* A domain has no parent, no dates and no status — it is one of ten
          fixed parts of life, named in code. What it does have is a count of
          what is going on inside it, which is the only honest property here.

          RENAMEABLE, BUT NOT REMOVABLE. Its label is a document field now, so
          ManDana Admin can change what it is called. There is deliberately no
          delete: every project on the account points at a domain by id, and
          removing one orphans work in a way removing a project never does.
          Renaming is safe precisely because the id is what everything holds. */}
      <RecordHead onBack={onBack} kind="Domain · مجال" color={col} tile={domIconP(id, col, 16)}
        name={domain.label} nameLabel="Domain name"
        /* No index document: the ten are fixed in code, so the only thing a
           domain can have travelled in is its own page. */
        where={wsWhere('', window.mdDomainWriting && window.mdDomainWriting(id))}
        rename={(next) => {
          const done = wsCommit(window.mdDomainTryRename(id, next)); again(); return { ok: done };
        }}>
        <WsProp name="pursuits" label="Pursuits">
          <span style={{ color: inkP(0.84), fontSize: 13 }}>{pursuits.length || MD_NONE}</span>
        </WsProp>
        <WsProp name="projects" label="Projects">
          <span style={{ color: inkP(0.84), fontSize: 13 }}>{projects.length || MD_NONE}</span>
        </WsProp>
      </RecordHead>
      <WrittenPage accent={accent}
        read={() => domTextReadP(id)} readLinks={() => window.mdDomainLinksRead ? window.mdDomainLinksRead(id) : []}
        write={(t, links) => domTextWriteP(id, t, links)} sourceRef={{ type: 'domain', id }}
        wikiRecords={wikiRecords} onWikiOpen={onWikiOpen} onWikiCreate={onWikiCreate}
        tagOptions={tagOptions}
        ariaLabel={`${domain.label} — who you want to be here`} />

      {(pursuits.length > 0 || loose.length > 0) &&
        <div style={{ marginTop: 26 }}>
          <div className="md-label" style={{ color: inkP(0.5), fontSize: 11, fontWeight: 400,
            padding: '0 4px 10px' }}>Pursuits</div>
          {pursuits.map((pursuit) => (
            <PursuitCard key={pursuit.id} pursuit={pursuit} color={col} ctx={ctx}
              projects={projects.filter((project) => project.pursuitId === pursuit.id)}
              onOpen={() => onOpenPursuit(pursuit.id)} />
          ))}
          {loose.length > 0 && <div className="md-label" style={{ color: inkP(0.38), fontSize: 10.5,
            fontWeight: 400, padding: '12px 4px 9px' }}>Independent projects</div>}
          {loose.map((p) => (
            <ProjectCard key={p.id} project={p} color={col}
              stats={statsP(p.id, ctx)} onOpen={() => onOpenProject(p.id)} />
          ))}
        </div>}
      <div style={{ display: 'flex', gap: 16, padding: '20px 4px 6px' }}>
        <button onClick={onNewPursuit} style={{ border: 'none', background: 'transparent', color: rgbaP(col, 0.95),
          cursor: 'pointer', padding: 0, fontSize: 13.5, fontWeight: 600 }}>New pursuit</button>
        <button onClick={onNewProject} style={{ border: 'none', background: 'transparent', color: inkP(0.52),
          cursor: 'pointer', padding: 0, fontSize: 13.5 }}>Independent project</button>
      </div>
    </>);
}

function PursuitView({ id, ctx, accent, boards, onBack, onOpenProject, onNewProject, onOpenBoard, onNewBoard,
  onOpenDomain = null, onAgain = null, wikiRecords = [],
  onWikiOpen = null, onWikiCreate = null, tagOptions = [] }) {
  const pursuit = pursuitByIdP && pursuitByIdP(id);
  if (!pursuit) return null;
  const light = isLightP();
  const color = domColorP(pursuit.domainId, light);
  const projects = (projectIndexP ? projectIndexP({ open: true }) : []).filter((project) => project.pursuitId === id);
  const again = () => { if (onAgain) onAgain(); };
  return <div style={{ animation: 'mdWhenIn .28s cubic-bezier(.22,1,.36,1)' }}>
    {/* No status and no dates: a pursuit has neither, by law (md-pursuit.ts).
        IQAMA is renameable now, like every other built-in — the door is real
        and it refuses in words unless ManDana Admin is asking. */}
    <RecordHead onBack={onBack} kind="Pursuit · مسعى" color={color}
      tile={MDIP.orbit ? MDIP.orbit(color, 17) : domIconP(pursuit.domainId, color, 16)}
      name={pursuit.name} nameLabel="Pursuit name"
      where={wsWhere('md:pursuits', window.mdPursuitWriting && window.mdPursuitWriting(id))}
      rename={(next) => {
        const done = wsCommit(window.mdPursuitTryRename(id, next)); again(); return { ok: done };
      }}
      /* "Set this down" rather than "Close": a pursuit is not finished, it is
         rested. Deleting it is a separate act with its own confirmation. */
      actions={<WsActions label="Pursuit actions" accent={color} items={[
        { label: pursuit.closed ? 'Take it up again' : 'Set this down',
          run: () => { wsCommit(window.mdPursuitTrySetClosed(id, !pursuit.closed)); again(); } },
        { label: 'Delete pursuit', danger: true,
          /* A PURSUIT TAKES ITS TREE. Containment has one parent, so deleting
             its root takes the projects and records below it instead of
             leaving a broken tree. The thirty-day Trash buffer is what makes
             that safe rather than severe: the pursuit and its whole subtree
             can come back together. */
          confirm: { title: 'Delete this pursuit?',
            message: window.mdDeleteSay({
              opening: `“${pursuit.name}” will be removed.`,
              counts: window.mdDeleteReach(`pursuit:${id}`),
              cascade: true,
              buffer: true,
            }).message },
          run: async () => {
            if (await wsTrashed(window.mdTrashDelete(`pursuit:${id}`))) { again(); onBack(); }
          } },
      ]} />}>
      <WsProp name="domain" label="Part of life">
        <WsPick label="Part of life" accent={color} value={pursuit.domainId}
          options={(window.MD_DOMAINS || []).map((domain) => ({ id: domain.id, name: domain.label }))}
          onPick={(next) => { if (window.mdPursuitSetDomain(id, next)) again(); }}
          onOpen={onOpenDomain} />
      </WsProp>
      <WsProp name="projects" label="Projects">
        <span style={{ color: inkP(0.84), fontSize: 13 }}>{projects.length || MD_NONE}</span>
      </WsProp>
      <WsProp name="workflow" label="Workflow">
        <WsWorkflow kind="pursuit" id={id} accent={color} onChange={again} />
      </WsProp>
    </RecordHead>
    <WrittenPage accent={accent}
      read={() => pursuitTextReadP(id)} readLinks={() => pursuitLinksReadP ? pursuitLinksReadP(id) : []}
      write={(text, links) => pursuitTextWriteP(id, text, links)}
      seed={window.mdPursuitSeedText ? window.mdPursuitSeedText(pursuit.name) : pursuit.name}
      sourceRef={{ type: 'pursuit', id }} wikiRecords={wikiRecords}
      onWikiOpen={onWikiOpen} onWikiCreate={onWikiCreate} tagOptions={tagOptions}
      ariaLabel={`${pursuit.name} — enduring striving`} />
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px 10px' }}>
        <span className="md-label" style={{ color: inkP(0.5), fontSize: 11, fontWeight: 400 }}>Projects</span>
        <span style={{ flex: 1 }} />
        <button onClick={onNewProject} aria-label="New project" style={{ border: 'none', background: 'transparent',
          color: rgbaP(color, 0.95), cursor: 'pointer', padding: 4, display: 'flex' }}>{MDIP.plus(color, 17)}</button>
      </div>
      {projects.map((project) => <ProjectCard key={project.id} project={project} color={color}
        stats={statsP(project.id, ctx)} onOpen={() => onOpenProject(project.id)} />)}
      {!projects.length && <button onClick={onNewProject} style={{ border: 'none', background: 'transparent',
        color: inkP(0.48), cursor: 'pointer', padding: '12px 4px', fontSize: 13.5 }}>Begin a finite project</button>}
    </div>
    <ScopedBoards boards={boards} scopeRef={`pursuit:${id}`} color={color}
      onOpen={onOpenBoard} onNew={onNewBoard} />
  </div>;
}

function ProjectView({ id, ctx, accent, events, records, projectRecords, boards, onBack, onOpenRecord, onNewRecord,
  onOpenBacklog, onNewBacklog, onOpenBoard, onNewBoard, onOpenPursuit = null, onOpenDomain = null,
  onAgain = null, wikiRecords = [], onWikiOpen = null, onWikiCreate = null, tagOptions = [] }) {
  const project = projByIdP(id);
  if (!project) return null;
  const pursuit = project.pursuitId && pursuitByIdP ? pursuitByIdP(project.pursuitId) : null;
  const light = isLightP();
  const color = domColorP(project.domainId, light);
  const members = [];
  for (const map of [events || {}, records || {}]) for (const day of Object.keys(map)) {
    for (const record of (map[day] || [])) if (record && record.pid === id) members.push({ record, day });
  }
  members.sort((a, b) => b.day.localeCompare(a.day) || ((a.record.min ?? -1) - (b.record.min ?? -1)));
  const stats = statsP(id, ctx);
  const backlog = (projectRecords || []).filter((record) => record && record.pid === id);
  const again = () => { if (onAgain) onAgain(); };
  const start = window.mdProjectStart ? window.mdProjectStart(project) : null;
  const target = window.mdProjectTarget ? window.mdProjectTarget(project) : null;
  /* Both ends in one call, so a span is one write and can never land inverted
     (md-project.ts:618) — the reason `mdProjectSetSpan` exists at all. */
  const span = (nextStart, nextTarget) => {
    const done = wsCommit(window.mdProjectTrySetSpan(id, nextStart || null, nextTarget || null));
    again();
    return { ok: done };
  };
  return <div style={{ animation: 'mdWhenIn .28s cubic-bezier(.22,1,.36,1)' }}>
    <RecordHead onBack={onBack} kind="Project" color={color} tile={domIconP(project.domainId, color, 16)}
      name={project.name} nameLabel="Project name"
      where={wsWhere('md:projects', window.mdProjectWriting && window.mdProjectWriting(id))}
      /* A BUILT-IN NO LONGER HIDES ITS DOORS. It used to: there was nothing
         behind them, because a built-in was not a document and could not be
         renamed or removed by anybody. It is a document now, so the doors are
         real and the refusal is a sentence rather than an absence — which is
         the difference between "you may not" and "there is nothing here", and
         only one of those is true. See md-admin-door.ts. */
      rename={(next) => {
        const done = wsCommit(window.mdProjectTryRename(id, next)); again(); return { ok: done };
      }}
      actions={<WsActions label="Project actions" accent={color} items={[
        { label: project.closed ? 'Reopen' : 'Mark closed',
          run: () => { wsCommit(window.mdProjectTrySetClosed(id, !project.closed)); again(); } },
        { label: 'Delete project', danger: true,
          confirm: { title: project.builtin ? 'Remove this from ManDana?' : 'Delete this project?',
            message: project.builtin
              /* Said differently on purpose. One of these is reversible and the
                 other is not, and a confirmation that blurs that is worse than
                 no confirmation at all. A builtin is not counted either: it is
                 not being deleted, so there is no blast radius to state. */
              ? `“${project.name}” ships with ManDana. It will stop appearing anywhere, and ManDana Admin can put it back. Nothing you have written is removed.`
              : window.mdDeleteSay({
                opening: `“${project.name}” and the writing on it will be removed.`,
                counts: window.mdDeleteReach(`project:${id}`),
                buffer: true,
              }).message },
          run: async () => {
            if (project.builtin) {
              if (wsCommit(window.mdProjectTryDelete(id))) { again(); onBack(); }
              return;
            }
            if (await wsTrashed(window.mdTrashDelete(`project:${id}`))) { again(); onBack(); }
          } },
      ]} />}>
      <WsProp name="status" label="Status">
        <ProjectStatus project={project} color={color} onChange={again} />
      </WsProp>
      <WsProp name="priority" label="Priority">
        <ProjectPriority project={project} color={color} onChange={again} />
      </WsProp>
      <WsProp name="pursuit" label="Pursuit">
        <WsPick label="Pursuit" accent={color} value={project.pursuitId || null} none="No pursuit"
          options={[{ id: null, name: 'No pursuit' },
            ...(pursuitIndexP ? pursuitIndexP({ open: true }) : []).map((row) => ({ id: row.id, name: row.name }))]}
          onPick={(next) => { wsCommit(window.mdProjectTrySetPursuit(id, next)); again(); }}
          onOpen={onOpenPursuit} />
      </WsProp>
      <WsProp name="domain" label="Part of life">
        <WsPick label="Part of life" accent={color} value={project.domainId}
          options={(window.MD_DOMAINS || []).map((domain) => ({ id: domain.id, name: domain.label }))}
          onPick={(next) => { wsCommit(window.mdProjectTrySetDomain(id, next)); again(); }}
          onOpen={onOpenDomain} />
      </WsProp>
      <WsProp name="start" label="Start">
        <WsDay label="Start" value={start} commit={(next) => span(next, target)} />
      </WsProp>
      <WsProp name="target" label="Target">
        <WsDay label="Target" value={target} commit={(next) => span(start, next)} />
      </WsProp>
      {/* A measurement, not a field. The one number in this app that cannot
          fall is also the one nobody gets to edit. */}
      <WsProp name="given" label="Time given">
        <span style={{ color: inkP(0.84), fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
          {spentLabel(stats.minutes)}</span>
      </WsProp>
      {/* Last, and next to nothing else it changes: the status one end of this
          row away is the field this chip decides the words for. */}
      <WsProp name="workflow" label="Workflow">
        <WsWorkflow kind="project" id={id} accent={color} onChange={again} />
      </WsProp>
    </RecordHead>
    <WrittenPage accent={accent}
      read={() => projTextReadP(id)} readLinks={() => window.mdProjectLinksRead ? window.mdProjectLinksRead(id) : []}
      write={(text, links) => projTextWriteP(id, text, links)}
      seed={window.mdProjectSeedText(project.name)} sourceRef={{ type: 'project', id }}
      wikiRecords={wikiRecords} onWikiOpen={onWikiOpen} onWikiCreate={onWikiCreate}
      tagOptions={tagOptions} ariaLabel={`${project.name} project`} />
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px 10px' }}>
        <span className="md-label" style={{ color: inkP(.5), fontSize: 11, fontWeight: 400 }}>Backlog</span>
        <span style={{ color: inkP(.3), fontSize: 11.5, marginInlineStart: 7 }}>{backlog.length}</span>
        <span style={{ flex: 1 }} />
        <button onClick={() => onNewBacklog(id)} aria-label="New backlog record"
          style={{ border: 'none', background: 'transparent', color, cursor: 'pointer', padding: 4,
            display: 'flex', borderRadius: 10 }}>{MDIP.plus(color, 17)}</button>
      </div>
      {backlog.map((record) => <button key={record.uid} onClick={() => onOpenBacklog(record.uid)} {...pressP}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 4px',
          border: 'none', borderBottom: `1px solid ${inkP(.06)}`, background: 'transparent', color: inkP(1),
          cursor: 'pointer', textAlign: 'start', transition: 'transform .14s ease' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', border: `1.2px solid ${rgbaP(color, .78)}`,
          flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 550, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.name || 'New Record'}</span>
        <span style={{ display: 'flex' }}>{CHEVRON_P(inkP(.24))}</span>
      </button>)}
      {!backlog.length && <button onClick={() => onNewBacklog(id)} style={{ border: 'none', background: 'transparent',
        color: inkP(.48), cursor: 'pointer', padding: '10px 4px 18px', fontSize: 13.5 }}>Capture before choosing a day</button>}
    </div>
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px 10px' }}>
        <span className="md-label" style={{ color: inkP(0.5), fontSize: 11, fontWeight: 400 }}>Timeline</span>
        <span style={{ color: inkP(0.3), fontSize: 11.5, marginInlineStart: 7 }}>{members.length}</span>
        <span style={{ flex: 1 }} />
        <button onClick={() => onNewRecord(id)} aria-label="New project record"
          style={{ border: 'none', background: 'transparent', color: color, cursor: 'pointer', padding: 4,
            display: 'flex' }}>{MDIP.plus(color, 17)}</button>
      </div>
      {members.map(({ record, day }) => <button key={`${day}:${record.uid}`}
        onClick={() => onOpenRecord(record)} {...pressP}
        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', border: 'none',
          borderBottom: `1px solid ${inkP(light ? .07 : .06)}`, background: 'transparent', color: inkP(1),
          textAlign: 'start', padding: '12px 4px', cursor: 'pointer', transition: 'transform .14s ease' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: rgbaP(color, .82), flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14.5, fontWeight: 550, whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.name || 'New Record'}</span>
          <span style={{ display: 'block', color: inkP(.42), fontSize: 12, marginTop: 3 }}>{day}
            {Number.isFinite(record.min) ? ` · ${window.minToHHMM ? window.minToHHMM(record.min) : ''}` : ' · All day'}</span>
        </span>
        <span style={{ display: 'flex' }}>{CHEVRON_P(inkP(.24))}</span>
      </button>)}
      {!members.length && <button onClick={() => onNewRecord(id)} style={{ border: 'none', background: 'transparent',
        color: inkP(.48), cursor: 'pointer', padding: '12px 4px', fontSize: 13.5 }}>Schedule work on the timeline</button>}
    </div>
    <ScopedBoards boards={boards} scopeRef={`project:${id}`} color={color}
      onOpen={onOpenBoard} onNew={onNewBoard} />
  </div>;
}

/* A backlog record used to have a screen of its own here — an inline page with
   a re-parenting door and a "Choose its time" button. OD-3 retired it: records
   open in the compose sheet wherever they are opened from, the project chip is
   the re-parenting door, and the sheet's When control is the schedule door. */

/* ── the beginning ───────────────────────────────────────────────────────────
   B-5. What `+` used to do was push a FRAME: the Projects window was replaced
   by a name field and ten tiles, and the board you were standing in — the
   question you had asked of it, the column whose `+` you had just pressed —
   was gone until you were finished. It asked two questions and then dropped
   you into a project holding a name, a part of life, and a target forty days
   out that nobody had chosen. Everything else a project can be had to be set
   afterwards, from somewhere else, one control at a time.

   This is a SHEET OVER THE WINDOW, and the difference is the whole story.

   ── IT ASKS EVERYTHING A PROJECT IS, AND NOTHING MORE ─────────────────────
   Status · Priority · Start · Target · Tags, and the writing. Every one of
   them has somewhere to be read again — which is why B-0 to B-3 came first
   and why priority is a stored field rather than a word this sheet collects
   and loses. The reference's "short summary" line is not here: a project has
   ONE writing document and line one of it is the title, so a second
   name-shaped field would have had nowhere to live. Milestones are not here
   either: `mdProjectTemplate` writes the heading and there are no rows behind
   it, and a `+` that opens nothing is the button that lies.

   ── NOTHING IS WRITTEN UNTIL CREATE ───────────────────────────────────────
   A deliberate departure from the compose sheet, whose record exists from the
   first keystroke. A record is one document; a project is an index row, a
   document, a place other records point at and a thing that syncs. One begun
   and abandoned is not a card that quietly disappears. So Cancel writes
   nothing at all, and Create is ONE synchronous handler — one row through
   `mdProjectCreate`, one optional document — which is what makes the whole
   beginning a single ⌘Z (md-history.ts: a step is a gesture, not a write).

   ── THE SCRIM DOES NOT DISMISS ────────────────────────────────────────────
   Escape, Cancel and × close it; a click on the glass does not. The sheet can
   be holding a paragraph nobody has saved, and a surface that throws that away
   on a stray click is the same defect as an unconfirmed delete.

   ── WHAT IT INHERITS RATHER THAN INVENTS ──────────────────────────────────
   One escape layer on the shared LIFO stack, so a menu inside it closes before
   the sheet does and the sheet before the window. `useDbInertBackground`, this
   time genuinely — unlike the window at board size, this one covers the app
   and says so with `aria-modal`. The domain tile is `AiDot`'s grammar with the
   projects taken out: a hollow dot until a part of life is known, the domain's
   own colour and icon once it is, and the guess that arrives from the name
   while nobody has picked one by hand. And tags go where tags live — into the
   writing, as `#name` — because md-tag.ts derives the index from the prose and
   a tag stored beside it would be a second truth. */

/* Where a menu opened from inside the sheet hangs. From the VIEWPORT, not from
   the pill that opened it: the card is a few hundred pixels tall and its body
   scrolls, so a menu positioned inside it is clipped by the card's own rounded
   edge — a status picker with two of its four words cut off. Measured from the
   button's real box, flipped above it when there is no room below, and capped
   so a long list scrolls inside itself rather than off the screen. */
function npPlace(el, want = 300, width = 250) {
  const box = el.getBoundingClientRect();
  const gap = 4, edge = 8;
  const below = window.innerHeight - box.bottom - gap - edge;
  const above = box.top - gap - edge;
  const up = below < Math.min(want, above);
  return { position: 'fixed', right: 'auto', insetInlineEnd: 'auto', zIndex: 210,
    left: Math.max(edge, Math.min(box.left, window.innerWidth - width - edge)),
    top: up ? Math.max(edge, box.top - gap - Math.min(want, above)) : box.bottom + gap,
    maxHeight: Math.max(140, Math.min(want, up ? above : below)), overflowY: 'auto' };
}

/* And out to the BODY, because a menu drawn inside this card is cut off by the
   card's own rounded edge — which is exactly how the status picker lost two of
   its four words. (It began as a containing-block problem, when the card was
   frosted glass; the card is flat now and its `overflow: hidden` clips just the
   same.) The light scope and the accent travel with it, since it is leaving the
   surface that set them. */
function NpMenu({ title, at, accent, onClose, anchor = null, children }) {
  const light = isLightP();
  return ReactDOM.createPortal(
    <div className={light ? 'md-db-light' : ''} style={{ '--db-accent': accent }}>
      {/* `anchor` travels the whole way down: the pill is on the sheet and the
          menu is on the body, so nothing about the DOM says they belong to each
          other unless somebody says it. See DbMenu — this is what makes a
          second press close a picker instead of reopening it. */}
      <DbMenu title={title} onClose={onClose} style={at} anchor={anchor}>{children}</DbMenu>
    </div>, document.body);
}

function npBox(light, filled) {
  return { display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 28, flex: 'none',
    padding: '4px 11px 4px 10px', border: `1px solid ${inkP(light ? 0.1 : 0.12)}`,
    /* Fully round: they are answers you carry, not fields you fill in, and the
       shape says which of those it is before a word is read. */
    borderRadius: 999, background: 'transparent',
    color: inkP(filled ? 0.84 : 0.5), font: 'inherit', fontSize: 12.5, cursor: 'pointer',
    transition: 'background .14s, border-color .14s' };
}

/* One pill, twice over: status and priority differ in their words and their
   glyph and in nothing else, and the draft has no project to write to yet. */
function NpChoice({ field, label, value, labelOf, options, mark, accent, onPick,
  clearable = true }) {
  const light = isLightP();
  const [open, setOpen] = useStP(null);
  const btn = useRefP(null);
  return <span data-md-np-pill={field} className={light ? 'md-db-light' : ''}
    style={{ display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" ref={btn} aria-label={`${label} — ${labelOf(value)}`} aria-haspopup="listbox"
      aria-expanded={open ? 'true' : 'false'}
      onClick={(event) => setOpen(open ? null : npPlace(event.currentTarget))}
      style={npBox(light, !!value)}>
      {mark ? mark(value) : null}
      <span style={{ whiteSpace: 'nowrap' }}>{labelOf(value)}</span>
    </button>
    {open && <NpMenu title={label} at={open} accent={accent} anchor={btn}
      onClose={() => setOpen(null)}>
      <div role="listbox" aria-label={label}>
        {[...options, ...(clearable ? [null] : [])].map((option) => {
          const on = (value || null) === option;
          return <button key={option || 'none'} type="button" role="option"
            aria-selected={on ? 'true' : 'false'} className="md-db-menu-row"
            onClick={() => { onPick(option); setOpen(null); }}>
            {mark ? mark(option) : null}
            <span>{labelOf(option)}</span>
            {on ? MDIP.check(inkP(0.55), 13) : null}
          </button>;
        })}
      </div>
    </NpMenu>}
  </span>;
}

/* ── a day, in the app's own hand ────────────────────────────────────────────
   This began as a native `<input type="date">` — the one control on the sheet
   drawn by somebody else, shouting `dd/mm/yyyy` in the browser's voice on a
   surface whose word for a missing value is an em-dash. What opens now is the
   month this app already draws twice — the day view's dropdown and the
   scheduler's takeover: mdDayCircle rings the chosen day, the Hijri date
   whispers under each numeral, ‹ › walk the months. The pill carries the
   calendar glyph and its word while empty, and the day as `15 Jan 2026` once
   one is chosen; `Clear` under the grid — shown only once there is a day to
   clear — takes it back out, because a project with no finish date is a
   normal thing for one to be. */
const NP_MON_S = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const NP_MON_L = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];
function npFmtDay(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || '');
  return m ? `${+m[3]} ${NP_MON_S[+m[2] - 1]} ${+m[1]}` : '';
}
const npKey = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

function NpCal({ value, accent, onPick }) {
  const light = isLightP();
  const now = new Date();
  const seed = /^(\d{4})-(\d{2})-\d{2}$/.exec(value || '');
  const [cursor, setCursor] = useStP({ y: seed ? +seed[1] : now.getFullYear(),
    m: seed ? +seed[2] - 1 : now.getMonth() });
  const todayKey = npKey(now.getFullYear(), now.getMonth(), now.getDate());
  /* The same ring every calendar in the app draws; the fallback only exists for
     surfaces loaded without the theme. */
  const circle = window.mdDayCircle || ((sel) => ({
    bg: sel ? rgbaP(accent, 0.9) : 'transparent', numCol: sel ? '#fff' : inkP(0.82),
    fw: sel ? 700 : 550, shadow: 'none', bd: 'none' }));
  /* Saturday-first, the week every schedule surface starts on (md-app.jsx). */
  const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const wsIdx = 6;
  const lead = (new Date(cursor.y, cursor.m, 1).getDay() - wsIdx + 7) % 7;
  const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = Array(lead).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);
  const rows = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  const hijriOf = (d) => (window.mdHijriParts && window.mdOffsetForDate
    ? window.mdHijriParts(window.mdOffsetForDate(cursor.y, cursor.m, d)) : null);
  const nav = { width: 30, height: 30, border: 'none', background: 'none', cursor: 'pointer',
    display: 'grid', placeItems: 'center', padding: 0, borderRadius: 'var(--md-radius-control, 8px)' };
  return <div data-md-np-cal="" style={{ padding: '2px 4px 4px', width: 238 }}>
    <div data-md-np-cal-head="" style={{ display: 'flex', alignItems: 'center', padding: '2px 2px 8px' }}>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, flex: 1, minWidth: 0 }}>
        <span style={{ color: inkP(0.94), fontSize: 13.5, fontWeight: 680 }}>{NP_MON_L[cursor.m]}</span>
        <span style={{ color: inkP(0.48), fontSize: 12.5, fontWeight: 620 }}>{cursor.y}</span>
      </span>
      <button type="button" aria-label="Previous month" style={nav}
        onClick={() => setCursor((c) => { const d = new Date(c.y, c.m - 1, 1);
          return { y: d.getFullYear(), m: d.getMonth() }; })}>
        <span style={{ display: 'grid', transform: 'rotate(180deg)' }}>{MDIP.chevron(inkP(0.55), 11)}</span>
      </button>
      <button type="button" aria-label="Next month" style={nav}
        onClick={() => setCursor((c) => { const d = new Date(c.y, c.m + 1, 1);
          return { y: d.getFullYear(), m: d.getMonth() }; })}>
        {MDIP.chevron(inkP(0.55), 11)}
      </button>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
      {Array.from({ length: 7 }, (_, i) => WD[(wsIdx + i) % 7]).map((label, i) =>
        <span key={i} style={{ textAlign: 'center', color: inkP(0.44), fontSize: 10,
          fontWeight: 600 }}>{label}</span>)}
    </div>
    {rows.map((row, ri) =>
    <div key={ri} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 1 }}>
      {row.map((d, ci) => {
        if (!d) return <span key={ci} />;
        const key = npKey(cursor.y, cursor.m, d);
        const c = circle(key === value, key === todayKey, light, accent);
        const h = hijriOf(d);
        return <button key={ci} type="button" aria-label={npFmtDay(key)}
          onClick={() => onPick(key)}
          style={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 1, background: 'none', border: 'none', padding: '2px 0 3px', cursor: 'pointer' }}>
          <span style={{ width: 26, height: 26, borderRadius: '50%', display: 'grid',
            placeItems: 'center', backgroundColor: c.bg, color: c.numCol, border: c.bd,
            fontSize: 12.5, fontWeight: c.fw, fontVariantNumeric: 'tabular-nums',
            boxShadow: c.shadow,
            transition: 'background-color .18s ease, box-shadow .18s ease, color .18s ease' }}>{d}</span>
          {h && <span aria-hidden="true" style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            whiteSpace: 'nowrap', fontSize: h.isMonthStart ? 8 : 8.5,
            fontWeight: h.isMonthStart ? 700 : 500,
            color: h.isMonthStart ? rgbaP(accent, 0.95) : inkP(0.4) }}>{h.isMonthStart ? h.short : h.day}</span>}
        </button>;
      })}
    </div>)}
  </div>;
}

function NpDay({ field, label, value, accent, onChange }) {
  const light = isLightP();
  const [open, setOpen] = useStP(null);
  const btn = useRefP(null);
  return <span data-md-np-pill={field} className={light ? 'md-db-light' : ''}
    style={{ display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" ref={btn} aria-label={`${label} — ${value ? npFmtDay(value) : 'none'}`}
      aria-haspopup="dialog" aria-expanded={open ? 'true' : 'false'}
      onClick={(event) => setOpen(open ? null : npPlace(event.currentTarget, 384, 262))}
      style={npBox(light, !!value)}>
      {MDIP.calendar(inkP(value ? 0.7 : 0.4), 13)}
      <span style={{ whiteSpace: 'nowrap' }}>{value ? npFmtDay(value) : label}</span>
    </button>
    {open && <NpMenu title={label} at={{ ...open, width: 262 }} accent={accent} anchor={btn}
      onClose={() => setOpen(null)}>
      <NpCal value={value} accent={accent} onPick={(key) => { onChange(key); setOpen(null); }} />
      {/* One word, and only while there is something to take out: an empty
          picker offering to empty itself is a control with nothing behind it. */}
      {value && <button type="button" className="md-db-menu-row"
        onClick={() => { onChange(''); setOpen(null); }}>
        <span>Clear</span>
      </button>}
    </NpMenu>}
  </span>;
}

/* Tags are not a property of the project — they are words in its writing, and
   md-tag.ts derives the index from the prose for exactly that reason. So this
   pill edits the DESCRIPTION: choosing `#ramadan` puts `#ramadan` in the text,
   where the editor's own `#` completion would have put it, and taking it off
   takes it back out. Nothing new is stored, and there is one place a tag can be.

   ── IT LISTED TAGS AND COULD NOT WRITE ONE (Marwan, 2026-08-21) ────────────
   This opened a list of the tags that already exist, and on a first project on
   a fresh device that list is a sentence explaining that the list is empty.
   Every other find-or-create surface in this app — a database's option cell, a
   record's own tag inspector — lets you type the thing you meant and make it,
   and the tag inspector is the one written for exactly these words.

   So it is BORROWED, not redrawn: `MdTagInspector` unframed, the same component
   the record's Details panel stands in its Tags section, with its search line,
   its `Create #name` row, its chips and its counts. md-compose.jsx loads first
   (index.html), so it arrives through `window` like everything else this file
   takes from there. A second tag surface, drawn here, would have been the third
   place in one app to answer the same question. */
function NpTags({ accent, options, text, onText }) {
  const light = isLightP();
  const [open, setOpen] = useStP(null);
  const btn = useRefP(null);
  const held = window.mdTags ? window.mdTags(text) : [];
  const label = held.length === 0 ? 'Tags'
    : held.length === 1 ? `#${held[0].name}` : `${held.length} tags`;
  const rows = (options || []).filter((row) => row && row.name);
  const write = (fn, name) => {
    const next = fn ? fn(text, name) : null;
    onText(next && typeof next.text === 'string' ? next.text : text);
  };
  return <span data-md-np-pill="tags" className={light ? 'md-db-light' : ''}
    style={{ display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" ref={btn} aria-label={`Tags — ${held.length ? label : 'none'}`}
      aria-haspopup="listbox" aria-expanded={open ? 'true' : 'false'}
      onClick={(event) => setOpen(open ? null : npPlace(event.currentTarget, 460, 300))}
      style={npBox(light, !!held.length)}>
      <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
    </button>
    {open && <NpMenu title="Tags" at={{ ...open, width: 300 }} accent={accent} anchor={btn}
      onClose={() => setOpen(null)}>
      {window.MdTagInspector
        ? <window.MdTagInspector framed={false} light={light} accent={accent}
          active={held} options={rows} sourceRef={null}
          onAdd={(name) => write(window.mdTagAdd, name)}
          onRemove={(name) => write(window.mdTagRemove, name)}
          /* NOWHERE TO GO FROM A DRAFT. The inspector's "Records" list opens
             other records; this beginning is written nowhere, so following one
             would throw it away. With no `onOpen` the list is not drawn at all,
             which is the honest shape — not a row that quietly does nothing. */
          onOpen={null} />
        /* Not an empty box and not a scolding: the sentence that says where
           tags come from is the same answer as a list of them. */
        : <div style={{ padding: '4px 10px 8px', color: inkP(0.45), fontSize: 11.5,
          maxWidth: 200, lineHeight: 1.45 }}>Tags are written. Type <span>#</span> anywhere
          in the description and this list fills itself.</div>}
    </NpMenu>}
  </span>;
}

/* The breadcrumb, and it is a live control rather than a label. The reference
   reads `MANDANA › New project`; the workspace is not a fact in this app — a
   project is born into a PURSUIT or into nothing — so the crumb says which,
   and can be answered on the spot. `New pursuit…` opens inline for the same
   reason compose creates a project from inside its own picker: leaving a sheet
   to make the thing the sheet asked you for loses everything typed so far. */
function NpPursuit({ accent, pursuitId, domainId, onPick }) {
  const light = isLightP();
  const [open, setOpen] = useStP(null);
  const [naming, setNaming] = useStP(false);
  const [draft, setDraft] = useStP('');
  const btn = useRefP(null);
  const pursuit = pursuitId && pursuitByIdP ? pursuitByIdP(pursuitId) : null;
  const rows = pursuitIndexP ? pursuitIndexP({ open: true }) : [];
  const close = () => { setOpen(null); setNaming(false); setDraft(''); };
  const begin = () => {
    const clean = draft.trim();
    if (!clean || !domainId) return;
    const made = pursuitCreateP(clean, domainId);
    if (made) { onPick(made.id, made.domainId); close(); }
  };
  return <span className={light ? 'md-db-light' : ''}
    style={{ display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" ref={btn} data-md-np-pursuit aria-haspopup="menu"
      aria-expanded={open ? 'true' : 'false'}
      aria-label={pursuit ? `Pursuit — ${pursuit.name}` : 'Pursuit — none'}
      onClick={(event) => (open ? close() : setOpen(npPlace(event.currentTarget)))}
      /* The same capsule the property pills wear — it is one of the answers,
         and a chip that does not look pressable is a menu nobody finds. Its
         word for the empty state is the pills' own kind of honesty: "No
         pursuit", not an invitation dressed as a value. */
      style={{ ...npBox(light, !!pursuit), maxWidth: 220, overflow: 'hidden' }}>
      <span dir="auto" style={{ overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' }}>{pursuit ? pursuit.name : 'No pursuit'}</span>
    </button>
    {open && <NpMenu title="Pursuit" at={open} accent={accent} anchor={btn} onClose={close}>
      {naming
        ? <div style={{ padding: '2px 8px 8px', display: 'flex', flexDirection: 'column', gap: 8,
          width: 216 }}>
          <input aria-label="New pursuit name" autoFocus dir="auto" value={draft}
            placeholder="What are you devoted to building?"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') begin(); }}
            style={{ width: '100%', boxSizing: 'border-box', minHeight: 30, padding: '6px 8px',
              border: `1px solid ${inkP(light ? 0.1 : 0.12)}`,
              borderRadius: 'var(--md-radius-control, 8px)', background: 'transparent',
              color: inkP(1), font: 'inherit', fontSize: 13, outline: 'none' }} />
          {/* A pursuit owns a part of life, so one cannot be begun before the
              sheet knows which. Said here rather than left as a dead button. */}
          {!domainId && <span style={{ color: inkP(0.45), fontSize: 11, lineHeight: 1.4 }}>
            Choose a part of life first — a pursuit belongs to one.</span>}
          <button type="button" aria-label="Begin pursuit" disabled={!draft.trim() || !domainId}
            onClick={begin}
            style={{ minHeight: 28, border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
              background: draft.trim() && domainId ? rgbaP(accent, 0.95) : inkP(0.07),
              color: draft.trim() && domainId ? onAccP() : inkP(0.3), font: 'inherit',
              fontSize: 12.5, fontWeight: 640,
              cursor: draft.trim() && domainId ? 'pointer' : 'default' }}>Begin pursuit</button>
        </div>
        : <div role="listbox" aria-label="Pursuit">
          {rows.map((row) => {
            const on = pursuitId === row.id;
            return <button key={row.id} type="button" role="option"
              aria-selected={on ? 'true' : 'false'} className="md-db-menu-row"
              onClick={() => { onPick(row.id, row.domainId); close(); }}>
              <span dir="auto">{row.name}</span>
              {on ? MDIP.check(inkP(0.55), 13) : null}
            </button>;
          })}
          {pursuit && <button type="button" role="option" aria-selected="false"
            className="md-db-menu-row" onClick={() => { onPick(null, null); close(); }}>
            <span>No pursuit</span>
          </button>}
          <button type="button" className="md-db-menu-row" onClick={() => setNaming(true)}>
            <span>New pursuit…</span>
          </button>
        </div>}
    </NpMenu>}
  </span>;
}

/* The reference's yellow square, which in this app is the one thing a project
   cannot be without. `AiDot`'s tile without the projects in it: hollow while
   nothing is known, the domain's own colour and icon once something is.

   And below the ten tiles, the pursuits that live in them — because "which
   part of life" and "which pursuit" are one question asked at two depths. A
   pursuit row answers both at once (it owns its domain); `New pursuit…` turns
   the SAME tiles into the answer to "where will it live": the grid stays,
   picking a tile now picks the newborn's home instead of the project's, and
   Begin hands the pursuit straight back to the sheet. Nothing navigates away;
   the thing you were already looking at becomes the next question's answer. */
/* `word` — the tile SAYS which part of life when it stands in a labelled row
   (the Details panel). Beside the project's name a wordless tile is legible,
   because the name is right there and the tile is that project's mark. Under
   the heading "Part of life", a bare circle is the one row on the panel that
   answers nothing: every other row has a word in it. */
/* `pursuits` is opt-OUT and defaults to what this file has always drawn, so the
   beginning's tile is byte-for-byte what it was (P-5's freeze proves it). The
   record sheet passes `false`: a record belongs to a PROJECT, never to a
   pursuit, so offering "New pursuit…" under its part-of-life tile would be a
   door into somebody else's vocabulary. Same tile, same menu, same colours —
   one half of the menu simply is not a question a record can be asked. */
function NpDomain({ accent, domainId, pursuitId, pursuitName, onPick, onPursuit,
  word = false, pursuits: showPursuits = true }) {
  const light = isLightP();
  const [open, setOpen] = useStP(null);
  const [naming, setNaming] = useStP(false);
  const [draft, setDraft] = useStP('');
  /* The newborn pursuit's home — begun as whatever the sheet already knows. */
  const [home, setHome] = useStP(null);
  const btn = useRefP(null);
  const domain = domainId && window.mdDomainById ? window.mdDomainById(domainId) : null;
  const color = domainId ? domColorP(domainId, light) : null;
  const close = () => { setOpen(null); setNaming(false); setDraft(''); setHome(null); };
  const pursuits = pursuitIndexP ? pursuitIndexP({ open: true }) : [];
  /* A part of life already chosen narrows the list to its own pursuits; an
     open question shows every open pursuit, each wearing its home's colour. */
  const rows = domainId ? pursuits.filter((row) => row.domainId === domainId) : pursuits;
  const beginPursuit = () => {
    const clean = draft.trim();
    if (!clean || !home) return;
    const made = pursuitCreateP(clean, home);
    if (made) { onPursuit(made.id, made.domainId); close(); }
  };
  const lit = naming ? home : domainId;
  return <span className={light ? 'md-db-light' : ''}
    style={{ display: 'inline-flex', flex: 'none', '--db-accent': accent }}>
    <button type="button" ref={btn} data-md-np-domain aria-haspopup="menu"
      aria-expanded={open ? 'true' : 'false'}
      aria-label={domain ? `Part of life — ${domain.label}` : 'Choose a part of life'}
      onClick={(event) => (open ? close()
        : setOpen(npPlace(event.currentTarget, 430, 250)))}
      style={{ height: 38, flex: 'none', border: 'none', cursor: 'pointer',
        background: 'transparent', font: 'inherit',
        ...(word
          ? { display: 'inline-flex', alignItems: 'center', gap: 8, width: 'auto',
            padding: '0 10px 0 0', maxWidth: '100%', minWidth: 0 }
          : { width: 38, display: 'grid', placeItems: 'center', padding: 0 }) }}>
      <span style={{ width: 34, height: 34, flex: 'none', borderRadius: 11, display: 'grid', placeItems: 'center',
        background: domainId ? rgbaP(color, light ? 0.16 : 0.22)
          : light ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.06)',
        boxShadow: domainId ? `inset 0 0 0 1px ${rgbaP(color, 0.45)}`
          : `inset 0 0 0 1px ${inkP(0.12)}`,
        transition: 'background .3s ease, box-shadow .3s ease' }}>
        {domainId ? domIconP(domainId, color, 19)
          : <span style={{ width: 9, height: 9, borderRadius: '50%',
            border: `1.7px solid ${inkP(0.32)}` }} />}
      </span>
      {word && <span style={{ minWidth: 0, fontSize: 13, color: inkP(domain ? 0.84 : 0.5),
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {domain ? domain.label : 'Not chosen'}
      </span>}
    </button>
    {open && <NpMenu title={naming ? 'New pursuit' : 'Which part of life'}
      at={{ ...open, width: 250 }} accent={accent} anchor={btn}
      onClose={naming ? () => { setNaming(false); setDraft(''); setHome(null); } : close}>
      {/* The naming line arrives ABOVE the tiles rather than replacing them:
          what was "which part of life is this project" a moment ago is now
          "which part of life is this pursuit", and the reader watches the
          question change hands instead of losing the picture. */}
      {naming && <div style={{ padding: '2px 4px 10px' }}>
        <input aria-label="New pursuit name" autoFocus dir="auto" value={draft}
          placeholder="What are you devoted to building?"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') beginPursuit(); }}
          style={{ width: '100%', boxSizing: 'border-box', minHeight: 30, padding: '6px 8px',
            border: `1px solid ${inkP(light ? 0.1 : 0.12)}`,
            borderRadius: 'var(--md-radius-control, 8px)', background: 'transparent',
            color: inkP(1), font: 'inherit', fontSize: 13, outline: 'none' }} />
      </div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, padding: '0 2px' }}>
        {(window.MD_DOMAINS || []).map((row) => {
          const c = domColorP(row.id, light);
          const on = lit === row.id;
          return <button key={row.id} type="button" aria-pressed={on ? 'true' : 'false'}
            aria-label={naming ? `Home — ${row.label}` : row.label}
            onClick={() => (naming ? setHome(row.id)
              : (onPick(row.id), close()))}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              padding: '8px 2px', borderRadius: 12, cursor: 'pointer',
              border: on ? `1.5px solid ${rgbaP(c, 0.9)}` : `1px solid ${inkP(0.08)}`,
              background: on ? rgbaP(c, light ? 0.14 : 0.2) : 'transparent',
              transition: 'all .15s ease' }}>
            <span style={{ width: 28, height: 28, borderRadius: 9, display: 'grid',
              placeItems: 'center', background: rgbaP(c, light ? 0.16 : 0.22) }}>
              {domIconP(row.id, c, 16)}</span>
            <span style={{ fontSize: 9.5, fontWeight: 600, color: inkP(0.6),
              whiteSpace: 'nowrap' }}>{row.label}</span>
          </button>;
        })}
      </div>
      {!showPursuits ? null : naming
        ? <div style={{ padding: '10px 4px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* A pursuit owns a part of life; the tiles above are now its
              question. Said in the tense of what the hand just did. */}
          <span style={{ color: inkP(0.45), fontSize: 11, lineHeight: 1.4 }}>
            {home && window.mdDomainById && window.mdDomainById(home)
              ? `It will live in ${window.mdDomainById(home).label}.`
              : 'Tap a part of life above to give it a home.'}</span>
          <button type="button" aria-label="Begin pursuit" disabled={!draft.trim() || !home}
            onClick={beginPursuit}
            style={{ minHeight: 28, border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
              background: draft.trim() && home ? rgbaP(accent, 0.95) : inkP(0.07),
              color: draft.trim() && home ? onAccP() : inkP(0.3), font: 'inherit',
              fontSize: 12.5, fontWeight: 640,
              cursor: draft.trim() && home ? 'pointer' : 'default' }}>Begin pursuit</button>
        </div>
        : <>
          <div aria-hidden="true" style={{ height: 1, background: inkP(light ? 0.07 : 0.09),
            margin: '10px 2px 6px' }} />
          <div className="md-label" style={{ color: inkP(0.4), fontSize: 9.5, fontWeight: 600,
            letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0 8px 4px' }}>Pursuit</div>
          <div role="listbox" aria-label="Pursuit">
            {rows.map((row) => {
              const on = pursuitId === row.id;
              const c = domColorP(row.domainId, light);
              return <button key={row.id} type="button" role="option"
                aria-selected={on ? 'true' : 'false'} className="md-db-menu-row"
                onClick={() => { onPursuit(row.id, row.domainId); close(); }}>
                {/* When every pursuit is on the table, its home's colour says
                    at a glance where saying yes would put the project. */}
                {!domainId && <span aria-hidden="true" style={{ width: 7, height: 7, flex: 'none',
                  borderRadius: '50%', background: rgbaP(c, 0.9) }} />}
                <span dir="auto" style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap' }}>{row.name}</span>
                {on ? MDIP.check(inkP(0.55), 13) : null}
              </button>;
            })}
            <button type="button" className="md-db-menu-row"
              onClick={() => { setNaming(true); setHome(domainId || null); }}>
              <span>New pursuit…</span>
            </button>
          </div>
          {/* A pursuit OWNS its part of life (md-project.ts materialises a
              project's domain from it), so the two cannot both be answers.
              Said before the press, exactly as compose says it about a
              project. And the OTHER standing fact — a project begun with the
              question still open lands in Life Ops — is said here too, in the
              picker that is the question, since the sheet stopped carrying
              the line (the fifth pass). */}
          {pursuitName && <div style={{ color: inkP(0.4), fontSize: 11, padding: '9px 4px 2px',
            lineHeight: 1.45 }}>Inside {pursuitName}. Choosing another part of life leaves it.</div>}
          {!domainId && <div style={{ color: inkP(0.4), fontSize: 11, padding: '9px 4px 2px',
            lineHeight: 1.45 }}>Left unchosen, a project begins in Life Ops.</div>}
        </>}
    </NpMenu>}
  </span>;
}

/* ════════════════════════════════════════════════════════════════════════════
   THE BEGINNING, IN THE SHEET EVERY RECORD IS BEGUN IN (CS-8)
   ---------------------------------------------------------------------------
   This used to be its own surface: its own portal, its own scrim, its own
   two-state expand button, its own 620px card, its own animation. It LOOKED
   like the compose sheet and was a separate implementation of it, which is a
   thing that can only drift — and had. The popup is 880 and this floated at
   620; the ladder had three rungs and this had a boolean; the rung you chose
   was remembered for the compose sheet and forgotten here.

   So the frame is md-compose.jsx's, published (`MdSheetFrame`), and both sheets
   are its tenants. They stand on the same rung, remember it in the same place,
   step it with the same keys, and rise the same way.

   ── AND THE ONE THING THAT IS STILL NOT SHARED ────────────────────────────
   D5: a template changes the layout, never the write law. A record exists from
   the first keystroke; a project writes NOTHING until Create, because an
   abandoned beginning must leave no residue — a project is an index row, a
   document and a thing that syncs. Same frame, opposite laws, and the two
   controls in the head say which one is in force: a record's way out is a minus
   that means "put away", this one's is an × that says nothing was begun.
   ═════════════════════════════════════════════════════════════════════════ */
function NewProjectSheet({ accent, seed = {}, onClose, onCreated, tagOptions = [],
  wikiRecords = [], onWikiOpen = null, onWikiCreate = null }) {
  const light = isLightP();
  const field = useRefP(null);

  const [name, setName] = useStP('');
  const [pursuitId, setPursuitId] = useStP(seed.pursuitId || null);
  const [domainId, setDomainId] = useStP(seed.domainId || null);
  /* A project being begun is a project being planned, so the sheet opens with
     it already standing in Backlog (Marwan, 2026-08-18) — a real answer, shown
     and changeable, not applied behind the screen. A door that SAYS something
     about status still wins outright: the + under a status column arrives with
     that column's word, and the + under "Not saying" arrives with the honest
     nothing — `'status' in seed`, so a said null beats the default.

     `statusPick` is what a HAND said — a door's word, or a pick from the pill.
     What the sheet SHOWS is that answer put through the vocabulary the draft is
     currently standing in; see `flow` below. */
  const [statusPick, setStatusPick] = useStP('status' in seed ? seed.status : 'backlog');
  const [priority, setPriority] = useStP(seed.priority || null);
  const [start, setStart] = useStP('');
  /* The forty days are SHOWN rather than allowed to happen quietly. The engine
     would have applied them to any project created without a target, and a
     sheet with a Target pill reading nothing while the project lands with one
     is the pill telling a small lie. Pre-filled and clearable: a project with
     no finish date is a normal thing for one to be. */
  const [target, setTarget] = useStP(() => (window.mdProjectDefaultTarget
    ? window.mdProjectDefaultTarget() : ''));
  const [text, setText] = useStP('');
  const [refused, setRefused] = useStP('');

  /* What the sheet held when it opened — a door's seed and the shown defaults
     are not things a hand filled in, so leaving them behind costs nothing and
     asks nothing. Everything measured against this first render. */
  const opened = useRefP(null);
  if (opened.current === null) opened.current = { status: statusPick, priority, start, target,
    pursuitId, domainId };
  const dirty = !!name.trim() || !!text.trim()
    || statusPick !== opened.current.status || priority !== opened.current.priority
    || start !== opened.current.start || target !== opened.current.target
    || pursuitId !== opened.current.pursuitId || domainId !== opened.current.domainId;
  const [confirming, setConfirming] = useStP(false);
  /* A sheet holding somebody's writing does not vanish on one keystroke. The
     × and Escape both come through here: empty, it just closes; filled, it
     asks first — through the app's own ConfirmDialog, whose word for keeping
     is not "Cancel" on a sheet where Escape was the gesture being confirmed. */
  /* The frame this stands in, and the way out of it. `exit` plays the sheet's
     own fall-out before the caller unmounts it, which the old surface could
     not do at all: it vanished. */
  const stand = window.useMdSheetStand();
  const leave = () => stand.exit(onClose);
  const attemptClose = () => {
    if (dirty && window.ConfirmDialog) setConfirming(true); else leave();
  };

  useEfP(() => { if (field.current) field.current.focus(); }, []);
  useDbEscapeLayer(() => (confirming ? setConfirming(false) : attemptClose()), true, null, 'begin');

  /* ── DETAILS, FOR A THING THAT DOES NOT EXIST YET (Marwan, 2026-08-21) ────
     The record sheet's head opens a panel describing what you are writing; this
     one opens a panel describing what you are about to begin. Same shell, same
     row rhythm, same placement arithmetic — and the SAME controls the pills
     below hold, borrowed rather than restated, so a status picked in the panel
     and a status picked on a pill are one behaviour with one bug surface.

     What it does not carry is Backlinks and Related, and that is not an
     omission to fill in later: nothing can link to a project that has not been
     created, so a section reading "nothing links here yet" would be inventing
     an emptiness rather than reporting one. It appears the moment the project
     does — in the record sheet, which is what a project opens into.

     Registered ABOVE this sheet's own escape layer because it opened later, so
     Escape peels the panel and leaves the beginning standing (rule 2). */
  const [panelOpen, setPanelOpen] = useStP(false);
  const panelPlace = window.mdInfoPanelPlacement
    ? window.mdInfoPanelPlacement(stand.size, stand.layout) : 'overlay';
  useDbEscapeLayer(() => setPanelOpen(false), panelOpen, null, 'menu');
  const panelOn = panelOpen && !!window.MdInfoShell;

  /* The guess, on compose's terms: silent until it is confident — which it
     often is not, and that is the point — and never over a pick somebody made.
     Read during the render rather than pushed into state by an effect: PICKING
     is what fills `domainId`, so a filled one already means a hand chose it (or
     a pursuit did, or the door did) and the guess simply stops being consulted.
     Kept as a derived value, the guess also follows the sentence as it grows,
     instead of freezing on the first word confident enough to land. */
  const domain = domainId
    || (window.mdInferDomain ? window.mdInferDomain(name) : null)
    || null;

  const pursuit = pursuitId && pursuitByIdP ? pursuitByIdP(pursuitId) : null;

  /* ── THE VOCABULARY THIS PROJECT WILL SPEAK, ASKED BEFORE IT EXISTS ──────
     A project's workflow cascades project → pursuit → domain → default
     (md-workflow.ts). A DRAFT has no project yet, so it asks the cascade one
     rung up: whichever parent it is currently standing in.

     Until now it asked nothing and offered the default's seven words to
     everybody. A project begun inside a pursuit that speaks its own vocabulary
     was therefore offered statuses it does not have, and landed holding a word
     its own board files under Inbox — a status nobody chose, which is exactly
     the failure `mdWorkflowResolveStatus` exists to absorb rather than one to
     manufacture. Choosing a parent now RE-SEEDS the status if the vocabulary
     it lands in has never heard the old one, and leaves it alone if it has.

     DERIVED, never stored back. An effect that rewrote the state would flash
     the old word for one frame and would have to be undone when the parent is
     un-picked; a value computed here simply follows wherever the draft is
     standing, and `statusPick` stays the record of what a HAND said — which is
     what the dirty check has to measure itself against. */
  const flow = npDraftWorkflow(pursuitId, domain);
  const status = npSeedStatus(flow, statusPick);
  const statusOptions = (flow.statuses || []).map((row) => row.id);
  const statusName = (id) => {
    const row = (flow.statuses || []).find((s) => s.id === id);
    return row ? row.name : statusLabel(id);
  };
  const statusCat = (id) => {
    const row = (flow.statuses || []).find((s) => s.id === id);
    return row ? row.category : statusCategoryOf(id);
  };

  const inverted = !!(start && target && start > target);
  /* A name is enough (Marwan, 2026-08-18) — the part of life stopped being a
     gate. The engine still requires that a project live somewhere, so one
     begun without a pick lives in Life Ops, the operations-of-living bucket,
     and the sheet SAYS so below rather than filing it there quietly. */
  const ready = !!name.trim() && !inverted;

  /* One press, one gesture, one undo step: the row and — only if anything was
     written — its document, in the same synchronous handler. */
  const begin = () => {
    if (!ready) return;
    const made = projCreateP(name.trim(), domain || 'e7', pursuitId || null,
      { status, priority, start: start || null, target: target || null });
    if (!made) { setRefused('That could not be begun. Check the dates.'); return; }
    const body = text.trim();
    if (body) projTextWriteP(made.id, `${name.trim()}\n\n${text}`);
    onCreated(made.id);
  };

  /* The rows, in the order a project is decided — where it lives, where it
     stands, how hard it pushes, when it runs, what it is about — each one the
     pill it would have been below, now with the word for it beside it. That is
     the whole gain: "30 Sep 2026" on a pill is a date you have to infer the
     meaning of, and "Target · 30 Sep 2026" is not. */
  const InfoRowP = window.MdInfoRow;
  const draftPanel = panelOn
    ? <window.MdInfoShell placement={panelPlace}>
      <window.MdInfoSection id="properties" title="Properties" first>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <InfoRowP label="Pursuit">
            <NpPursuit accent={accent} pursuitId={pursuitId} domainId={domain}
              onPick={(id, owned) => { setPursuitId(id); if (owned) setDomainId(owned); }} />
          </InfoRowP>
          <InfoRowP label="Part of life">
            <NpDomain word accent={accent} domainId={domain} pursuitId={pursuitId}
              pursuitName={pursuit ? pursuit.name : null}
              onPick={(id) => { setDomainId(id); setPursuitId(null); }}
              onPursuit={(id, owned) => { setPursuitId(id); if (owned) setDomainId(owned); }} />
          </InfoRowP>
          <InfoRowP label="Status">
            <NpChoice field="status" label="Status" value={status} accent={accent}
              options={statusOptions} labelOf={statusName}
              mark={(value) => <StatusMark category={statusCat(value)} color={rgbaP(accent, 1)} />}
              clearable={false} onPick={setStatusPick} />
          </InfoRowP>
          <InfoRowP label="Priority">
            <NpChoice field="priority" label="Priority" value={priority} accent={accent}
              options={window.MD_PROJECT_PRIORITIES || []} labelOf={priorityLabel}
              mark={(value) => <PriorityMark priority={value} color={rgbaP(accent, 1)} />}
              onPick={setPriority} />
          </InfoRowP>
          <InfoRowP label="Start">
            <NpDay field="start" label="Start" value={start} accent={accent} onChange={setStart} />
          </InfoRowP>
          <InfoRowP label="Target">
            <NpDay field="target" label="Target" value={target} accent={accent} onChange={setTarget} />
          </InfoRowP>
          <InfoRowP label="Tags">
            <NpTags accent={accent} options={tagOptions} text={text} onText={setText} />
          </InfoRowP>
        </div>
      </window.MdInfoSection>
    </window.MdInfoShell>
    : null;

  /* The frame the whole app's sheets stand in. `attrs` keeps the mark this
     surface has always been found by, so nothing that knew where the beginning
     was has to learn a new name for it; `data-md-np-card` rides the same node
     because the card and the sheet are now ONE box rather than a card inside a
     full-screen grid — which was two boxes doing the job of one, and the reason
     the device's insets had to be paid twice, in two different ways, neither of
     them the way every window in this app pays them.

     `inertBackground` because this sheet can be holding a paragraph that exists
     nowhere else: Tab must not walk out of it into the board behind. */
  return <window.MdSheetFrame light={light} accent={accent} stand={stand}
    onClose={attemptClose} closeLabel="Close without beginning"
    role="dialog" ariaLabel="New project" inertBackground
    attrs={{ 'data-md-new-project': '', 'data-md-np-card': '' }}
    onDetails={window.MdInfoShell ? () => setPanelOpen(!panelOpen) : null}
    detailsOn={panelOn}
    aside={draftPanel} asideOverlay={panelPlace === 'overlay'}
    onAsideAway={() => setPanelOpen(false)}
    /* No rule above it and no Cancel beside it: the × in the head is already
       the way out, and two controls that both mean "leave" is a reader deciding
       which one is safe. What is left is the round accent check, dead until
       there is a name.

       D5 IN ONE CONTROL. The compose sheet's ✓ on an EXISTING record means "I
       am done looking at this". This one is the write: nothing exists until it
       is pressed. Same shape because it is the same act of assent; different
       word, so what it does is never in doubt. And since P-5 the record sheet
       says it the same way when its subject does not exist yet either.

       Against the SHEET'S corner, not the measure's (Marwan, 2026-08-21). It
       used to end the rail, which put it 40pt in from the side of a popup and
       14 up from the bottom — a circle meant to read as nested in the sheet's
       rounded corner, sitting short of it by a different amount on each axis.
       One inset, both axes, and the record sheet's ✓ keeps the appointment.

       Handed to the FRAME rather than drawn among the children, so the Details
       panel cannot be laid over it. It was: the panel covers the whole box
       below the head, and this is the trailing corner of that box, so opening
       Details hid Create while leaving it drawn and enabled. The position on
       screen is unchanged — this is the same last row of the same column. */
    foot={<div data-md-np-foot="" style={{ flexShrink: 0, position: 'relative',
      zIndex: 11, minHeight: 46 + (window.MD_SHEET_INSET || 18) }}>
      <window.MdSheetDone accent={rgbaP(accent, 1)} ready={ready} onPress={begin}
        label="Create project" attrs={{ 'data-md-np-begin': '' }} />
    </div>}
    /* Asked OUTSIDE the frame's own box — a question clipped by the sheet's
       rounded edge is a question you cannot answer. The escape stack above
       silences every useEscape behind it (ConfirmDialog's included), so this
       sheet's layer answers for the confirm too: one press closes the question,
       the next asks it again. */
    after={confirming && window.ConfirmDialog
      ? <window.ConfirmDialog light={light} accent={accent}
        title="Discard this beginning?"
        message="Nothing is saved until Create — close now and what you filled in is gone."
        confirmLabel="Discard" cancelLabel="Keep writing"
        onConfirm={leave} onCancel={() => setConfirming(false)} />
      : null}>

      {/* The blessing and the way out are the sheet's head now, said once for
          both sheets. What is left here is the crumb: where this project is
          being begun, and what it is. */}
      <div style={{ flexShrink: 0, padding: '0 22px' }}>
      <div style={{ ...npRail(), display: 'flex', alignItems: 'center', gap: 4 }}>
        <NpPursuit accent={accent} pursuitId={pursuitId} domainId={domain}
          onPick={(id, owned) => { setPursuitId(id); if (owned) setDomainId(owned); }} />
        <span aria-hidden="true" style={{ color: inkP(0.3), fontSize: 12.5 }}>›</span>
        <span style={{ color: inkP(0.6), fontSize: 12.5, fontWeight: 600 }}>New project</span>
      </div>
      </div>

      <div className="md-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto',
        padding: '10px 22px 4px' }}>
      {/* THE BEGINNING RIDES THE RECORD'S OWN MEASURE. It never had to before:
          the old surface was 620pt wide and could not be made wider, so its
          form was the whole of it. On the ladder it can be 880, or two thirds
          of a laptop, or the screen — and a name line and a paragraph set
          across 1200pt are the exact thing MD_MEASURE exists to stop. Same
          rail, same number, same reason as the writing in the record beside
          it, so a project and a record read as one kind of thing. */}
      <div style={npRail()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <NpDomain accent={accent} domainId={domain} pursuitId={pursuitId}
            pursuitName={pursuit ? pursuit.name : null}
            onPick={(id) => { setDomainId(id); setPursuitId(null); }}
            onPursuit={(id, owned) => { setPursuitId(id); if (owned) setDomainId(owned); }} />
          <input ref={field} dir="auto" value={name} aria-label="Project name"
            placeholder="Project name" onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') begin(); }}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent',
              color: inkP(1), font: 'inherit', fontSize: 22, fontWeight: 640,
              outline: 'none', padding: '2px 0' }} />
        </div>

        {/* The properties, in the order a project is decided: where it stands,
            how hard it pushes, when it runs, what it is about. */}
        {/* …and they STAY while the panel holds them too (Marwan, 2026-08-21).
            They used to retire, on B1's argument that the pills are that panel
            closed. What that cost was the sheet itself: press Details and the
            beginning you were filling in lost its whole middle, for a panel
            that overlays it and is gone again a moment later. The pills and the
            panel's rows are the same components over the same state — picking a
            status in either is one call — so what is drawn twice is a value,
            never a behaviour. */}
        <div data-md-np-props="" style={{ display: 'flex', flexWrap: 'wrap', gap: 7,
          padding: '14px 0 12px' }}>
          {/* The words this project's OWN board will use, not the default
              workflow's — see `flow` above. */}
          <NpChoice field="status" label="Status" value={status} accent={accent}
            options={statusOptions} labelOf={statusName}
            mark={(value) => <StatusMark category={statusCat(value)} color={rgbaP(accent, 1)} />}
            clearable={false} onPick={setStatusPick} />
          <NpChoice field="priority" label="Priority" value={priority} accent={accent}
            options={window.MD_PROJECT_PRIORITIES || []} labelOf={priorityLabel}
            mark={(value) => <PriorityMark priority={value} color={rgbaP(accent, 1)} />}
            onPick={setPriority} />
          <NpDay field="start" label="Start" value={start} accent={accent} onChange={setStart} />
          <NpDay field="target" label="Target" value={target} accent={accent} onChange={setTarget} />
          <NpTags accent={accent} options={tagOptions} text={text} onText={setText} />
        </div>

        {/* What the project IS, ruled off from what it is ABOUT — edge to edge
            on the RAIL, the way the compose sheet rules off its own head. The
            rail is the record's width now, so a rule that bled to the sheet's
            own edge would be ruling off something wider than the thing it is
            about. */}
        <div data-md-np-rule="" aria-hidden="true"
          style={{ height: 1, background: inkP(light ? 0.07 : 0.09), margin: '0 0 12px' }} />

        {inverted && <div style={{ color: inkP(0.55), fontSize: 11.5, padding: '2px 0 6px' }}>
          That start comes after its target.</div>}
        {refused && !inverted && <div role="status"
          style={{ color: inkP(0.55), fontSize: 11.5, padding: '2px 0 6px' }}>{refused}</div>}

        {/* The same editor everything in this app is written in — held in state
            and written nowhere until Create — and at FULL POWER: the slash
            menu, the embeds, the database blocks a record's page carries
            (WrittenPage, above, is the model). One deliberate seam in the
            nothing-until-Create rule: `/database` creates a real database the
            moment it is asked for, exactly as the inline pursuit does — both
            are things the draft points AT, not parts of the draft. */}
        <div style={{ padding: '4px 0 8px' }}>
          {LiveMDP ? <LiveMDP value={text} onChange={setText} accent={accent}
            tagOptions={tagOptions} wikiRecords={wikiRecords} onWikiOpen={onWikiOpen}
            onWikiCreate={onWikiCreate} fontSize={14.5} minHeight={120}
            ariaLabel="What this project is"
            onCreateDatabase={() => {
              const db = window.mdDatabaseCreate && window.mdDatabaseCreate('Untitled database');
              return db && window.mdDatabaseLine ? window.mdDatabaseLine(db.id) : '';
            }}
            blockOf={(line) => (window.mdDatabaseBlockOf && window.mdDatabaseBlockOf(line))
              || (window.mdEmbedBlockOf && window.mdEmbedBlockOf(line))}
            blockFormats={window.mdEmbedFormatsOf} formatBlock={window.mdEmbedFormat}
            renderBlock={({ key, line, width, height, active, edit }) => {
              const raw = text.split('\n')[line];
              const databaseId = window.mdDatabaseIdFromLine ? window.mdDatabaseIdFromLine(raw) : '';
              if (databaseId) return <window.DatabaseBlock key={key} databaseId={databaseId}
                width={width} height={height} accent={accent} onRemove={() => edit('')} />;
              const e = window.mdEmbedLine ? window.mdEmbedLine(raw) : null;
              if (!e) return null;
              return <window.EmbedCard key={key} embed={e} raw={raw} light={light}
                accent={accent} active={active} width={width} height={height}
                onEdit={edit} />;
            }} /> : null}
        </div>
      </div>
      </div>

      {/* No rule above it and no Cancel beside it: the × in the head is already
          the way out, and two controls that both mean "leave" is a reader
          deciding which one is safe. What is left is the compose sheet's own
          confirmation — the round accent check (md-compose.jsx:571) — dead
          until there is a name, exactly as compose's inline creator greys out
          until it has one.

          D5 IN ONE CONTROL. The compose sheet's ✓ means "I am done looking at
          this", because the record has been saved since the first keystroke.
          THIS one is the write: nothing exists until it is pressed. Same shape
          because it is the same act of assent; different word, so what it does
          is never in doubt. */}
  </window.MdSheetFrame>;
}

/* ── THE RAIL ────────────────────────────────────────────────────────────────
   One measure for the crumb, the form, the writing and the ✓, so a project on a
   full-screen sheet is a column somebody can read rather than a line stretched
   across a laptop. It never had to have one before: the old surface was 620pt
   wide and could not be made wider, so its form WAS the whole of it. On the
   ladder it can be 880, or two thirds of a laptop, or the screen.

   It is the compose sheet's own number (`MD_MEASURE`, 780), READ rather than
   copied, because the day either one moves they have to move together — the
   beginning and the record are meant to read as one surface holding two
   different things, not two surfaces that happen to agree today. */
function npRail() {
  return { width: '100%', maxWidth: window.MD_MEASURE || 780, marginInline: 'auto',
    boxSizing: 'border-box' };
}

/* ── THE VOCABULARY A DRAFT SPEAKS ────────────────────────────────────────────
   A project's workflow cascades project → pursuit → domain → default, and
   md-workflow.ts already answers every rung of it. A draft is simply the case
   where the bottom rung does not exist yet, so it asks one step up.

   Pure, and outside the component, so the rule can be read in four lines and
   proved without a render. */
function npDraftWorkflow(pursuitId, domainId) {
  const flow = window.mdWorkflowFor
    ? (pursuitId ? window.mdWorkflowFor('pursuit', pursuitId)
      : window.mdWorkflowFor('domain', domainId || ''))
    : null;
  return flow || { id: 'default', statuses: [] };
}

/* WHAT A DRAFT STANDS IN, GIVEN WHAT A HAND SAID. `backlog` is the word a
   beginning opens with (Marwan, 2026-08-18) and every workflow that descends
   from the default still has it, so the overwhelmingly common answer is "the
   thing you asked for". A workflow that has never heard the word answers with
   its own Inbox instead — the place everything unfiled already stands, which is
   also exactly where `mdWorkflowResolveStatus` would have quietly put the
   project afterwards. Saying it BEFORE the press is the difference: the sheet
   shows the word the board will show, rather than one it will replace. */
function npSeedStatus(flow, want) {
  const rows = (flow && flow.statuses) || [];
  if (want === null || rows.some((row) => row.id === want)) return want;
  const inbox = window.mdWorkflowInboxOf ? window.mdWorkflowInboxOf(flow.id) : null;
  return inbox ? inbox.id : null;
}

function NewPursuit({ accent, onCreated, onBack, initialDomain = null }) {
  const light = isLightP();
  const [name, setName] = useStP('');
  const [domainId, setDomainId] = useStP(initialDomain);
  const ready = name.trim() && domainId;
  return <>
    <SubHeader onBack={onBack} title="New pursuit" />
    <div style={{ color: inkP(0.48), fontSize: 13.5, lineHeight: 1.5, padding: '0 4px 16px' }}>
      Enduring striving with no finish line. Projects will live inside it.
    </div>
    <GlassP radius={18} tint={surfP(0.08)} stroke={0.14} style={{ marginBottom: 22 }}>
      <input value={name} onChange={(event) => setName(event.target.value)} placeholder="What are you devoted to building?"
        aria-label="Pursuit name" autoFocus style={{ width: '100%', boxSizing: 'border-box', padding: '15px 16px',
          borderRadius: 18, border: 'none', background: 'transparent', color: inkP(1), fontSize: 16,
          fontWeight: 600, fontFamily: 'inherit', outline: 'none' }} />
    </GlassP>
    {!initialDomain && <><div className="md-label" style={{ color: inkP(0.5), fontSize: 11,
      fontWeight: 400, padding: '0 4px 10px' }}>Which part of life</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 7 }}>
        {(window.MD_DOMAINS || []).map((domain) => {
          const color = domColorP(domain.id, light); const selected = domainId === domain.id;
          return <button key={domain.id} onClick={() => setDomainId(domain.id)}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, padding: '9px 2px',
              borderRadius: 15, cursor: 'pointer', border: selected ? `1.5px solid ${rgbaP(color, .9)}` : `1px solid ${inkP(.07)}`,
              background: selected ? rgbaP(color, light ? .13 : .18) : 'transparent', transition: 'all .18s ease' }}>
            <Tile color={color} size={30} radius={10}>{domIconP(domain.id, color, 17)}</Tile>
            <span style={{ fontSize: 9.5, fontWeight: 600, color: inkP(.6) }}>{domain.label}</span>
          </button>;
        })}
      </div></>}
    <button disabled={!ready} onClick={() => { const pursuit = pursuitCreateP(name, domainId); if (pursuit) onCreated(pursuit.id); }}
      style={{ width: '100%', marginTop: 24, padding: '14px 12px', borderRadius: 18, border: 'none',
        background: ready ? rgbaP(accent, .95) : inkP(.07), cursor: ready ? 'pointer' : 'default',
        color: ready ? onAccP() : inkP(.28), fontSize: 15, fontWeight: 650,
        boxShadow: ready && !light ? `0 8px 28px ${rgbaP(accent, .28)}` : 'none', transition: 'all .2s ease' }}>
      Begin pursuit
    </button>
  </>;
}

/* ══ THE WORKSHOP ═══════════════════════════════════════════════════════════
   What replaced the Projects sheet, and why.

   The sheet was a card list inside the day panel, grouped by domain, with one
   non-stack `view` state and one remembered frame. It could not sort, filter,
   search, or show you a project's status — four words the engine had carried
   since the field was written and no screen had ever said. Closed projects were
   invisible AND unreachable. It was the weakest surface in the app.

   ── IT IS A WINDOW, AND IT IS THE SAME WINDOW ─────────────────────────────
   Databases already earned this shape: a catalogue is the thing you consult
   WHILE working, so it opens over the BOARD and takes exactly the board — the
   day stays readable beside it — and full screen is one keystroke away. Its
   geometry is imported rather than copied (`mdSpaceWindowStyle`), so there is
   one rectangle with two tenants and no chance of the day being covered by one
   of them the week `L.panel` changes.

   The second size exists only where there is somewhere to float. A phone has no
   board; an upright iPad gives its whole viewport to one. Over there the small
   size would be the full size wearing a margin, so both get full screen and no
   control at all — asked of useMdLayout every render rather than stored, so
   rotating an iPad finds the window already right.

   ── ESCAPE UNWINDS, IT DOES NOT DEMOLISH ──────────────────────────────────
   The sheet listened for Escape on window in the capture phase, which is Audit
   №1's P0-3 exactly: one keystroke closed the menu, the record and the sheet
   together, because whoever registered first took the key. This registers ONE
   layer on the shared LIFO stack (md-database.jsx:262) and pops one frame per
   press — menu, then record, then the window. Navigation is a real STACK now
   for the same reason: "back" that goes somewhere other than where you came
   from is the same lie told with a mouse.

   ── WHAT THIS SCREEN IS AND IS NOT ────────────────────────────────────────
   Projects are ROWS. Domain and pursuit are grouping and filter AXES, not rows
   of their own: a pursuit has no dates and no status by law (md-pursuit.ts:6),
   so it can never be a bar on a timeline or a card in a status column, and
   pretending otherwise is where the old screen's confusion started.

   Projects are still projects in storage. Nothing here turns them into database
   rows — that would break "storage unit = sync unit" and take the built-ins
   with it. What is shared with the database is its CHROME, not its model.

   W-5 builds the window, the toolbar and the saved views. The three view bodies
   arrive on top of it: the list in W-6, the board in W-7, the timeline in W-9.
   Until then all three tabs draw the same plain rows — the tab still switches
   and still persists, which is the part the window owes them. */

/* ONE TABLE FOR BOTH CATALOGUES, not one per window. A project has a pursuit
   and a domain; a record has a day and a created; both have a name, a status
   and time given, and those three mean the same thing at either altitude. Two
   tables would be two places to spell Status, and the day one of them is
   changed is the day the two windows stop agreeing about a word. The names do
   not collide, so a merged table costs nothing and cannot drift. */
const WS_FIELD_LABEL = { name: 'Name', pursuit: 'Pursuit', domain: 'Domain', status: 'Status',
  priority: 'Priority', start: 'Start', target: 'Target', time: 'Time given', records: 'Records',
  day: 'Day', created: 'Created' };

/* What the asking still ASKS ABOUT. Pursuit and Domain left this list when the
   lens arrived (P-4): which part of a life you are reading through is not a
   question somebody composes and keeps, it is the switcher above the table, and
   offering it in both places is two controls that must always agree.

   The ENGINE keeps both fields, deliberately. A view stored before this — or one
   arriving from a device that still offers them — filters exactly as it did,
   draws its pill, and keeps its ×. Dropping the field would silently delete a
   question out of somebody's document, which is the one thing the sanitiser
   exists to prevent. */
const WS_FILTER_FIELDS = () => (window.MD_WORKSHOP_FIELDS || [])
  .filter((field) => field !== 'pursuit' && field !== 'domain');

/* What Reset restores: the DISPLAY decisions, at their shipped defaults for
   the view's type. Filters, conjunction, name, type — the things somebody
   asked or chose — are deliberately not in this object, so a Reset can never
   widen what a view shows. */
const WS_DISPLAY_DEFAULTS = (type) => ({
  groupBy: type === 'board' ? 'status' : null,
  groupDirection: 'asc',
  sorts: [],
  order: [],
  showClosed: false,
  showEmptyGroups: true,
  timelineZoom: 'month',
  /* The shipped default, which is not every field: Domain is a column
     somebody can ask for, not one a Reset should hand back. */
  visibleFields: [...(window.MD_WORKSHOP_DEFAULT_FIELDS || window.MD_WORKSHOP_FIELDS || [])],
});

const WS_OP_LABEL = { is: 'is', is_not: 'is not', contains: 'contains',
  not_contains: 'does not contain', empty: 'is empty', not_empty: 'is not empty',
  before: 'before', after: 'after', on_or_before: 'on or before', on_or_after: 'on or after' };

/* Which questions a field can actually answer. Offering "before" on a status is
   a menu that produces a filter matching nothing, which reads as a broken
   screen rather than as a nonsense question. */
const WS_TEXT_OPS = ['contains', 'not_contains', 'is', 'is_not', 'empty', 'not_empty'];
const WS_PICK_OPS = ['is', 'is_not', 'empty', 'not_empty'];
const WS_DAY_OPS = ['is', 'before', 'after', 'on_or_before', 'on_or_after', 'empty', 'not_empty'];
const WS_NUM_OPS = ['is', 'is_not', 'before', 'after'];
const WS_OPS_FOR = { name: WS_TEXT_OPS, status: WS_PICK_OPS, priority: WS_PICK_OPS,
  pursuit: WS_PICK_OPS, domain: WS_PICK_OPS, start: WS_DAY_OPS, target: WS_DAY_OPS,
  time: WS_NUM_OPS, records: WS_NUM_OPS, day: WS_DAY_OPS, created: WS_DAY_OPS };

/* The choosable values of a field that has any. `''` is the empty one and it is
   a real choice — "projects with no pursuit" is a question people ask. */
function wsChoices(field) {
  if (field === 'status') {
    return (window.mdWorkflowById ? window.mdWorkflowById('default').statuses : [])
      .map((row) => ({ id: row.id, label: row.name, category: row.category }));
  }
  if (field === 'priority') return [...(window.MD_PROJECT_PRIORITIES || [])].map((id) => ({ id, label: priorityLabel(id) }));
  if (field === 'domain') return (window.MD_DOMAINS || []).map((d) => ({ id: d.id, label: d.label }));
  if (field === 'pursuit') return (pursuitIndexP ? pursuitIndexP() : []).map((p) => ({ id: p.id, label: p.name }));
  return null;
}

/* How many answers, said in the field's own noun — "2 statuses", not
   "2 values". */
const WS_MANY = { status: 'statuses', priority: 'priorities', domain: 'domains',
  pursuit: 'pursuits', name: 'names', start: 'days', target: 'days', time: 'amounts',
  records: 'counts', day: 'days', created: 'days' };

/* The one ink a destructive control is allowed, the same rose the row menus
   already spend on Delete project. */
const WS_DANGER = (light) => (light ? '#E11D48' : '#F43F5E');

/* The operator, worded for the value it holds. Several answers flip is/is not
   to any-of/none-of: "Status is Active, Parked" reads as a contradiction, and
   "is any of" is what the engine actually does with the array. */
function wsOpLabel(filter) {
  if (Array.isArray(filter.value) && filter.value.length > 1) {
    if (filter.operator === 'is') return 'is any of';
    if (filter.operator === 'is_not') return 'is none of';
  }
  return WS_OP_LABEL[filter.operator] || filter.operator;
}

/* The value's own word: one answer by its name, several as a count. A count
   of one is not printed as a count — "Parked", never "1 status". */
function wsValueLabel(filter) {
  const choices = wsChoices(filter.field);
  const one = (value) => (choices
    ? (choices.find((c) => c.id === value) || { label: statusLabel(null) }).label
    : (value == null || value === '' ? '—' : String(value)));
  if (Array.isArray(filter.value)) {
    if (filter.value.length === 1) return one(filter.value[0]);
    return `${filter.value.length} ${WS_MANY[filter.field] || 'answers'}`;
  }
  return one(filter.value);
}

function wsFilterLabel(filter) {
  const field = WS_FIELD_LABEL[filter.field] || filter.field;
  if (filter.operator === 'empty' || filter.operator === 'not_empty')
    return `${field} ${WS_OP_LABEL[filter.operator]}`;
  return `${field} ${wsOpLabel(filter)} ${wsValueLabel(filter)}`;
}

/* ── a chip on the timeline's rail ────────────────────────────────────────
   The property, small and live: press the status ring and set the status
   without leaving the rail. The picker is a DbMenu — the ONE escape stack,
   so Escape closes it and stops (W-0) — but it hangs FIXED from the chip's
   own box rather than absolutely inside it: the rail truncates its overflow,
   and a menu clipped to a 46px row is a menu nobody can read.

   The write goes through the door that can refuse (wsCommit), and the chip
   never paints optimistically — the row it draws from is re-read after the
   write, so the engine's word is the one that shows. */
function WsRailChip({ row, field, accent, onWrite }) {
  const light = isLightP();
  const [open, setOpen] = useStP(null);           // the chip's box, while open
  const value = field === 'status' ? (row.status || null)
    : field === 'priority' ? (row.priority || null)
      : field === 'pursuit' ? (row.pursuitId || null) : (row.domainId || null);
  /* THE ROW'S OWN VOCABULARY for a status, not the account's default one: this
     chip writes to THIS project, and offering it a word its workflow has never
     heard of is offering a choice the door will refuse. Every other field is
     the same list for every row. */
  const choices = (field === 'status' && window.mdProjectStatuses
    ? window.mdProjectStatuses(row.id).map((s) => ({ id: s.id, label: s.name, category: s.category }))
    : wsChoices(field)) || [];
  const found = choices.find((choice) => choice.id === value);
  const valueLabel = field === 'status' ? (found ? found.label : row.statusName)
    : field === 'priority' ? priorityLabel(value)
      : found ? found.label : (field === 'pursuit' ? 'No pursuit' : MD_NONE);
  /* A pursuit and a priority can honestly be nothing; a project always has a
     domain, and since WF-2 it always has a status too — Inbox is in `choices`
     already, so an absence row there would be the same place offered twice. */
  const options = field === 'priority' ? [...choices, { id: null, label: priorityLabel(null) }]
    : field === 'pursuit' ? [{ id: null, label: 'No pursuit' }, ...choices]
      : choices;
  const face = field === 'status'
    ? <StatusMark category={row.statusCategory} color={row.statusColor || null} size={11} />
    : field === 'priority'
      ? <PriorityMark priority={value} color={domColorP(row.domainId, light) || accent} size={11} />
    : field === 'domain'
      ? <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 3, flex: 'none',
        background: value ? rgbaP(domColorP(value, light), 0.85) : inkP(0.22) }} />
      : <span style={{ maxWidth: 76, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', color: inkP(value ? 0.55 : 0.3), fontSize: 10.5 }}>{valueLabel}</span>;
  return <span className={light ? 'md-db-light' : ''}
    style={{ display: 'inline-flex', flex: 'none', '--db-accent': accent }}>
    <button type="button" data-md-rail-chip={field}
      aria-label={`${WS_FIELD_LABEL[field]}: ${valueLabel} — ${row.name}`}
      aria-haspopup="listbox" aria-expanded={open ? 'true' : 'false'}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        if (open) { setOpen(null); return; }
        /* Fixed positioning answers to the nearest CONTAINING BLOCK, and the
           Workshop window's backdrop-filter makes the window exactly that — so
           the chip's viewport box is translated into the window's own frame,
           and clamped so the menu never runs past the window's clipped edge. */
        const box = event.currentTarget.getBoundingClientRect();
        const base = wsSpaceBase(event.currentTarget);
        setOpen({ x: Math.max(6, Math.min(box.left - base.left, (base.width || 9999) - 226)),
          y: Math.min(box.bottom - base.top + 4, (base.height || 9999) - 316) });
      }}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 20, height: 20, padding: field === 'pursuit' ? '0 5px' : 0, border: 'none',
        borderRadius: 6, background: 'transparent', cursor: 'pointer', font: 'inherit' }}>
      {face}</button>
    {open && <DbMenu title={WS_FIELD_LABEL[field]} onClose={() => setOpen(null)}
      style={{ position: 'fixed', top: open.y, left: open.x, right: 'auto',
        insetInlineEnd: 'auto', maxHeight: 300, overflowY: 'auto', zIndex: 60 }}>
      <div role="listbox" aria-label={WS_FIELD_LABEL[field]}>
        {options.map((option) => {
          const on = value === (option.id || null);
          return <button key={option.id || 'none'} type="button" role="option"
            aria-selected={on ? 'true' : 'false'} className="md-db-menu-row"
            onClick={() => { setOpen(null); if (!on) onWrite(field, row.id, option.id); }}>
            {field === 'status' ? <StatusMark category={option.category} color={option.color || null} /> : null}
            {field === 'priority' ? <PriorityMark priority={option.id} color={rgbaP(accent, 1)} /> : null}
            <span>{option.label}</span>
            {on ? MDIP.check(inkP(0.55), 13) : null}
          </button>;
        })}
      </div>
    </DbMenu>}
  </span>;
}

/* ── the columns ──────────────────────────────────────────────────────────
   Eight facts the engine has always held, and a list that used to print three.
   The widths are a design decision: the name takes what is left, and everything
   else is exactly as wide as its widest honest value — a date column sized for
   a date, a count sized for a count. Nothing is sized for the header's own
   word, because the header is read once and the values are read every time. */
/* `day` is the one column NOT sized for a date. A record's day is either the one
   somebody chose or the one it was written on, and the cell says which — so its
   widest honest value is "Written 16 Aug" and not "16 Aug". Sized for the date
   alone, the word that carries the whole distinction is what gets cut. */
const WS_COL_WIDTH = { name: 'minmax(210px,2.6fr)', pursuit: 'minmax(96px,1fr)', domain: '78px',
  status: '98px', priority: '88px', start: '80px', target: '80px', time: '82px', records: '58px',
  day: '112px', created: '80px' };
/* What each column needs before anything has to be cut. Below their sum the
   table SCROLLS SIDEWAYS rather than squeezing — eight columns do not fit the
   board-width window, and squeezing them turns the one column that matters into
   "40 Days On Time …" while a 58px count column sits at full size beside it.
   The name is what the reader came for. Hide a column and the floor drops with
   it, so a three-column view never scrolls at all. */
const WS_COL_MIN = { name: 210, pursuit: 96, domain: 78, status: 98, priority: 88, start: 80,
  target: 80, time: 82, records: 58, day: 112, created: 80 };
const WS_COL_GAP = 10;
/* On a phone the window IS the screen, and eight columns become a sideways
   scroll in which the first thing to slide off is the name. So a narrow window
   shows the head of the list and nothing else. It does not touch the saved
   view: this is what fits, not what anybody chose, and the columns come back
   the moment there is room for them. (W-13) */
const WS_NARROW_FIELDS = ['name', 'status', 'target'];
/* The same decision for a record: what it is, where it stands, when it is. */
/* Every frame the Projects window knows how to draw. A project's page is
   deliberately NOT here: it belongs to the Project window now (and a backlog
   record stopped being a frame anywhere — it opens in the compose sheet,
   OD-3). Kept as one list so the switch below and the door above cannot
   disagree about what this window can show. */
const WS_FRAMES = ['domain', 'pursuit', 'new-pursuit'];

const WS_NARROW_RECORD_FIELDS = ['name', 'status', 'day'];

/* What the bucket for "nobody has said" is called, per axis — the same words
   md-workshop.ts labels the column with, in mid-sentence case. */
const WS_EMPTY_LABEL = { status: 'not saying', pursuit: 'no pursuit', domain: 'no domain' };

const WS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* A day, written the way a person says it. The year appears only when it is not
   this one — on a list where nearly everything is this year, printing 2026
   sixty times is sixty repetitions of the one thing the reader already knows. */
function wsDayLabel(day, todayKey) {
  if (typeof day !== 'string' || day.length < 10) return MD_NONE;
  const month = WS_MONTHS[Number(day.slice(5, 7)) - 1] || '';
  const short = `${Number(day.slice(8, 10))} ${month}`;
  return todayKey && day.slice(0, 4) === String(todayKey).slice(0, 4) ? short : `${short} ${day.slice(0, 4)}`;
}

/* An icon-only control in the toolbar. Named without exception: an unlabelled
   glyph is a control only to somebody who can see it. */
function WsTool({ label, onClick, active = false, children, text = null }) {
  const light = isLightP();
  return <button type="button" onClick={onClick} aria-label={label} title={label}
    aria-pressed={active ? 'true' : undefined}
    style={{ height: 30, minWidth: text ? 0 : 30, display: 'flex', alignItems: 'center', gap: 5,
      flex: 'none', border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
      padding: text ? '0 9px' : 0, justifyContent: 'center', cursor: 'pointer',
      background: active ? inkP(light ? 0.06 : 0.08) : 'transparent',
      color: inkP(active ? 0.86 : 0.52), font: 'inherit', fontSize: 12, fontWeight: 600 }}>
    {children}{text ? <span>{text}</span> : null}
  </button>;
}

/* A popover's anchor. The menu is absolutely positioned against it, which is
   what `.md-db-menu` expects of whatever holds it. */
function WsAnchor({ children }) {
  return <span className="md-db-menu-anchor" style={{ position: 'relative', display: 'flex',
    alignItems: 'center', flex: 'none' }}>{children}</span>;
}

function WsMenuRow({ onClick, children, on = false }) {
  return <button type="button" className="md-db-menu-row" onClick={onClick}
    aria-pressed={on ? 'true' : undefined}>
    <span style={{ flex: 1, textAlign: 'start' }}>{children}</span>
    {on ? MDIP.check(inkP(0.55), 13) : null}
  </button>;
}

/* ── the window everything inside it measures from ────────────────────────
   THIS GRAMMAR IS WORN BY TWO WINDOWS, and for a long time it only knew about
   one of them. The Workshop's own space is `data-md-projects-space`; a single
   project's window (md-project-space.jsx) is `data-md-project-space`, and it
   portals to the body rather than nesting inside the other — so a query for the
   plural walked the whole way up from a card in the Project window and found
   nothing at all.

   What it found instead was the fallback, the viewport's own corner. That is
   the right answer for a window that starts at the corner and a silently wrong
   one for a window that does not: a resizable space is a gutter in from every
   edge (md-databases.jsx — `max(safe, 22px)`), so everything absolute or fixed
   inside the Project window was placed a gutter down and a gutter across from
   where it belonged. Menus hung off their words, and a dropped card was drawn
   twice: once where the board put it, once a gutter away, which read as an echo.

   One name for both windows, then, in one place, because the failure mode of
   two copies of a selector is that only one of them learns. */
const WS_SPACE = '[data-md-projects-space],[data-md-project-space]';

/** The window `el` is inside, as a box. The fallback stands for "no window
    found", and it is the viewport — which is the truth for a full-bleed space
    and the best guess available for anything that is not in a window at all. */
function wsSpaceBase(el) {
  const host = el && el.closest ? el.closest(WS_SPACE) : null;
  return host && host.getBoundingClientRect ? host.getBoundingClientRect()
    : { left: 0, top: 0, height: window.innerHeight };
}

/* Where a menu hangs from a pressed word: the word's own viewport box,
   translated into the window's frame — the window's backdrop-filter makes it
   the containing block for position:fixed — and clamped inside it. The rail
   the word sits on scrolls sideways, and a menu clipped to a 25px chip is a
   menu nobody can read (the WsRailChip lesson, paid once already). */
function wsBoxPlace(el) {
  const box = el.getBoundingClientRect();
  const base = wsSpaceBase(el);
  return { x: Math.max(6, Math.min(box.left - base.left, (base.width || 9999) - 226)),
    y: Math.min(box.bottom - base.top + 4, (base.height || 9999) - 316) };
}
const wsMenuPlace = (event) => wsBoxPlace(event.currentTarget);

/* ── a filter, said as a pill of words ────────────────────────────────────
   Linear's rail grammar in the Workshop's voice: one 25px chip per filter,
   [field | operator | value | ×], every word its own button with a hairline
   between neighbours. The field and value words open the asking menu scoped
   to their field — the one place answers are chosen; the operator word opens
   the small menu of only the questions its field can answer; × withdraws the
   filter whole. DOM order is the sentence's order, so the tab key reads it
   the way an eye does.

   The value wears dir="auto": an Arabic name sits right inside an English
   rail and the reverse, without the pill taking a side. */
function WsPill({ filter, accent, onAsk, onWrite, onRemove }) {
  const light = isLightP();
  const [opMenu, setOpMenu] = useStP(null);       // where the menu hangs, while open
  const valueless = filter.operator === 'empty' || filter.operator === 'not_empty';
  const word = { height: 25, border: 'none', background: 'transparent', cursor: 'pointer',
    font: 'inherit', fontSize: 10.5, whiteSpace: 'nowrap', padding: '0 7px',
    color: inkP(0.78), display: 'inline-flex', alignItems: 'center' };
  const rule = <span aria-hidden="true" style={{ width: 1, margin: '5px 0', flex: 'none',
    background: inkP(light ? 0.1 : 0.13) }} />;
  return <span data-md-pill style={{ display: 'inline-flex', alignItems: 'stretch', flex: 'none',
    height: 25, borderRadius: 'var(--md-radius-control, 8px)',
    background: rgbaP(accent, light ? 0.1 : 0.14),
    animation: wsStill() ? 'none' : 'mdDbSpring .18s cubic-bezier(.22,.8,.25,1)' }}>
    <button type="button" data-md-pill-field style={word}
      aria-label={`${wsFilterLabel(filter)} — change what it asks about`}
      onClick={(event) => onAsk(wsMenuPlace(event))}>
      {WS_FIELD_LABEL[filter.field]}</button>
    {rule}
    <button type="button" data-md-pill-op style={{ ...word, color: inkP(0.52) }}
      aria-label="How this filter asks" aria-haspopup="menu"
      aria-expanded={opMenu ? 'true' : 'false'}
      onClick={(event) => setOpMenu(opMenu ? null : wsMenuPlace(event))}>
      {wsOpLabel(filter)}</button>
    {opMenu && <DbMenu title="How it asks" onClose={() => setOpMenu(null)}
      style={{ position: 'fixed', top: opMenu.y, left: opMenu.x, right: 'auto',
        insetInlineEnd: 'auto', zIndex: 60 }}>
      {(WS_OPS_FOR[filter.field] || WS_PICK_OPS).map((op) => <WsMenuRow key={op}
        on={op === filter.operator}
        onClick={() => { setOpMenu(null); if (op !== filter.operator) onWrite(op); }}>
        {wsOpLabel({ ...filter, operator: op })}
      </WsMenuRow>)}
    </DbMenu>}
    {!valueless && <>
      {rule}
      <button type="button" data-md-pill-value dir="auto" style={{ ...word, fontWeight: 620 }}
        aria-label={`${wsFilterLabel(filter)} — choose the answers`}
        onClick={(event) => onAsk(wsMenuPlace(event))}>
        {wsValueLabel(filter)}</button>
    </>}
    {rule}
    <button type="button" data-md-pill-remove aria-label="Remove this filter"
      title="Remove this filter" onClick={onRemove}
      style={{ ...word, padding: '0 7px', color: inkP(0.42), fontSize: 12 }}>
      <span aria-hidden="true">×</span></button>
  </span>;
}

/* ── the asking menu ──────────────────────────────────────────────────────
   One door for what a filter asks about and which answers it holds. Two
   pages: the fields, then the field's values. This is the picker's FRAME —
   Q-3 teaches it to count, to search, and to hold several answers at once.

   Nothing is written until an answer is chosen: choosing a field only turns
   the page, so backing out of the menu leaves the view exactly as it was.
   The one exception is a typed field (a name, a day, an amount), whose input
   needs a filter to live in — choosing one writes the empty question and
   hands the keyboard to its input, which is what the old form did with less
   honesty about it. */
function WsAsk({ view, ctx, patch, at, field, x, y, onClose, lens = null }) {
  const light = isLightP();
  /* WHICH CATALOGUE IS BEING ASKED ABOUT. The menu used to reach for the
     Workshop's fields and the Workshop's counting out of the air, which is
     right until a second window opens: a Project window offering Pursuit and
     Domain would be offering the two things that cannot vary inside it. The
     lens is handed in; absent, this is the Workshop, exactly as it was. */
  const askFields = (lens && lens.fields) || WS_FILTER_FIELDS();
  const askChoices = (lens && lens.choices) || wsChoices;
  const askCounts = (lens && lens.facetCounts) || window.mdWorkshopFacetCounts;
  const [page, setPage] = useStP({ at, field });
  const [query, setQuery] = useStP('');            // page one's type-to-filter
  const [unfolded, setUnfolded] = useStP(false);   // the zero-count fold, opened
  const write = (index, changes) => patch({ filters: view.filters.map((row, i) =>
    (i === index ? { ...row, ...changes } : row)) });
  const filter = page.at >= 0 ? view.filters[page.at] : null;
  const frame = { position: 'fixed', top: y, left: x, right: 'auto', insetInlineEnd: 'auto',
    maxHeight: 320, overflowY: 'auto', zIndex: 60 };
  const finder = { width: 'calc(100% - 10px)', height: 32, margin: '2px 5px 6px', border: 'none',
    borderRadius: 'var(--md-radius-control, 8px)', outline: 'none', padding: '0 9px',
    color: inkP(0.9), background: inkP(light ? 0.05 : 0.07), font: 'inherit', fontSize: 12.5 };

  /* Page one: the fields, narrowed as fast as somebody can type. */
  if (!page.field) {
    const needle = query.trim().toLocaleLowerCase();
    const fields = askFields
      .filter((f) => !needle || (WS_FIELD_LABEL[f] || f).toLocaleLowerCase().includes(needle));
    return <DbMenu title="Filter by" onClose={onClose} style={frame}>
      <span style={{ position: 'relative', display: 'block' }}>
        <input aria-label="Find a property" placeholder="Filter…" autoFocus value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ ...finder, paddingInlineEnd: 28, boxSizing: 'border-box' }} />
        {/* The key that opened this door, said faintly where the eye lands
            last — the same courtesy Linear pays, hidden from the reader whose
            screen is spoken. */}
        <kbd aria-hidden="true" style={{ position: 'absolute', top: '50%', insetInlineEnd: 13,
          transform: 'translateY(-50%)', padding: '1px 5px', borderRadius: 4,
          border: `1px solid ${inkP(0.14)}`, color: inkP(0.34), fontSize: 9.5,
          fontFamily: 'inherit' }}>F</kbd>
      </span>
      {fields.map((f) => <WsMenuRow key={f}
        onClick={() => {
          const first = (WS_OPS_FOR[f] || WS_PICK_OPS)[0];
          if (page.at >= 0 && filter && filter.field !== f) {
            write(page.at, { field: f, operator: first, value: null });
            setPage({ at: page.at, field: f });
          } else if (page.at < 0 && !askChoices(f)) {
            /* A typed answer needs a filter to live in — made here, on the
               way in, not as a side effect of drawing the page. */
            patch({ filters: [...view.filters, { field: f, operator: first, value: '' }] });
            setPage({ at: view.filters.length, field: f });
          } else {
            setPage({ at: page.at, field: f });
          }
        }}>
        {WS_FIELD_LABEL[f]}
      </WsMenuRow>)}
      {!fields.length && <div style={{ padding: '8px 10px 10px', color: inkP(0.4), fontSize: 12.5 }}>
        No property by that name.</div>}
    </DbMenu>;
  }

  /* Page two: the field's answers, each wearing the count of rows it would
     show. The counts come from the same pipeline that draws the list —
     conditioned on the view's OTHER filters, never this field's own, so the
     menu never starves itself (mdWorkshopFacetCounts, Q-1). */
  const choices = askChoices(page.field);
  const noneWord = page.field === 'pursuit' ? 'No pursuit'
    : page.field === 'domain' ? 'No domain' : statusLabel(null);
  /* Withdrawing the last answer withdraws the filter whole: nothing asked is
     no filter at all, not a filter matching nothing. */
  const withdraw = () => {
    if (page.at >= 0) patch({ filters: view.filters.filter((_, i) => i !== page.at) });
    onClose();
  };
  if (choices) {
    const counts = askCounts ? askCounts(view, ctx, page.field) : {};
    const negative = !!filter && (filter.operator === 'is_not' || filter.operator === 'not_empty'
      || filter.operator === 'not_contains');
    const noneOn = !!filter && (filter.operator === 'empty' || filter.operator === 'not_empty');
    const held = filter && !noneOn ? (Array.isArray(filter.value) ? filter.value.map(String)
      : filter.value == null || filter.value === '' ? [] : [String(filter.value)]) : [];
    /* "No pursuit" and "no domain" are real answers. "No status" stopped being
       one in Phase 5 — every project stands somewhere, so the row would be an
       answer that can never count anything, sitting under an Inbox row that
       already finds exactly the projects it used to mean. */
    const asksAbsence = page.field !== 'status';
    const all = [
      ...choices.map((choice) => ({ id: String(choice.id), label: choice.label,
        count: counts[String(choice.id)] || 0, on: held.includes(String(choice.id)), none: false })),
      ...(asksAbsence
        ? [{ id: null, label: noneWord, count: counts.empty || 0, on: noneOn, none: true }] : []),
    ];
    /* An answer that matches nothing folds — unless somebody HOLDS it, because
       a checked answer that vanished would read as the menu lying. */
    const shown = all.filter((row) => row.on || row.count > 0 || unfolded);
    const folded = all.length - shown.length;
    const toggle = (row) => {
      if (row.none) {
        if (row.on) { withdraw(); return; }
        /* "No pursuit" is a real answer, and the engine's word for it is the
           empty operator — `is` against nothing matches nothing, honestly. */
        const operator = negative ? 'not_empty' : 'empty';
        if (page.at >= 0) write(page.at, { operator, value: null });
        else {
          patch({ filters: [...view.filters, { field: page.field, operator: 'empty', value: null }] });
          setPage({ at: view.filters.length, field: page.field });
        }
        return;
      }
      const next = row.on ? held.filter((id) => id !== row.id) : [...held, row.id];
      if (!next.length) { withdraw(); return; }
      if (page.at >= 0) write(page.at, { operator: negative ? 'is_not' : 'is', value: next });
      else {
        patch({ filters: [...view.filters, { field: page.field, operator: 'is', value: next }] });
        setPage({ at: view.filters.length, field: page.field });
      }
    };
    return <DbMenu title={WS_FIELD_LABEL[page.field]} onClose={onClose} style={frame}>
      <div role="listbox" aria-multiselectable="true" aria-label={WS_FIELD_LABEL[page.field]}>
        {shown.map((row) => <button key={row.id || 'none'} type="button" role="option"
          aria-selected={row.on ? 'true' : 'false'} className="md-db-menu-row"
          onClick={() => toggle(row)}>
          <span style={{ flex: 1, textAlign: 'start',
            color: row.none ? inkP(0.55) : undefined }}>{row.label}</span>
          <span data-md-count style={{ color: inkP(0.35), fontSize: 10.5,
            fontVariantNumeric: 'tabular-nums' }}>{row.count}</span>
          {row.on ? MDIP.check(inkP(0.55), 13) : null}
        </button>)}
      </div>
      {!unfolded && folded > 0 && <button type="button" data-md-fold className="md-db-menu-row"
        onClick={() => setUnfolded(true)}>
        {/* dir="auto": the sentence leads with its count, and a digit is
            direction-neutral — without this an RTL page reads the line as
            "options match nothing here 2". */}
        <span dir="auto" style={{ flex: 1, textAlign: 'start', color: inkP(0.42), fontSize: 11 }}>
          {folded === 1 ? '1 option matches nothing here' : `${folded} options match nothing here`}
        </span>
      </button>}
    </DbMenu>;
  }

  /* A typed answer: a name fragment, a day, an amount. The input writes live,
     the way the form's did; the filter it writes into was made on the way in. */
  if (!filter) return null;
  return <DbMenu title={WS_FIELD_LABEL[page.field]} onClose={onClose} style={frame}>
    <input aria-label={`${WS_FIELD_LABEL[page.field]} to ask for`} autoFocus
      value={filter && filter.value != null ? String(filter.value) : ''}
      type={page.field === 'start' || page.field === 'target' ? 'date'
        : page.field === 'time' || page.field === 'records' ? 'number' : 'text'}
      onChange={(event) => write(page.at, { value: event.target.value })}
      style={{ width: 'calc(100% - 10px)', height: 32, margin: '2px 5px 6px', border: 'none',
        borderRadius: 'var(--md-radius-control, 8px)', outline: 'none', padding: '0 9px',
        color: inkP(0.9), background: inkP(light ? 0.05 : 0.07), font: 'inherit', fontSize: 12.5 }} />
  </DbMenu>;
}

/* ── the lens ─────────────────────────────────────────────────────────────
   Which part of a life the window is being read through. It is not a filter,
   and it is not drawn like one: it stands in the row that NAMES the window,
   between the title and the plus, because that row is where the window's own
   doors are. The rail belongs to the questions somebody composed; this is the
   thing they switch twenty times a day.

   THE MENU IS A LIFE, NOT A LIST OF CHECKBOXES. Pursuits stand under the domain
   they already belong to, in the order md-domain.ts declares them — Spirit
   first, because prayer is the axis of this app and the list should say so
   before anything else does. The heading is itself the control: a part of life
   is taken whole or given back whole, and its mark says all / some / none
   before a hand arrives. `aria-checked="mixed"` is that third state said out
   loud rather than drawn and left unsaid.

   FROM ALL, A PRESS STARTS A SELECTION. Pressing one pursuit while the lens is
   open means "this one" — not "all of them minus nothing", which is what ten
   ticked boxes would have said. Untaking the last one returns to All Pursuits,
   never to a blank rectangle: an empty lens is no lens, which is the engine's
   own law (md-workshop.ts) rather than a kindness this screen invented.

   The counts are the engine's arithmetic, asked against the UNSCOPED view, so
   the numbers do not shuffle under the hand that is choosing. */

const WS_LENS_ALL = 'All Pursuits';

/* Rested pursuits are not offered: a pursuit somebody has set down is not a
   lens they switch to, and its work is still there under All Pursuits. */
const wsLensPursuits = () => (pursuitIndexP ? pursuitIndexP({ open: true }) : []);

function wsLensLabel(scope, pursuits) {
  if (!scope || !scope.length) return WS_LENS_ALL;
  /* Capital P: the word is the control's own name — the same Pursuits that
     stands on the button when nothing is chosen — not a count of things. */
  if (scope.length > 1) return `${scope.length} Pursuits`;
  if (scope[0] === 'empty') return 'No pursuit';
  const one = pursuits.find((row) => row.id === scope[0]);
  return one ? one.name : '1 pursuit';
}

/* A lens held inside ONE part of life wears that part's own colour — the hue
   the rest of the app has been spending on it since the beginning. Across two
   there is no honest single colour, so it falls back to the window's accent
   rather than picking a winner. */
function wsLensColor(scope, pursuits, light, accent) {
  if (!scope || !scope.length) return null;
  const domains = new Set();
  for (const id of scope) {
    const one = pursuits.find((row) => row.id === id);
    domains.add(one ? one.domainId : null);       // 'empty' has no part of life
  }
  const only = domains.size === 1 ? [...domains][0] : null;
  return only ? domColorP(only, light) : accent;
}

/* The one pursuit a new project should be born into, when the lens is holding
   exactly one — the same law the board's per-column plus already keeps: a
   thing begun inside a place belongs to the place it was begun in. */
function wsLensSeed(scope) {
  if (!scope || scope.length !== 1 || scope[0] === 'empty') return {};
  const one = wsLensPursuits().find((row) => row.id === scope[0]);
  return one ? { pursuitId: one.id, domainId: one.domainId } : {};
}

function WsLens({ scope, accent, open, onOpen, onClose }) {
  const light = isLightP();
  const pursuits = wsLensPursuits();
  const label = wsLensLabel(scope, pursuits);
  const on = !!(scope && scope.length);
  const color = wsLensColor(scope, pursuits, light, accent) || inkP(0.3);
  return <button type="button" data-md-lens="" aria-haspopup="menu"
    aria-expanded={open ? 'true' : 'false'} aria-label={`Pursuits — ${label}`} title="Pursuits"
    onClick={(event) => (open ? onClose() : onOpen(wsMenuPlace(event)))}
    style={{ height: 28, maxWidth: 196, flex: 'none', display: 'inline-flex', alignItems: 'center',
      gap: 7, border: 'none', borderRadius: 'var(--md-radius-control, 8px)', padding: '0 9px',
      cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 620,
      background: on ? rgbaP(color, light ? 0.12 : 0.16) : inkP(light ? 0.045 : 0.06),
      color: inkP(on ? 0.88 : 0.62) }}>
    <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 4, flex: 'none',
      background: on ? rgbaP(color, 0.9) : inkP(0.28) }} />
    <span data-md-lens-label style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' }}>{label}</span>
    {MDIP.chevDown ? MDIP.chevDown(inkP(0.38), 10) : <span aria-hidden="true">▾</span>}
  </button>;
}

/* all · some · none, painted rather than borrowed: .md-db-tick's colours are
   scoped to the database's dark surface and would leave a white box on a white
   page (the PP-3 trap this file has been caught by before). */
function WsLensBox({ state, color, light }) {
  const filled = state !== 'none';
  return <span aria-hidden="true" style={{ width: 15, height: 15, flex: 'none', borderRadius: 5,
    boxSizing: 'border-box', display: 'grid', placeItems: 'center',
    border: filled ? 'none' : `1.5px solid ${inkP(light ? 0.18 : 0.22)}`,
    background: filled ? rgbaP(color, light ? 0.9 : 0.85) : 'transparent' }}>
    {state === 'all' ? (MDIP.check ? MDIP.check('#fff', 10) : null)
      : state === 'some' ? <span style={{ display: 'block', width: 7, height: 2, borderRadius: 1,
        background: '#fff' }} /> : null}
  </span>;
}

function WsLensMenu({ scope, onScope, view, ctx, accent, x, y, onClose }) {
  const light = isLightP();
  const [query, setQuery] = useStP('');
  const pursuits = wsLensPursuits();
  /* Against the unscoped view: what each choice WOULD show, not what the
     current lens already lets through. */
  const counts = window.mdWorkshopFacetCounts
    ? window.mdWorkshopFacetCounts({ ...view, scope: null }, ctx, 'pursuit') : {};
  const held = scope && scope.length ? scope : null;
  const has = (id) => !!held && held.indexOf(id) !== -1;
  const write = (ids) => onScope(ids.length ? ids : null);
  const toggle = (id) => (has(id) ? write(held.filter((row) => row !== id))
    : write(held ? [...held, id] : [id]));
  const takeDomain = (rows) => {
    const ids = rows.map((row) => row.id);
    const rest = (held || []).filter((id) => ids.indexOf(id) === -1);
    write(ids.every(has) ? rest : [...rest, ...ids]);
  };

  const needle = query.trim().toLocaleLowerCase();
  const matching = pursuits.filter((row) => !needle
    || row.name.toLocaleLowerCase().includes(needle));
  const groups = (window.MD_DOMAINS || [])
    .map((domain) => ({ domain, rows: matching.filter((row) => row.domainId === domain.id) }))
    .filter((group) => group.rows.length);
  /* "No pursuit" is a real place, and it is offered whenever anything is
     actually there — or whenever the lens is already pointed at it. */
  const loose = counts.empty || 0;
  const showEmpty = (!needle || 'no pursuit'.includes(needle)) && (loose > 0 || has('empty'));
  const total = Object.keys(counts).reduce((sum, key) => sum + counts[key], 0);
  /* Nine pursuits scroll; three do not need a finder standing over them. */
  const finding = pursuits.length > 7;

  const frame = { position: 'fixed', top: y, left: x, right: 'auto', insetInlineEnd: 'auto',
    /* Wider and taller than a plain menu, because a pursuit's name and its
       count have to share a line and ten domains have to fit — but clamped
       against the viewport the way `.md-db-menu` clamps its own, so a short
       window never gets a menu taller than the screen holding it. */
    width: 252, maxHeight: 'min(380px, 60vh)', overflowY: 'auto', zIndex: 60 };

  const row = (key, label, count, state, color, press, child) => <button key={key}
    type="button" data-md-lens-row={key} role="menuitemcheckbox"
    aria-checked={state === 'none' ? 'false' : 'true'} className="md-db-menu-row"
    onClick={press} style={{ minHeight: 32, paddingInlineStart: child ? 26 : 8 }}>
    <WsLensBox state={state} color={color} light={light} />
    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' }}>{label}</span>
    <span data-md-count style={{ color: inkP(0.33), fontSize: 10.5,
      fontVariantNumeric: 'tabular-nums' }}>{count}</span>
  </button>;

  return <DbMenu title="Pursuits" onClose={onClose} style={frame}>
    {finding && <input aria-label="Find a pursuit" placeholder="Find a pursuit…" autoFocus
      value={query} onChange={(event) => setQuery(event.target.value)}
      style={{ width: 'calc(100% - 10px)', height: 32, margin: '2px 5px 6px', border: 'none',
        borderRadius: 'var(--md-radius-control, 8px)', outline: 'none', padding: '0 9px',
        color: inkP(0.9), background: inkP(light ? 0.05 : 0.07), font: 'inherit', fontSize: 12.5 }} />}
    <div role="menu" aria-label="Pursuits">
      {row('all', WS_LENS_ALL, total, held ? 'none' : 'all', accent, () => write([]), false)}
      {groups.map((group) => {
        const color = domColorP(group.domain.id, light);
        const state = group.rows.every((one) => has(one.id)) ? 'all'
          : group.rows.some((one) => has(one.id)) ? 'some' : 'none';
        return <div key={group.domain.id} role="group" aria-label={group.domain.label}>
          <button type="button" data-md-lens-domain={group.domain.id} data-md-lens-state={state}
            role="menuitemcheckbox" aria-label={`All of ${group.domain.label}`}
            aria-checked={state === 'all' ? 'true' : state === 'some' ? 'mixed' : 'false'}
            className="md-db-menu-row" onClick={() => takeDomain(group.rows)}
            style={{ minHeight: 30, marginTop: 4 }}>
            <WsLensBox state={state} color={color} light={light} />
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0,
              color: inkP(0.72), fontSize: 11.5, fontWeight: 680 }}>
              {domIconP(group.domain.id, rgbaP(color, 0.95), 13)}
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap' }}>{group.domain.label}</span>
            </span>
          </button>
          {group.rows.map((one) => row(one.id, one.name, counts[one.id] || 0,
            has(one.id) ? 'all' : 'none', color, () => toggle(one.id), true))}
        </div>;
      })}
      {showEmpty && <>
        <div className="md-db-menu-rule" />
        {row('empty', 'No pursuit', loose, has('empty') ? 'all' : 'none', accent,
          () => toggle('empty'), false)}
      </>}
      {!groups.length && !showEmpty && <div style={{ padding: '8px 10px 10px', color: inkP(0.4),
        fontSize: 12.5 }}>{needle ? 'No pursuit by that name.' : 'No pursuits yet.'}</div>}
    </div>
  </DbMenu>;
}

/* One labelled row of the Display popover: name in the margin, control in the
   column, an optional third thing (a direction arrow) hugging the end. The
   geometry is .md-db-config-row's, re-painted inline because that class's
   colors are scoped to the database's dark surface (the PP-3 trap). */
function WsControlRow({ label, children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '86px minmax(0,1fr) auto',
    alignItems: 'center', gap: 6, minHeight: 37, padding: '0 6px' }}>
    <span style={{ color: inkP(0.45), fontSize: 11, minWidth: 0, overflow: 'hidden',
      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    {children}
  </div>;
}

/* The pressable direction beside a Grouping or Ordering control — the same
   ↑/↓ grammar the column headers speak, wearing aria-pressed for descending
   so the state is hearable as well as visible. */
function WsDirArrow({ label, direction, onFlip }) {
  const light = isLightP();
  return <button type="button" aria-label={label} title={label}
    aria-pressed={direction === 'desc' ? 'true' : 'false'} onClick={onFlip}
    style={{ width: 28, height: 30, flex: 'none', border: 'none', borderRadius: 7,
      cursor: 'pointer', font: 'inherit', fontSize: 12.5,
      background: inkP(light ? 0.045 : 0.06), color: inkP(0.72) }}>
    <span aria-hidden="true">{direction === 'desc' ? '↓' : '↑'}</span>
  </button>;
}

/* A named switch — the whole row is the button, the track is only the state
   made visible. Painted inline rather than borrowing .md-db-toggle, whose
   off-track is white-on-white outside the database's dark surface. */
function WsSwitch({ label, on, onFlip }) {
  const light = isLightP();
  return <button type="button" aria-label={label} aria-pressed={on ? 'true' : 'false'}
    onClick={onFlip}
    style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', minHeight: 34,
      border: 'none', borderRadius: 9, padding: '0 8px', cursor: 'pointer',
      background: 'transparent', color: inkP(0.8), font: 'inherit', fontSize: 12.5 }}>
    <span style={{ flex: 1, textAlign: 'start' }}>{label}</span>
    <span aria-hidden="true" style={{ width: 30, height: 18, borderRadius: 10, padding: 2,
      boxSizing: 'border-box', flex: 'none',
      background: on ? 'var(--db-accent, #7C68E6)' : inkP(light ? 0.14 : 0.15) }}>
      <span style={{ display: 'block', width: 14, height: 14, borderRadius: '50%',
        background: on ? '#fff' : inkP(light ? 0.45 : 0.65),
        transform: on ? 'translateX(12px)' : 'none', transition: 'transform .16s' }} />
    </span>
  </button>;
}

/* A segmented control — the app's first. One strip, one choice always on, the
   active segment wearing the saved-view tab's grammar (accent wash over an
   inset accent underline) so switching a view's TYPE reads as kin to switching
   which view. `hook` stamps a data attribute for the tests to hold on to. */
function WsSegment({ label, value, options, onChange, accent, hook = null }) {
  const light = isLightP();
  return <div role="radiogroup" aria-label={label} {...(hook ? { [hook]: 'true' } : {})}
    style={{ display: 'grid', gridTemplateColumns: `repeat(${options.length}, 1fr)`, gap: 2,
      flex: 'none', margin: '2px 6px 6px', padding: 2, borderRadius: 'var(--md-radius-control, 8px)',
      background: inkP(light ? 0.045 : 0.055) }}>
    {options.map((option) => {
      const on = option.id === value;
      return <button key={option.id} type="button" role="radio" aria-checked={on ? 'true' : 'false'}
        onClick={() => onChange(option.id)}
        style={{ height: 27, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 5, border: 'none', borderRadius: 7, cursor: 'pointer', font: 'inherit', fontSize: 12,
          fontWeight: on ? 650 : 500, color: inkP(on ? 0.9 : 0.46),
          background: on ? rgbaP(accent, light ? 0.12 : 0.16) : 'transparent',
          boxShadow: on ? `inset 0 -1.5px ${rgbaP(accent, 0.9)}` : 'none' }}>
        {option.glyph && DbGlyph ? <DbGlyph name={option.glyph} size={13} /> : null}
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' }}>{option.label}</span>
      </button>;
    })}
  </div>;
}

/* ── the refusal, in words ────────────────────────────────────────────────
   md-project.ts's `Try` doors name which rule stopped a change; these are what
   those names sound like to the person who just dragged something. Anything not
   listed here is a refusal the database already has words for — a full device,
   most of all — so it borrows them rather than inventing a second dialect. */
const WS_NOTICE = 'workshop';
const WS_REFUSAL = {
  agent: 'Where a project stands is yours to say, not the Organizer’s.',
  /* `builtin` is kept because an older build may still be the one answering
     through a synced document; `admin` is what this build says. */
  builtin: 'This one ships with ManDana. Where it lives is not a preference.',
  admin: 'This one ships with ManDana. Only ManDana Admin can rename or remove it.',
  has_pursuit: 'This project follows its pursuit’s part of life. Move the pursuit, or set the project loose first.',
  missing: 'That project is no longer here.',
  /* WF-13, and unreachable since WK-1: every workflow is read with all six
     categories occupied, so a spine column always means something to every
     card under it. The sentence stays because the refusal is still the honest
     answer if that ever stops being true — a card refused in silence is the
     one outcome this door exists to prevent. */
  no_landing: 'That project’s workflow has nothing standing in that category.',
};

/** True if it landed. A refusal says why on the way past. */
function wsCommit(result) {
  if (result && result.ok) return true;
  const reason = result && result.reason;
  if (dbNotifyP) {
    dbNotifyP(WS_NOTICE, WS_REFUSAL[reason]
      || (dbMessageP ? dbMessageP(reason) : 'This change could not be saved.'));
  }
  return false;
}

/** The same promise for the Trash door, which answers a moment later.

    wsCommit cannot serve it: a `Try` door answers synchronously with a reason
    from WS_REFUSAL, and mdTrashDelete answers a macrotask later with a reason
    of its own vocabulary that md-trash already knows the words for. What must
    be identical is the part a person meets — a delete that is refused SAYS SO.
    IQAMA is where the gap showed: the plan refuses `builtin`, and without this
    the dialog simply closed over a pursuit that had not moved. */
async function wsTrashed(promise) {
  const result = await promise;
  if (result && result.ok) return true;
  if (dbNotifyP) {
    dbNotifyP(WS_NOTICE, window.mdTrashSayRefusal
      ? window.mdTrashSayRefusal(result && result.reason)
      : 'It could not be moved to Trash.');
  }
  return false;
}

/* ── the hand ─────────────────────────────────────────────────────────────
   One grammar for taking hold of a row, shared by the list, the board and the
   timeline rail. Pointer events rather than HTML5 drag — md-timeline.jsx's
   header says why (touch has no dragstart) — with a THRESHOLD so a click
   stays a click, window listeners so the gesture survives leaving the row it
   began on, and ONE write per gesture, on release over a target. A release
   over nothing releases nothing: the pointer abandoned the question, and the
   surface does not invent an answer.

   Mid-drag Escape is a real layer on the shared stack, not a keydown handler:
   the window's own layer is directly beneath, and without this one the key
   that meant "put it back" would close the Workshop over the moving row.

   Anything draggable wears `data-md-drag-id`; where the pointer is is asked
   with elementFromPoint (a captured touch pointer keeps its origin as
   `target`, so the target cannot be trusted), falling back to the event's
   target where the DOM cannot answer the question. */
/** Two answers the surface would draw identically. */
const wsSameHold = (a, b) => a.id === b.id && a.over === b.over && a.zone === b.zone
  && a.edge === b.edge;

/* How long a finger has to hold still before the surface reads the press as a
   lift, and how far it may drift while doing it. 220ms is long enough that a
   swipe never trips it and short enough that a deliberate press does not feel
   ignored; 10px is a resting thumb, not a moving one. */
const WS_HOLD_MS = 220;
const WS_HOLD_SLOP = 10;

/* How long a card takes to reach the seat the space pushed it into, how long
   the last inch of a release takes, and the curve both travel on. Both control
   points stay inside the journey: a card that passes its place and comes back
   is the board being pleased with itself, and a board has nothing to be pleased
   about. Taste rather than law — the app-wide rule now forbids only shame — but
   the taste is the house's. The settle is the shorter of the two, because the
   card is arriving somewhere the eye has been looking at for a while. */
const WS_SLIDE_MS = 160;
const WS_SETTLE_MS = 140;
const WS_SLIDE_EASE = 'cubic-bezier(.22,.61,.36,1)';

/* Asked at the moment of the gesture rather than at mount, the way the
   timeline's rail asks it: somebody who turns reduced motion on mid-session
   means it now. */
const wsStill = () => !!(window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

/** The nearest scroller above `el` that can move on `axis` ('x' or 'y').
    NEAREST is not enough on its own, which is the whole reason this exists: the
    board scrolls sideways and each of its columns scrolls down, so the closest
    scroller to a card is a column, and asking a column to scroll sideways is
    asking it for something it does not have. Whichever axis is being asked for,
    the search keeps climbing until it finds a scroller that actually declared
    that axis open.

    Declaring the axis is not enough either, and a browser is the only place that
    shows it: a column asks for `overflow-y: auto` and says nothing about the
    other axis, and CSS answers by computing its `overflow-x` to `auto` as well,
    because `visible` cannot survive next to a neighbour that clips. So every
    column claims both axes and has room on one. Room is what decides. */
function wsScroller(el, axis) {
  let node = el;
  while (node && node.closest) {
    const found = node.closest('.md-scroll,[data-md-scroller]');
    if (!found) return null;
    if (wsOpen(found, axis) && wsRoom(found, axis)) return found;
    node = found.parentElement;
  }
  return null;
}

/** Whether `node` has said this axis may scroll. The long-hand first and the
    shorthand behind it: `overflow: auto` opens both axes, and whether it has
    been expanded into the two long-hand properties by the time it is read is the
    reader's business — browsers expand it, the test runner does not. */
function wsOpen(node, axis) {
  const style = window.getComputedStyle ? window.getComputedStyle(node) : null;
  if (!style) return false;
  const way = (axis === 'x' ? style.overflowX : style.overflowY) || style.overflow;
  return way === 'auto' || way === 'scroll';
}

/** Whether `node` has anywhere to go on this axis.

    A node that reports neither a scroll size nor a client size has not been laid
    out — no browser answers that way and the test runner answers that way about
    everything — and there the declaration is allowed to stand alone. It is the
    same bargain the rest of this surface makes with an unmeasured DOM. */
function wsRoom(node, axis) {
  const inner = (axis === 'x' ? node.scrollWidth : node.scrollHeight) || 0;
  const outer = (axis === 'x' ? node.clientWidth : node.clientHeight) || 0;
  return inner || outer ? inner > outer : true;
}

function useWsHand({ canDrag, onDrop, touchHold = false, travel = false }) {
  const [drag, setDrag] = useStP(null);          // { id, over, zone, edge } once past the threshold
  const gesture = useRefP(null);
  const swallow = useRefP(false);

  const clear = () => { gesture.current = null; setDrag(null); };
  useDbEscapeLayer(clear, !!drag);

  /* ── what is in the hand ──────────────────────────────────────────────
     A copy of the row itself, hung inside the window and MOVED rather than
     re-imagined: React owns the box (so it leaves when the window does), the
     copy inside it is filled once, and every pointer move after that writes a
     transform straight onto the node. Drawing a row again sixty times a
     second is the one thing a drag cannot afford.

     A copy, because one implementation has to hold a list row, a board card
     and a rail row, and each of those is already exactly what it should look
     like in the air.

     Absolute, not fixed: this window's backdrop-filter is the containing
     block for anything fixed inside it, so a fixed ghost would measure from
     the wrong origin — the trap the rail's chip pickers already paid for. */
  const ghost = useRefP(null);
  const place = (x, y) => {
    const g = ghost.current, held = gesture.current;
    if (!g || !held || !held.base) return;
    g.style.transform = `translate3d(${x - held.base.left - held.grabX}px, `
      + `${y - held.base.top - held.grabY}px, 0)`;
  };
  useLayEfP(() => {
    const g = ghost.current, held = gesture.current;
    if (!g || !held || !held.source || g.firstChild) return;
    const copy = held.source.cloneNode(true);
    /* The copy answers no question the app asks. Every `data-md-` hook comes
       off it, so a query for rows or cards or drag ids still finds exactly the
       real ones; and nothing inside it can be tabbed to, because a focusable
       thing inside a hidden subtree is a trap for whoever is not looking at
       the screen. */
    const strip = (node) => {
      for (const name of node.getAttributeNames ? node.getAttributeNames() : []) {
        if (name.startsWith('data-md-')) node.removeAttribute(name);
      }
      if (/^(BUTTON|A|INPUT|SELECT|TEXTAREA)$/.test(node.tagName || '')) node.setAttribute('tabindex', '-1');
      for (const child of node.children || []) strip(child);
    };
    strip(copy);
    /* The board takes the carried card out of its column's flow by hiding it,
       and cloneNode copies the style attribute that hides it. A copy of a hidden
       thing is a hidden thing, so the copy is told to show itself — it is the
       one in the hand, and the only one anybody can see.

       Told the shape it was in, not merely told to show: CLEARING the display
       hands a <button> back to the user agent, which says inline-block, which
       shrink-wraps to its own text. A card with a short name became a narrow
       black box in the left end of a card-wide hand, and the rest of the hand
       was the hand's own dark material — grey, card-shaped, and as wide as the
       name was short. A long name filled the width and hid it, which is why it
       only happened sometimes. */
    copy.style.display = held.display || '';
    if (held.width) {
      g.style.width = `${held.width}px`;
      /* And the copy fills it. A row is stretched to its column's width by
         being a flex item in it, and the hand is not a column — but a <button>
         asked to be `width:auto` answers with the width of its own text no
         matter what its display says, which is the rule for a form control and
         not one `display:flex` overrides. So the width is said twice: once on
         the hand, which was measured, and once here, to fill it. Both are
         border-box, so the second is exactly the first. */
      copy.style.width = '100%';
    }
    if (held.radius) g.style.borderRadius = held.radius;
    g.append(copy);
    place(held.lastX, held.lastY);
  }, [drag ? drag.id : null]);

  const start = (event, id) => {
    if (!canDrag || gesture.current) return;
    if (event.button != null && event.button !== 0) return;
    const x = event.clientX || 0, y = event.clientY || 0;
    const source = event.currentTarget || null;
    /* A finger and a cursor mean different things by the same press, and only a
       surface that opted in is asked to tell them apart. The board did; the list
       and the rail keep exactly the grammar they shipped with. */
    const byFinger = touchHold && event.pointerType === 'touch';
    /* Measured at the press, so the row hangs where it was taken hold of
       rather than snapping its corner to the cursor. */
    const box = source && source.getBoundingClientRect ? source.getBoundingClientRect() : null;
    const held = { id, x, y, live: false, source, lastX: x, lastY: y,
      /* The window this card is in, and the corner every point below is
         measured from — the hand hangs inside the window, so a corner taken
         from the viewport draws it a gutter away from the finger and lands the
         settle a gutter away from the card. `wsSpaceBase` knows both windows. */
      base: wsSpaceBase(source),
      grabX: box ? x - box.left : 0, grabY: box ? y - box.top : 0,
      width: box ? box.width : 0,
      /* The layout the row was standing in, read while it is still standing in
         it. A card is a flex column and a list row is a grid of columns, and
         both say so inline; the copy in the hand has to be handed the same
         sentence, because by the time it is made the board may already have
         hidden the original. */
      display: source && source.style ? source.style.display : '',
      /* Kept because the board opens a space of exactly this size where the
         card will land, and by the time it needs the number the card is out of
         the flow and can no longer be asked. */
      height: box ? box.height : 0,
      radius: source && window.getComputedStyle ? window.getComputedStyle(source).borderRadius : '' };
    gesture.current = held;

    /* A hit is a row (`data-md-drag-id`) or the zone standing behind one
       (`data-md-drop-zone` — a board column, holding cards or empty). The
       row's own zone rides along so a drop can mean both "here" and "in
       here".

       And WHICH HALF of the row the pointer is in, because that is the whole
       difference between a line that tells the truth and one that does not: a
       release in the lower half means below, and the older rule — take the
       target's index, whichever way you came — could only ever be right about
       one direction. A row that reports no height cannot say which half you
       are in, and there the older rule stands. */
    /* When the pointer is on a zone rather than on one of its rows, the zone's
       own rows are asked where that is. The board needs this twice over: the
       space it opens takes no pointer, so the finger is on the column for most
       of the carry, and the run of empty column under the last card is a real
       place to aim at. Without it both answers collapse to "the foot of the
       column" and the space lurches away from the hand.

       Rows that report no height are skipped rather than measured: the card in
       the hand is out of the flow and would otherwise measure as a zero-height
       row at the very top, and win. */
    const nearestIn = (zoneEl, y) => {
      let best = null;
      for (const node of zoneEl.querySelectorAll('[data-md-drag-id]')) {
        const box = node.getBoundingClientRect ? node.getBoundingClientRect() : null;
        if (!box || !box.height) continue;
        const away = y < box.top ? box.top - y : (y > box.bottom ? y - box.bottom : 0);
        if (!best || away < best.away) best = { id: node.getAttribute('data-md-drag-id'), box, away };
      }
      return best;
    };
    /* And the same rule sideways, one level out: when the point is on the board
       rather than on any of its columns, the board is asked which column is
       nearest it.

       This is not a corner case on a phone, it is the normal case. The board
       keeps 12px of its own padding at each end, the edge-pull is at its fastest
       in exactly that strip, and a finger holding a card against the edge of a
       phone is IN it — so the fastest part of the pull was also the part that
       answered "back where you started", and the space went home while the
       board raced.

       Sideways is asked first because sideways is what separates the places on a
       board: the columns stand next to each other and the list of the ones that
       are empty stands at the end of them. But the rows in that list are stacked
       DOWNWARD and share one column of screen, so sideways alone has nothing to
       choose between them and would hand back the first row every time, wherever
       in the list the finger was. Downward breaks that tie, and only that tie —
       a row of columns has no ties to break, so it gets exactly the answer it
       got before. */
    const nearestZone = (hostEl, x, y) => {
      let best = null;
      for (const node of hostEl.querySelectorAll('[data-md-drop-zone]')) {
        const box = node.getBoundingClientRect ? node.getBoundingClientRect() : null;
        if (!box || !box.width) continue;
        const away = x < box.left ? box.left - x : (x > box.right ? x - box.right : 0);
        const down = y < box.top ? box.top - y : (y > box.bottom ? y - box.bottom : 0);
        if (!best || away < best.away || (away === best.away && down < best.down)) {
          best = { node, away, down };
        }
      }
      return best ? best.node : null;
    };
    const seek = (el, x, y) => {
      if (!el || !el.closest) return null;
      let hit = el.closest('[data-md-drag-id],[data-md-drop-zone]');
      if (!hit) {
        const host = el.closest('.md-scroll,[data-md-scroller]');
        hit = host ? nearestZone(host, x, y) : null;
      }
      if (!hit) return null;
      const zoneEl = hit.closest('[data-md-drop-zone]');
      let id = hit.getAttribute('data-md-drag-id') || null;
      let box = id && hit.getBoundingClientRect ? hit.getBoundingClientRect() : null;
      if (!id && zoneEl) {
        const near = nearestIn(zoneEl, y);
        if (near) { id = near.id; box = near.box; }
      }
      return { id,
        zone: zoneEl ? zoneEl.getAttribute('data-md-drop-zone') : null,
        edge: box && box.height > 0 ? (y > box.top + box.height / 2 ? 'after' : 'before') : null };
    };
    const overOf = (ev) => {
      const y = ev.clientY || 0, x = ev.clientX || 0;
      return seek(document.elementFromPoint ? document.elementFromPoint(x, y) : null, x, y)
        || seek(ev.target, x, y) || { id: null, zone: null, edge: null };
    };
    /* The same question asked of a bare point. A finger's lift happens on a
       timer, and a timer has no event to fall back on — the thing pressed is
       the fallback, which is also the honest answer: the card is still exactly
       where the finger left it. */
    const overAt = (px, py) => seek(document.elementFromPoint
      ? document.elementFromPoint(px, py) : null, px, py)
      || seek(held.source, px, py) || { id: null, zone: null, edge: null };
    /* Over the row it is carrying is not "over nothing" — it is "still here",
       and the two answers look nothing alike. Read as nothing, the space the
       board opens goes to the foot of the column, which is a lurch away from
       the hand at the exact moment the press is being answered; and it happens
       on every gesture, because the first live move of one is by definition
       still within a few pixels of where it began.

       Where the row is, in a flow it has left, is where the row after it now
       begins — so the answer is that row, on its near side. A row with nothing
       after it is already at the foot, and the foot is the honest answer. */
    const nextSeat = () => {
      let sib = held.source ? held.source.nextElementSibling : null;
      while (sib && !(sib.getAttribute && sib.getAttribute('data-md-drag-id'))) {
        sib = sib.nextElementSibling;
      }
      return sib ? sib.getAttribute('data-md-drag-id') : null;
    };
    const aim = (over) => {
      if (over.id && over.id !== held.id) return { on: over.id, edge: over.edge };
      if (over.id === held.id) { const on = nextSeat(); return { on, edge: on ? 'before' : null }; }
      return { on: null, edge: null };
    };
    /* A list longer than the window has to come to the hand. Near an edge of
       whatever is scrolling under the pointer, the surface moves itself — at
       the speed you lean into it, so a hand that has just arrived at the edge
       creeps and a hand pressed against it travels.

       The scroller is asked for on every move rather than kept from the press:
       the board has one under every column, and a card carried across two of
       them changes which one is being leaned on. */
    /* What is under the hand, asked from the hand's last known point rather
       than from an event — so the lift can ask before anything has moved, and
       the edge-pull can ask again after the board has moved underneath it. */
    const resolve = () => {
      if (gesture.current !== held || !held.live) return;
      const over = overAt(held.lastX, held.lastY);
      const at = aim(over);
      const next = { id: held.id, over: at.on, zone: over.zone, edge: at.edge,
        height: held.height };
      setDrag((prev) => (prev && wsSameHold(prev, next) ? prev : next));
    };
    const BAND = 48, LEAN = 14;
    let leaning = null;                                    // [{ node, dx, dy }]
    let frame = 0;
    const step = () => {
      frame = 0;
      if (!leaning || gesture.current !== held) return;
      for (const pull of leaning) {
        pull.node.scrollTop += pull.dy;
        pull.node.scrollLeft += pull.dx;
      }
      /* The board moved and the hand did not, so the answer under the hand is a
         different answer — asked again from the same point rather than waiting
         for a pointer that is being held perfectly still against the edge. A
         space that only moved when the finger did was a space that could not
         follow the board it was standing in. */
      resolve();
      frame = window.requestAnimationFrame(step);
    };
    const rate = (near, far) => {
      if (near < BAND) return -Math.ceil(((BAND - Math.max(near, 0)) / BAND) * LEAN);
      if (far < BAND) return Math.ceil(((BAND - Math.max(far, 0)) / BAND) * LEAN);
      return 0;
    };
    const lean = (ev) => {
      const y = ev.clientY || 0, x = ev.clientX || 0;
      const at = document.elementFromPoint ? document.elementFromPoint(x, y) : null;
      const el = at || ev.target || held.source;
      /* Each axis asks separately, because one node cannot answer for both. The
         board scrolls sideways and every column inside it scrolls down, so the
         NEAREST scroller under the pointer is the column — and a column has no
         sideways to give. Leaning a horizontal pull on it wrote scrollLeft to a
         node that could not move, which is the whole of the bug: on a phone a
         column is nearly the width of the screen, so the pointer is inside one
         for the entire carry and the board never went anywhere. */
      const pulls = [];
      const down = wsScroller(el, 'y');
      const downBox = down && down.getBoundingClientRect ? down.getBoundingClientRect() : null;
      if (downBox && downBox.height) {
        const dy = rate(y - downBox.top, downBox.bottom - y);
        if (dy) pulls.push({ node: down, dx: 0, dy });
      }
      const across = wsScroller(el, 'x');
      const acrossBox = across && across.getBoundingClientRect ? across.getBoundingClientRect() : null;
      if (acrossBox && acrossBox.width) {
        const dx = rate(x - acrossBox.left, acrossBox.right - x);
        if (dx) pulls.push({ node: across, dx, dy: 0 });
      }
      leaning = pulls.length ? pulls : null;
      if (leaning && !frame) frame = window.requestAnimationFrame(step);
    };
    const rest = () => {
      if (frame && window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
      frame = 0;
      leaning = null;
    };
    /* ── the finger ──────────────────────────────────────────────────────
       iOS decides on the FIRST touchmove of a gesture whether the movement
       belongs to a scroller, and a listener registered after that decision
       cannot take it back. So the block goes on at the press and says nothing
       at all until the lift has happened — which is why the lift has to be
       decided by stillness rather than by movement. `touch-action: none` would
       be the one-line version of this and would cost the board the ability to
       be scrolled by touching a card, which is most of the board. */
    const block = (ev) => { if (held.live) ev.preventDefault(); };
    let hold = 0;
    const lift = () => {
      hold = 0;
      if (gesture.current !== held) return;
      held.live = true;
      /* Drawn on the hold, before anything has moved: the press has been
         answered, and a hand that had to move to find that out would be back
         to fighting the scroller. The finger is on the card it just took, so
         the space opens in that card's own place and the board stays still. */
      resolve();
    };
    const unhook = () => {
      rest();                                              // the edge stops pulling
      if (hold) window.clearTimeout(hold);
      hold = 0;
      document.removeEventListener('touchmove', block);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
    const move = (ev) => {
      if (gesture.current !== held) return unhook();      // Escape got here first
      held.lastX = ev.clientX || 0;
      held.lastY = ev.clientY || 0;
      if (!held.live) {
        const dx = held.lastX - held.x, dy = held.lastY - held.y;
        if (byFinger) {
          /* A finger that moved before the hold finished was swiping, and the
             board it was swiping is entitled to the movement: the gesture ends
             here, having never taken anything. */
          if (dx * dx + dy * dy > WS_HOLD_SLOP * WS_HOLD_SLOP) { unhook(); clear(); }
          return;
        }
        if (dx * dx + dy * dy < 16) return;                // a hand at rest is a click
        held.live = true;
      }
      const over = overOf(ev);
      const at = aim(over);
      const next = { id: held.id, over: at.on, zone: over.zone, edge: at.edge,
        height: held.height };
      /* The same answer is the same object, and React draws nothing. A pointer
         crossing one row asks this sixty times a second and the window has no
         business redrawing itself for any of them — the only thing that has
         actually moved is the thing in the hand, and that moves itself. */
      setDrag((prev) => (prev && wsSameHold(prev, next) ? prev : next));
      place(held.lastX, held.lastY);                       // the hand is 1:1 with the pointer
      lean(ev);
    };
    /* ── the settle ──────────────────────────────────────────────────────
       The board goes final first — the write has happened, the space has
       closed, the card is back in its flow — and then a copy of what was in
       the hand travels the last inch into the place it landed, and stops being
       anything.

       A COPY, handed to the DOM. The ghost belongs to the gesture and the
       gesture ended the instant the pointer lifted; a settle that kept it
       mounted would have the drop's own state machine waiting on an animation,
       and the write is not tradeable for 140ms of travel. So this owns one
       node, outside React, for exactly as long as it takes to arrive.

       Measured a frame late, because the place the card landed is only knowable
       once the board has drawn itself again — and a board with a sort speaking
       may land it somewhere the space never was. */
    const settle = (from) => {
      const host = from && from.parentNode;
      if (!host || wsStill()) return;
      const copy = from.cloneNode(true);
      /* It is no longer the hand, and nothing should be able to mistake it for
         one: the hand is what a pointer is holding, and this is a card that has
         already been put down. */
      copy.removeAttribute('data-md-ghost');
      copy.setAttribute('data-md-settling', '');
      host.append(copy);
      /* THE COPY'S REMOVAL OWES THE COMPOSITOR NOTHING. The travel needs a
         frame — where the card landed is only knowable once the board has drawn
         itself again — but the removal used to be scheduled inside that same
         frame, and a frame is not promised. requestAnimationFrame is suspended
         in a hidden tab, so letting go of a card and switching away in the same
         beat left this copy on the board: a second card under the real one, for
         as long as the tab stayed away. A permanent echo, from the animation
         that exists to prevent one.

         So the removal is armed here, on a timer, before anything is asked of a
         frame; the frame only ever animates. `settled` keeps the two paths from
         reaching for the same node twice. */
      let settled = false;
      const drop = () => {
        if (settled) return;
        settled = true;
        if (copy.parentNode) copy.parentNode.removeChild(copy);
      };
      window.setTimeout(drop, WS_SETTLE_MS + 40);
      window.requestAnimationFrame(() => {
        const node = document.querySelector(`[data-md-drag-id="${held.id}"]`);
        const box = node && node.getBoundingClientRect ? node.getBoundingClientRect() : null;
        if (!box || !box.height || !copy.parentNode) return drop();
        copy.style.transition = `transform ${WS_SETTLE_MS}ms ${WS_SLIDE_EASE}`;
        copy.style.transform = `translate3d(${box.left - held.base.left}px, `
          + `${box.top - held.base.top}px, 0)`;
      });
    };
    const up = (ev) => {
      unhook();
      if (gesture.current !== held) return;
      const hand = ghost.current;                          // before React takes it back
      clear();
      if (!held.live) return;
      swallow.current = true;                              // the click that follows meant this
      const over = overOf(ev);
      if (over.id === held.id) over.id = null;
      if (!(over.id || over.zone)) return;                 // released over nothing, answered nothing
      /* A door that says no drops the whole gesture, the travel included. */
      if (onDrop(held.id, over) === false) return;
      if (travel) settle(hand);
    };
    const cancel = () => { unhook(); if (gesture.current === held) clear(); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    if (byFinger) {
      document.addEventListener('touchmove', block, { passive: false });
      hold = window.setTimeout(lift, WS_HOLD_MS);
    }
  };

  /* Asked by the row's onClick: a click that ends a drag is the drag's, and a
     browser fires it anyway. */
  const swallowClick = () => { const held = swallow.current; swallow.current = false; return held; };

  const light = isLightP();
  const carried = drag
    ? <div data-md-ghost aria-hidden="true" ref={ghost}
      style={{ position: 'absolute', left: 0, top: 0, zIndex: 60, pointerEvents: 'none',
        boxSizing: 'border-box', cursor: 'grabbing',
        /* Opaque, and the one place in this window that is. A list row carries
           no material of its own, and a translucent copy of one floating over
           the rows it is crossing reads as two rows at once — which is the
           opposite of holding something. The lift is said by the shadow. */
        background: light ? 'rgba(255,255,255,0.97)' : 'rgba(30,33,40,0.98)',
        boxShadow: light
          ? '0 2px 6px rgba(24,30,42,0.10), 0 18px 44px rgba(24,30,42,0.18)'
          : '0 2px 6px rgba(0,0,0,0.36), 0 18px 44px rgba(0,0,0,0.48)' }} />
    : null;

  return { drag, start, swallowClick, carried };
}

/** Where the row lands: on the side of the target the hand said, and where the
    hand could not say (an unmeasurable row), on the target's own index — the
    older rule, which is right for a drag that came from below and one row off
    for a drag that came from above.

    Returns the whole visible sequence — the order is a hint resolved against
    it, so writing all of it keeps every unmoved row exactly where it reads
    today. Returns null when the sequence would not change: a gesture that
    changes nothing is not a change, and an order nobody altered is not a fact
    worth writing down. */
function wsReorder(flatIds, movedId, targetId, edge) {
  const to = flatIds.indexOf(targetId);
  if (to === -1 || flatIds.indexOf(movedId) === -1 || movedId === targetId) return null;
  const out = flatIds.filter((id) => id !== movedId);
  const beside = out.indexOf(targetId);
  out.splice(edge ? beside + (edge === 'after' ? 1 : 0) : to, 0, movedId);
  return out.every((id, at) => id === flatIds[at]) ? null : out;
}

/** Which edge of `id` the carried row would land on, or null if it is not the
    target. Where the hand could not measure the row it says `before` — the top
    line, which is what the surface has always drawn. */
const wsLanding = (drag, id) => (drag && drag.over === id ? (drag.edge || 'before') : null);
const wsLandingLine = (edge, accent) => `inset 0 ${edge === 'after' ? '-2px' : '2px'} 0 0 ${accent}`;

/** Where in a column's own cards the space opens — the index the carried card
    would take among the cards that are still in the flow, or -1 for a column
    the carry has nothing to do with.

    `rows` is the column's cards in the order it draws them, carried card
    included; the carried card is removed before counting, because it is out of
    the flow and an index that counted it would be one too far.

    A carry with no card under it — the pointer on the space itself, or on the
    run of column under the last card — lands at the foot. The hand resolves
    that case against the column's cards before it gets here, so this is only
    reached by a column with nothing to measure: an empty one, or a runner with
    no geometry to report. */
function wsGapAt(rows, drag, zoneId) {
  if (!drag || drag.zone !== zoneId) return -1;
  const flow = rows.filter((row) => row.id !== drag.id);
  if (!drag.over) return flow.length;
  const at = flow.findIndex((row) => row.id === drag.over);
  return at === -1 ? flow.length : at + (drag.edge === 'after' ? 1 : 0);
}

/** Whether a column PAINTS while the hand is over it.

    THE WASH IS THE ANSWER OF LAST RESORT. Two things were saying one sentence:
    under manual order the column opens a space at exactly the carried card's
    size, in exactly the seat it will take, and that space already answers both
    questions a wash could — which column, and where in it. Washing the whole
    column as well says the first half again in a vaguer voice, and the eye reads
    the pair as one thing echoing rather than one thing answering.

    So where the space can speak, it speaks alone. Where it cannot — a sort is
    on, so there is no seat to promise (`wsGapAt` returns -1), or the column is a
    folded rail with no flow to open — the wash is the only thing left that can
    say which column, and it stays.

    The over-state is a different fact and is not touched by this: a column the
    hand is over still says so (`data-md-drop-over`), whether or not it paints. */
const wsWashes = (over, spaceOpen) => !!over && !spaceOpen;

/** Where each card in a column sits, by id — the tops of whatever is actually
    in the flow. The card in the hand is out of it and reports no height, so it
    is skipped rather than recorded as a card at the very top; and a runner with
    no layout at all (happy-dom) records nothing, which is the same answer as
    "nothing has moved". */
function wsSeats(nodes) {
  const out = {};
  for (const node of nodes) {
    const box = node.getBoundingClientRect ? node.getBoundingClientRect() : null;
    if (!box || !box.height) continue;
    out[node.getAttribute('data-md-drag-id')] = box.top;
  }
  return out;
}

/** The distance each card has to be put back by, so that a layout change reads
    as travel instead of a jump — the invert half of a FLIP.

    `seated` is where the cards were the last time they were measured, `now` is
    where they belong after this render, and `flight` is whatever is left of a
    slide that is still playing (a card interrupted halfway is not at either
    seat, and inverting from the seat it left would jump it forward before it
    travelled back).

    A card the previous measurement never saw has no distance to be put back
    by — there is no "before" to come from — and half a pixel is not a move. */
function wsSlide(seated, now, flight = null) {
  const out = {};
  for (const id in now) {
    if (!(id in seated)) continue;
    const dy = seated[id] + ((flight && flight[id]) || 0) - now[id];
    if (Math.abs(dy) >= 0.5) out[id] = dy;
  }
  return out;
}

/* ── a column of the board ────────────────────────────────────────────────
   The database's board is the shape being followed (md-database.jsx:877): a
   drop target that lights up, a header carrying its own count, and a `+` at the
   foot that starts something already belonging where it was pressed.

   What is NOT followed is its painting. `.md-db-board` sizes itself inside a
   database surface and its light twins are scoped to that class, so a column
   borrowed whole would be a dark card on a white page — the PP-3 trap W-5 came
   through. This paints in the file's own inline objects. */
function WsColumn({ group, mark, accent, hand, onNew, onOpen, meta, droppable = true,
  placed = true, noun = 'project' }) {
  const light = isLightP();
  const [shut, setShut] = useStP(false);
  const [menu, setMenu] = useStP(false);
  const scroll = useRefP(null);
  const seats = useRefP(null);          // where this column's cards sat, mid-gesture only

  /* The wash is the hand's state now, not the column's: pointer hit-testing
     says which zone the carried card is over, and every column reads its own
     answer out of that one gesture. */
  const over = !!(droppable && hand.drag && hand.drag.zone === group.id);

  /* ── the space ─────────────────────────────────────────────────────────
     Where the carried card will land, standing open at exactly its own size.
     Derived from the hand and nothing else — no column holds any state about a
     gesture, so three columns cannot disagree about one carry.

     It takes no pointer. What is underneath it is the column, and the column
     answers for it (`nearestIn` in the hand): a space that intercepted the
     finger resting on it would be asked where to go and would answer "the foot
     of the column", which is the one place the finger is not.

     And it does not open at all while a sort is speaking. The space promises
     the place the card will take, and a sorted board has no place to promise:
     within a column the order is the sort's, so the release writes nothing, and
     across two the status changes but the sort decides where the card sits
     afterwards. Then, and only then, the column's wash is what says which
     column — see `wsWashes` above. */
  const gapAt = wsGapAt(group.rows, hand.drag, droppable && placed ? group.id : null);
  /* `shut` is passed the space's own state: a folded column returns before it
     ever draws one (see below), so a space it would have opened is a space
     nobody can see — and the wash is the only thing left that can say a
     46px rail is the one taking the card. */
  const washes = wsWashes(over, !shut && gapAt >= 0);

  /* No fill and no border: the well is gone, and the cards stand directly on
     the window. The radius stays because the drag-over wash still needs a
     shape to take. */
  const frame = {
    position: 'relative', display: 'flex', flexDirection: 'column', flex: 'none',
    width: shut ? 46 : 272, minHeight: 0, height: '100%',
    borderRadius: 18,
    background: washes ? rgbaP(accent, light ? 0.05 : 0.07) : 'transparent',
    transition: 'background .16s cubic-bezier(.22,.61,.36,1)',
  };

  const flow = hand.drag ? group.rows.filter((row) => row.id !== hand.drag.id) : group.rows;
  const gapBefore = gapAt >= 0 && gapAt < flow.length ? flow[gapAt].id : null;
  const gapLast = gapAt >= 0 && gapAt >= flow.length;
  const space = gapAt < 0 ? null
    : <div key="md-gap" data-md-gap aria-hidden="true"
      style={{ flex: 'none', height: hand.drag.height || 64, borderRadius: 17,
        pointerEvents: 'none', background: rgbaP(accent, light ? 0.07 : 0.1),
        border: `1px dashed ${rgbaP(accent, light ? 0.3 : 0.36)}` }} />;

  /* ── the cards move out of the way ─────────────────────────────────────
     The space opening pushes everything below it down a seat, and a seat taken
     without a word about it is a jump. So: measure, let React move them,
     measure again, put each one back where it was, and release it — 160ms of
     travel and then the column is simply the column.

     Measured only while something is in the hand. Every column measures, not
     just the one under the pointer, because the column the card LEFT closed up
     behind it and that is a move too.

     Transforms come off before the second measurement: a transformed node
     reports where it looks, not where it belongs, and a FLIP that measured the
     first would chase its own tail. What it looked like is read first and kept,
     so a card interrupted halfway travels on from where it actually is. */
  const held = hand.drag ? hand.drag.id : null;
  /* ── D2 · which columns can actually have moved ───────────────────────────
     Two, and only ever two: the column the card came FROM, which closes up
     behind it, and the column it is currently OVER, which opens a space for
     it. Every other column on the board holds exactly the cards it held last
     frame, in exactly the order it held them, so a FLIP there measures a
     stillness — several times a second, once per column, for the whole gesture.

     The file used to say "every column measures, not just the one under the
     pointer, because the column the card LEFT closed up behind it and that is
     a move too". That was right about the column it left and wrong to conclude
     "every". Both are named here instead. */
  const mine = !!(held && group.rows.some((row) => row.id === held));
  const involved = !!held && (mine || hand.drag.zone === group.id);
  useLayEfP(() => {
    const host = scroll.current;
    if (!host) return undefined;
    /* A column that has stopped being involved gets ONE more pass, because the
       space it was holding open still has to close. `seats` is what says it has
       unfinished business, and it is cleared at the end of that final pass so
       the column then falls silent for the rest of the gesture. */
    if (held && !involved && !seats.current) return undefined;
    const nodes = [...host.querySelectorAll('[data-md-drag-id]')];
    if (!held) {                        // the hand is empty: nothing is owed a seat
      seats.current = null;
      for (const node of nodes) { node.style.transition = ''; node.style.transform = ''; }
      return undefined;
    }
    const shown = wsSeats(nodes);
    for (const node of nodes) { node.style.transition = 'none'; node.style.transform = ''; }
    const now = wsSeats(nodes);
    const was = seats.current;
    seats.current = involved ? now : null;    // not involved: this was the last pass
    if (!was || wsStill()) return undefined;
    const flight = {};
    for (const id in now) if (id in shown) flight[id] = shown[id] - now[id];
    const moves = wsSlide(was, now, flight);
    const playing = nodes.filter((node) => moves[node.getAttribute('data-md-drag-id')]);
    if (!playing.length) return undefined;
    for (const node of playing) {
      node.style.transform = `translateY(${moves[node.getAttribute('data-md-drag-id')]}px)`;
    }
    host.getBoundingClientRect();       // the inverted board is now what is painted
    const frame = window.requestAnimationFrame(() => {
      for (const node of playing) {
        node.style.transition = `transform ${WS_SLIDE_MS}ms ${WS_SLIDE_EASE}`;
        node.style.transform = '';
      }
    });
    return () => { if (window.cancelAnimationFrame) window.cancelAnimationFrame(frame); };
  }, [held, gapAt]);

  if (shut) {
    return <section data-md-column={group.id} data-md-drop-zone={group.id}
      data-md-drop-over={over ? '' : undefined}
      data-md-drop-wash={washes ? '' : undefined} style={frame}>
      <button type="button" onClick={() => setShut(false)} aria-label={`Expand ${group.label}`}
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 10, padding: '12px 0', border: 'none', background: 'transparent', cursor: 'pointer',
          font: 'inherit' }}>
        <span data-md-column-count style={{ color: inkP(0.4), fontSize: 11.5,
          fontVariantNumeric: 'tabular-nums' }}>{group.rows.length}</span>
        <span style={{ writingMode: 'vertical-rl', color: inkP(0.55), fontSize: 12, fontWeight: 620,
          whiteSpace: 'nowrap' }}>{group.label}</span>
      </button>
    </section>;
  }

  const cardFor = (row) => { const land = wsLanding(hand.drag, row.id); return <button key={row.id}
        type="button" data-md-card
        data-md-drag-id={row.id}
        data-md-drop-target={land ? '' : undefined}
        data-md-drop-edge={hand.drag && hand.drag.over === row.id ? hand.drag.edge || undefined : undefined}
        onPointerDown={(event) => hand.start(event, row.id)}
        onClick={() => { if (hand.swallowClick()) return; onOpen(row.id); }}
        aria-label={`${row.name} — ${group.label}`}
        /* The day view's Glass, replicated inline at board scale rather than
           imported — Glass couples to the MD_GLASS tweak state and its light
           twins are scoped elsewhere (the PP-3 trap). Radius is a literal for
           the same reason the tests want one: a var() here is a radius only
           where the shell defines it. 20, the Favorites card's own number —
           every working card one tap from the day wears it. */
        style={{ position: 'relative', aspectRatio: '1.618 / 1',
          /* A column of name over meta — except while the card is in the air,
             when it goes out of the flow rather than being dimmed in a place it
             has already left. It stays mounted because the thing in the hand is
             a copy of this node, and because React should not have to rebuild a
             card that never went anywhere.

             Keep it the object's only `display`. It was written twice for a
             while — the plain `flex` here, this ternary further down — and a
             duplicate key is settled by position, in silence: the build said
             so on every run and the line scrolled past four commits in a row. */
          display: hand.drag && hand.drag.id === row.id ? 'none' : 'flex',
          flexDirection: 'column', alignItems: 'stretch',
          gap: 4, flex: 'none',
          borderRadius: 20, padding: '11px 12px',
          border: light ? '0.75px solid rgba(255,255,255,0.95)' : '0.75px solid rgba(255,255,255,0.18)',
          /* Opaque in both themes. A translucent film over this window's glass
             made the card slightly less window rather than a thing standing on
             it — which is exactly wrong for a surface whose whole gesture is
             picking cards up. The dark closed first (#000); daylight followed
             when Marwan asked for solid paper (2026-08-28) — white at 0.62 let
             the frost show through the card it was supposed to be a body on. */
          background: light ? '#FFF' : '#000',
          /* No landing line. The space open in the column is the whole answer,
             and a space the size of the card in your hand cannot be off by one
             — which is more than the two-pixel rule it replaces could say. The
             list and the rail still draw theirs. */
          boxShadow: light
            ? 'inset 0 1px 0.5px rgba(255,255,255,0.96), 0 1px 2px rgba(24,30,42,0.05), 0 8px 24px rgba(24,30,42,0.055)'
            : 'inset 0 1px 1px rgba(255,255,255,0.06), 0 12px 30px rgba(0,0,0,0.35)',
          textAlign: 'start', cursor: 'grab', font: 'inherit' }}>
        <span data-md-card-name dir="auto" style={{ color: inkP(1), fontSize: 14, fontWeight: 600, lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: inkP(0.6), fontSize: 12,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          fontVariantNumeric: 'tabular-nums' }}>{meta(row)}</span>
      </button>; };

  /* ── D1 · does this column need a window? ───────────────────────────────
     The hook is called every render whatever the answer — React forbids the
     conditional — and only its ANSWER is conditional. Under the floor the
     virtualizer is built and ignored, which costs one object.

     WHY A FLOOR AT ALL. Windowing is not free: it costs absolute positioning,
     a container that can no longer size itself to its content, and a
     measurement pass. A column of nine cards pays every bit of that to save
     nothing. tests/workshop-window.test.js asserts BOTH sides of this number —
     crossing it must change the DOM and staying under it must not. */
  const windowed = flow.length > WS_WINDOW_FLOOR;
  const gapHeight = (hand.drag && hand.drag.height) || WS_CARD_SEAT;
  const virt = useVirtualP({
    count: flow.length,
    getScrollElement: () => scroll.current,
    estimateSize: () => WS_CARD_SEAT,
    overscan: 8,
    getItemKey: (index) => (flow[index] ? flow[index].id : index),
  });

  return <section data-md-column={group.id} data-md-drop-zone={group.id} style={frame}
    data-md-drop-over={over ? '' : undefined}
    data-md-drop-wash={washes ? '' : undefined}>

    <header style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none', padding: '9px 8px 7px' }}>
      {mark}
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        color: inkP(0.82), fontSize: 12.5, fontWeight: 640 }} dir="auto">{group.label}</span>
      <span data-md-column-count style={{ color: inkP(0.32), fontSize: 11.5,
        fontVariantNumeric: 'tabular-nums' }}>{group.rows.length}</span>
      <span style={{ flex: 1 }} />
      <span className="md-db-menu-anchor" style={{ position: 'relative', display: 'flex', flex: 'none' }}>
        <button type="button" aria-label={`${group.label} column options`} onClick={() => setMenu(!menu)}
          style={{ width: 24, height: 24, display: 'grid', placeItems: 'center', border: 'none',
            borderRadius: 7, background: 'transparent', color: inkP(0.42), cursor: 'pointer',
            font: 'inherit', fontSize: 13, lineHeight: 1 }}>⋯</button>
        {menu && <DbMenu title={group.label} onClose={() => setMenu(false)}>
          <WsMenuRow onClick={() => { setMenu(false); onNew(group.id); }}>New {noun} here</WsMenuRow>
          <WsMenuRow onClick={() => { setMenu(false); setShut(true); }}>Collapse column</WsMenuRow>
        </DbMenu>}
      </span>
      <button type="button" data-md-column-new aria-label={`New ${noun} in ${group.label}`}
        onClick={() => onNew(group.id)}
        style={{ width: 24, height: 24, flex: 'none', display: 'grid', placeItems: 'center', border: 'none',
          borderRadius: 7, background: 'transparent', color: inkP(0.42), cursor: 'pointer',
          font: 'inherit', fontSize: 15, lineHeight: 1 }}>+</button>
    </header>

    <div className="md-scroll" ref={scroll} style={{ flex: 1, minHeight: 0, overflowY: 'auto',
      display: 'flex', flexDirection: 'column', gap: 6, padding: '0 7px 9px' }}>
      {/* Flattened, not wrapped. Putting the space and the card it precedes
          inside a fragment gives that position a NEW key, and React answers a
          new key by throwing the card's node away and building another — which
          costs a card its identity every time the space moves to it: the slide
          has nothing to measure from, and anything the node was holding is
          gone. Two siblings in one flat list keep their own keys. */}
      {/* ── D1 · a window, above the floor ─────────────────────────────
          Below WS_WINDOW_FLOOR this is the flat flex column it has always
          been: cards in the flow, gap 6, the space slotted in beside the card
          it will replace. Above it, the same cards hang in a stack that
          declares the height of the WHOLE column, so the scrollbar keeps
          telling the truth about how much is there.

          The windowed path lays out `flow` — the rows minus the one in the
          hand. The plain path keeps the held card mounted and hidden, which is
          deliberate (see the card's own note), but a column of twelve hundred
          is going to unmount it by scrolling anyway, so there is nothing left
          for that trick to protect. */}
      {windowed ? <div data-md-window-stack style={{ position: 'relative', width: '100%',
        /* flexShrink MATTERS HERE and its absence does not look like a bug. The
           column body is a flex column, so this stack is a flex ITEM, and a
           flex item's default is to shrink to fit its container. It did: the
           stack declared 48,128px, the scroller reported 1,261, and seven
           hundred cards were below a floor nobody could scroll to. The height
           was right in the DOM and wrong on the screen. */
        flexShrink: 0,
        height: virt.getTotalSize() + (space ? gapHeight : 0) }}>
        {virt.getVirtualItems().map((item) => { const row = flow[item.index]; if (!row) return null;
          /* Everything at or below the open space is pushed down by exactly the
             space's height — the same seat the flow path gets for free. */
          const shift = space && item.index >= gapAt ? gapHeight : 0;
          return <div key={row.id} data-md-window-seat
            style={{ position: 'absolute', top: 0, insetInlineStart: 0, insetInlineEnd: 0,
              transform: `translateY(${item.start + shift}px)`, display: 'flex', flexDirection: 'column' }}>
            {cardFor(row)}
          </div>; })}
        {space && <div style={{ position: 'absolute', top: 0, insetInlineStart: 0, insetInlineEnd: 0,
          transform: `translateY(${(gapAt >= flow.length ? virt.getTotalSize() : gapAt * WS_CARD_SEAT)}px)`,
          display: 'flex', flexDirection: 'column' }}>{space}</div>}
      </div>
      : group.rows.flatMap((row) => { const card = cardFor(row);
      return gapBefore === row.id ? [space, card] : [card]; })}
      {gapLast && space}
      {!group.rows.length && !space && <div style={{ padding: '10px 4px', color: inkP(0.24), fontSize: 11.5 }}>
        Nothing here.
      </div>}
    </div>
  </section>;
}

/* ── the places the board is not drawing ───────────────────────────────────
   Hiding what is empty is a good instinct — nine columns of nothing is a
   progress bar in disguise, and this Workshop has said so since its first day.
   But there is a difference between not drawing a place and not having one, and
   the board had been treating them as the same thing: an empty status LEFT, and
   the only way back to it was the menu the decision was made in. So the places
   that are not drawn are listed at the end of the board.

   IN THE BOARD'S OWN SIDEWAYS SCROLL, not pinned to the edge of the window.
   This is the end of the board, so it should be reached by getting to the end of
   the board — which also means the pull that brings the board to a hand holding
   a card brings this along with it, and a phone reaches it the same way it
   reaches the fourth column.

   IT IS NOT A COLUMN and does not pretend to be one. No card lives here, so
   nothing measures seats, opens a space or slides. What a row does is take a
   card — and a row with nothing in it to part says so by lighting itself rather
   than by parting. */
function WsEmptyRail({ places, axis, accent, hand, onNew, noun = 'project' }) {
  const light = isLightP();
  /* Held here and nowhere else. A collapsed column keeps its own posture the
     same way (`shut`, above): for as long as the Workshop is open, and not one
     minute longer. Storing it would make the shape of a glance into a setting to
     maintain, and there is nothing here worth remembering. */
  const [folded, setFolded] = useStP(false);
  return <section data-md-empty-rail style={{ display: 'flex', flexDirection: 'column',
    flex: 'none', width: 272, gap: 5, alignSelf: 'start', minHeight: 0, maxHeight: '100%',
    overflowY: 'auto' }}>
    <header style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none',
      padding: '9px 8px 7px' }}>
      <button type="button" data-md-empty-fold onClick={() => setFolded(!folded)}
        aria-expanded={folded ? 'false' : 'true'}
        aria-label={`${folded ? 'Show' : 'Hide'} the empty columns`}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 0, border: 'none',
          background: 'transparent', color: 'inherit', cursor: 'pointer', font: 'inherit' }}>
        <span style={{ display: 'flex', transform: folded ? 'none' : 'rotate(90deg)',
          transition: 'transform .16s cubic-bezier(.22,.61,.36,1)' }}>{CHEVRON_P(inkP(0.3))}</span>
        <span style={{ color: inkP(0.45), fontSize: 11.5, fontWeight: 620 }}>Empty columns</span>
      </button>
    </header>
    {!folded && places.map((group) => { const over = !!(hand.drag && hand.drag.zone === group.id);
      return <div key={group.id} data-md-empty-row={group.id} data-md-drop-zone={group.id}
        data-md-drop-over={over ? '' : undefined}
        style={{ display: 'flex', alignItems: 'center', gap: 7, flex: 'none',
          borderRadius: 12, padding: '8px 10px',
          /* Lit, not parted. The wash and the ring are the column's own — one
             grammar for "the card is going here" — but a row has no seats, so
             what it lights is itself. */
          background: over ? rgbaP(accent, light ? 0.05 : 0.07) : 'transparent',
          border: `1px solid ${over ? rgbaP(accent, light ? 0.34 : 0.4) : inkP(light ? 0.1 : 0.14)}`,
          transition: 'background .16s cubic-bezier(.22,.61,.36,1)' }}>
      {wsAxisMark(axis, group, light, accent)}
      <span dir="auto" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', color: inkP(0.7), fontSize: 12.5, fontWeight: 620 }}>{group.label}</span>
      <span style={{ flex: 1 }} />
      {/* Always a nought, which is the only number it can ever be. It is here to
          say why the row is, not to be watched. */}
      <span data-md-empty-count style={{ flex: 'none', color: inkP(0.3), fontSize: 11.5,
        fontVariantNumeric: 'tabular-nums' }}>0</span>
      {/* The same `+` every column header carries, on the places that have no
          header — which is where it is needed most, since the alternative is to
          start the project somewhere else and then carry it here. */}
      <button type="button" data-md-empty-new={group.id}
        aria-label={`New ${noun} in ${group.label}`} onClick={() => onNew(group.id)}
        style={{ width: 22, height: 22, flex: 'none', display: 'grid', placeItems: 'center',
          border: 'none', borderRadius: 7, background: 'transparent', color: inkP(0.42),
          cursor: 'pointer', font: 'inherit', fontSize: 15, lineHeight: 1 }}>+</button>
    </div>; })}
  </section>;
}

/* ── the window ──────────────────────────────────────────────────────────── */
/* What this window's ⌘Z governs: the documents its own six modules write, and
   nothing else. Named here rather than guessed at the door, and pinned by a
   test that reads those modules — a key added to one of them and not to this
   list would otherwise be silently outside undo, with nothing to say so.

   `md:workflows` is in the list even though the Statuses page lives in
   Settings, and that is deliberate: deleting a status MOVES the records that
   wore it, in one gesture. With the records inside this scope and the
   vocabulary outside it, one press would restore fourteen records into a status
   that no longer exists — and half an undo is worse than none.

   `WS_UNDO_TEXT` is the subset somebody WRITES into. Only a document a person
   types into may merge into a burst; an index that happens to be rewritten
   twice in a second is two acts, not one sentence. */
const WS_UNDO_KEYS = ['md:projects', 'md:project:', 'md:pursuits', 'md:pursuit:',
  'md:projectViews', 'md:projectRecords', 'md:domain:', 'md:workflows', 'md:workflowUse',
  'md:recordStatus'];
const WS_UNDO_TEXT = ['md:project:', 'md:pursuit:', 'md:domain:'];

function ProjectsSpace({ accent, onClose, events, records, boards, completed, todayKey, nowMin,
  onNewPage, projectRecords, onOpenDayRecord, onOpenBoard, onNewBoard,
  onOpenProject = null,
  initialDomain = null, initialView = null, wikiRecords = [], wikiBacklinksFor = null,
  onWikiOpen = null, onWikiCreate = null, tagOptions = [] }) {
  const light = isLightP();
  const L = useLayoutP ? useLayoutP() : { canvas: false, overlay: true, panel: 0 };
  const shell = useRefP(null);

  /* The docked-canvas desktop, and nowhere else. Asked here rather than stored,
     so the answer changes with the window and not with the state. */
  const resizable = !!L.canvas && !L.overlay;
  /* How you left it (CS-4) — the same hook the other two windows and the sheet
     use, keyed to THIS shape of screen. The default stays `board`; what changes
     is that your own choice outlives the window. */
  const [wanted, setWanted] = window.useMdRemembered('projects', L,
    resizable ? window.MD_SPACE_SIZES : window.MD_SPACE_FULL_ONLY,
    resizable ? 'board' : 'full');
  const size = resizable ? wanted : 'full';

  /* Which frames this window can actually draw. A frame it cannot draw would
     render an EMPTY SCREEN with no way back — `frame` is truthy, so the body is
     replaced by nothing — and the caller most likely to hand one over is an
     older build's deep link, or `{kind:'project'}`, which moved out to the
     Project window in this phase. Refusing to enter is the honest answer:
     you are left on the list, which is where that link used to start. */
  const drawable = (next) => !!next && WS_FRAMES.indexOf(next.kind) !== -1;

  /* A real stack — the shared walk (useMdFrames, N-6). The old screen kept
     ONE remembered frame, so a project opened from a pursuit opened from a
     domain went "back" to the root and lost two steps of where you had been.
     A deep link from outside replaces where you are — the hook reseeds when
     these two props change, and never on an ordinary re-render. */
  const { stack, frame, push, pop, clear: toList, replaceTop } = window.useMdFrames(
    initialView || (initialDomain ? { kind: 'domain', id: initialDomain } : null),
    [initialView, initialDomain], { accepts: drawable, ride: 'workshop-frame' });

  /* Walking to another page ends whatever was being typed on the last one.
     Writing merges into a burst while it is the same page inside half a second,
     so without this, leaving a project and typing on the next one quickly
     enough would join the two — and one ⌘Z would take back a sentence from a
     page that is no longer on screen. */
  useEfP(() => {
    if (typeof window.mdHistorySeal === 'function') window.mdHistorySeal('projects');
  }, [frame ? frame.kind : null, frame ? frame.id : null]);
  /* The sheet a project begins in is not a FRAME. A frame replaces the window,
     and beginning a project is something you do while looking at the others —
     see NewProjectSheet's own header. `null` means closed; an object is the
     seed the door that opened it carries. */
  const [beginning, setBeginning] = useStP(null);

  /* ⌘Z in this window, armed while the window is open and forgotten when it
     closes — the per-surface rule every multi-window app converges on, and the
     one that stops a key reaching into something you can no longer see. While
     it is open it sits above the day: a project renamed is this window's to
     take back, and a prayer marked is still the day's. */
  useEfP(() => {
    if (typeof window.mdHistoryOpen !== 'function') return undefined;
    window.mdHistoryOpen('projects', { keys: WS_UNDO_KEYS, text: WS_UNDO_TEXT });
    return () => window.mdHistoryClose('projects');
  }, []);

  const [tick, bump] = useStP(0);
  const again = () => bump((n) => n + 1);

  /* An undo that moves storage and leaves the screen showing the old answer
     looks like a press that did nothing — so the hand presses again, and two
     acts are gone with one of them accounted for. Everything here is already
     re-read from `tick`, so the history's own event only has to bump it. */
  useEfP(() => {
    const hear = () => again();
    window.addEventListener('mdhistorychange', hear);
    return () => window.removeEventListener('mdhistorychange', hear);
  }, []);
  const [menu, setMenu] = useStP(null);          // 'display' | 'newview'
  /* The asking menu, when open: which filter it edits (at, or -1 for a new
     one), which field's answers it shows (null = the field page), and where
     it hangs from — the pressed word's own box. */
  const [ask, setAsk] = useStP(null);
  /* A view being THOUGHT, not yet written: + fills this instead of storage.
     Every edit — filters, display, the name itself — belongs to this object
     until Save writes the whole of it as one view, or Cancel forgets it. */
  const [draft, setDraft] = useStP(null);
  /* The saved view's own words, being edited in place from its chip. */
  const [editHead, setEditHead] = useStP(false);
  /* The bin on that row, asked once. A view holds questions somebody composed
     and there is nothing to undo it with, so the first press only arms — and
     anything else that happens (the hand leaves, the row closes, another view
     is chosen) disarms it. */
  const [armed, setArmed] = useStP(false);

  /* F opens the asking, the way it does in the app this grammar was learned
     from — but never while somebody is typing an f into a field, and never
     over a menu already open. The menu hangs from the Filter tool, the word
     the key stands for. */
  useEfP(() => {
    const hear = (event) => {
      if (event.key !== 'f' && event.key !== 'F') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target && target.closest
        && target.closest('input, textarea, select, [contenteditable="true"]')) return;
      /* The front window owns the key. A standing Project window is in front
         of the Workshop by construction, so decline rather than duel — the
         Project window's twin no longer has to silence anyone. */
      if (document.querySelector('[data-md-project-space]')) return;
      if (ask) return;
      const tool = document.querySelector('[data-md-projects-space] [aria-label="Filter"]');
      setAsk({ at: -1, field: null, ...(tool ? wsBoxPlace(tool) : { x: 8, y: 48 }) });
      event.preventDefault();
    };
    window.addEventListener('keydown', hear);
    return () => window.removeEventListener('keydown', hear);
  }, [ask]);
  const [search, setSearch] = useStP('');
  const [searchOpen, setSearchOpen] = useStP(false);
  /* Which part of a life is being read through, and where its menu hangs from.
     Two pieces of state for the same reason `ask` is one: the lens outlives the
     menu, and the menu has to remember the box it was opened under. */
  const [scope, setScope] = useStP(null);
  const [lens, setLens] = useStP(null);

  /* P opens the pursuits, the way F opens the asking — same guards, same door:
     the key hangs its menu from the control it stands for. */
  useEfP(() => {
    const hear = (event) => {
      if (event.key !== 'p' && event.key !== 'P') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target && target.closest
        && target.closest('input, textarea, select, [contenteditable="true"]')) return;
      /* Same front-window rule as F above. */
      if (document.querySelector('[data-md-project-space]')) return;
      if (lens || ask) return;
      const tool = document.querySelector('[data-md-projects-space] [data-md-lens]');
      if (!tool) return;
      setLens(wsBoxPlace(tool));
      event.preventDefault();
    };
    window.addEventListener('keydown', hear);
    return () => window.removeEventListener('keydown', hear);
  }, [lens, ask]);

  const views = useMeP(() => window.mdWorkshopViews(), [tick]);
  const [activeId, setActiveId] = useStP(() => window.mdWorkshopActiveId());
  /* While a draft stands, IT is the view every control edits and every body
     draws — the stored views are untouched while somebody thinks. */
  const view = draft || views.find((row) => row.id === activeId) || views[0];

  /* The typed search is TRANSIENT. A saved view may carry one of its own — "the
     cutting view" is a legitimate thing to keep — but what somebody types into
     the box is a question, not a setting, and storing it means reopening the
     Workshop tomorrow to a list mysteriously missing most of itself. */
  const effective = useMeP(() => ({ ...view, search: search || view.search, scope }),
    [view, search, scope]);

  /* Handed in from the state md-app already holds, so the pipeline is pure and
     the arithmetic stays provable without a browser. */
  const ctx = useMeP(() => ({ events: events || {}, boards: boards || [],
    projectRecords: projectRecords || [], completed: completed || {}, todayKey, nowMin }),
  [events, boards, projectRecords, completed, todayKey, nowMin]);

  const groups = useMeP(() => window.mdWorkshopGroups(effective, ctx), [effective, ctx, tick]);
  const total = groups.reduce((sum, group) => sum + group.rows.length, 0);

  /* How many rows the ASKING hides: the same pipeline with the asking
     stripped, minus what shows. Display decisions stay in place on both
     sides of the subtraction, so the closed are never in this number. */
  const asking = view.filters.length > 0 || !!(search || view.search);
  const hidden = useMeP(() => (asking
    ? window.mdWorkshopRows({ ...effective, filters: [], search: '' }, ctx).length - total
    : 0), [asking, effective, ctx, tick, total]);
  const clearAsking = () => { setAsk(null); setSearch(''); setSearchOpen(false);
    patch({ filters: [], search: '' }); };

  /* The hand, over the list rows. Dropping on a row in ANOTHER group is two
     facts at once: the axis property changes (through the door that can
     refuse, and a refusal drops the whole gesture — a row that would not
     regroup should not jump either), and the order splices so the row lands
     where it was put. One write per document. */
  const flatIds = groups.reduce((all, group) => all.concat(group.rows), []).map((row) => row.id);
  const groupOf = (id) => groups.find((group) => group.rows.some((row) => row.id === id));
  const listDrop = (movedId, over) => {
    const targetId = over.id;
    if (!targetId) return;
    /* The order and the group are two separate answers now: a row can change
       its section without moving in the sequence, and a drop that changes
       nothing at all in the sequence must still be allowed to change the
       fact. So the axis write is asked first, and the order is written only
       if there is one to write. */
    const order = wsReorder(flatIds, movedId, targetId, over.edge);
    if (view.groupBy) {
      const from = groupOf(movedId), to = groupOf(targetId);
      if (from && to && from.id !== to.id) {
        const value = to.id === 'empty' ? null : to.id;
        const write = view.groupBy === 'status' ? window.mdProjectTrySetStatus(movedId, value)
          : view.groupBy === 'priority' ? window.mdProjectTrySetPriority(movedId, value)
            : view.groupBy === 'pursuit' ? window.mdProjectTrySetPursuit(movedId, value)
              : window.mdProjectTrySetDomain(movedId, value);
        if (!wsCommit(write)) { again(); return; }
      }
    }
    if (order) patch({ order }); else again();
  };
  const listHand = useWsHand({ canDrag: view.type === 'list' && !view.sorts.length,
    onDrop: listDrop });
  /* The rail's drops are the LIST's drops: a target row names both a place in
     the order and — grouped — the section whose fact the row must take on.
     `listDrop` already speaks that whole sentence, refusal included. */
  const railHand = useWsHand({ canDrag: view.type === 'timeline' && !view.sorts.length,
    onDrop: listDrop });

  /* One layer on the shared stack, and one frame per press. */
  useDbEscapeLayer(() => { if (stack.length) pop(); else if (onClose) onClose(); }, true);
  /* The open search field is a layer of its own, stacked above the window —
     and it OWNS its input, so Escape pressed inside it withdraws the question
     in one press instead of merely blurring the field. */
  const searchField = useRefP(null);
  useDbEscapeLayer(() => { setSearch(''); setSearchOpen(false); },
    searchOpen || search !== '', (target) => target === searchField.current);
  /* Only once the window has actually covered the app. At board size it is drawn
     to leave the day panel showing — that is the entire reason the size exists —
     and a day you can read but cannot touch is worse than one that is hidden:
     opening the Workshop should not cost you the ability to tick something off
     beside it. The Databases window, the same rectangle with the other tenant,
     never silenced anything, and this is that answer with the modal case kept:
     full screen leaves nothing to reach, so there the silence is the truth. */
  useDbInertBackground(shell, size === 'full');

  const chooseView = (id) => {
    /* Leaving a draft for a saved tab is Cancel by another door: nothing was
       ever written, so there is nothing to lose but the thought. */
    setDraft(null);
    setEditHead(false);
    setArmed(false);
    setActiveId(id);
    setSearch('');
    window.mdWorkshopSetActive(id);
    again();
  };
  const patch = (changes) => {
    if (draft) { setDraft({ ...draft, ...changes }); return; }
    window.mdWorkshopViewPatch(view.id, changes); again();
  };

  /* ── the draft's three verbs ────────────────────────────────────────── */
  const startDraft = () => {
    if (draft) return;
    setMenu(null); setAsk(null); setEditHead(false);
    const base = window.mdWorkshopSanitiseView({ type: 'list' }) || {};
    setDraft({ ...base, id: 'draft', name: '', description: '' });
  };
  const cancelDraft = () => setDraft(null);
  const saveDraft = () => {
    if (!draft) return;
    const { id: _draftId, name, description, type, ...rest } = draft;
    const made = window.mdWorkshopViewCreate(type, name || undefined, { ...rest, description });
    setDraft(null);
    if (!made) return;
    setActiveId(made.id);
    if (dbNotifyP) dbNotifyP(WS_NOTICE, 'The view is kept.');
    again();
  };
  /* Escape is Cancel — one layer on the same stack every menu answers to,
     registered under them so an open menu closes first. */
  useDbEscapeLayer(() => { setDraft(null); setEditHead(false); setArmed(false); },
    !!draft || editHead);

  /* Ending the view from its own row. The last one is not offered: a screen
     with no view is no screen, which is the engine's law too. */
  const deleteView = () => {
    if (views.length <= 1) return;
    window.mdWorkshopViewDelete(view.id);
    setActiveId(window.mdWorkshopActiveId());
    setArmed(false);
    setEditHead(false);
    again();
  };

  /* A PROJECT IS A WINDOW NOW, not a frame in this one. Five doors lead to one
     — a row in the list, a card on the board, a row on the timeline's rail, a
     row inside a domain's page or a pursuit's — and all five arrive here,
     which is the only reason this is one line rather than five places to
     forget. Projects stays mounted underneath with its scroll and its stack
     exactly as they were; the new window stands on it.

     There is no frame left to fall back to, and that is deliberate: two ways
     to look at one project drift, and the one nobody maintains is the one
     somebody lands on. */
  const openProject = (id) => { if (onOpenProject) onOpenProject(id); };
  const resize = () => setWanted(size === 'full' ? 'board' : 'full');

  /* What the last drop is on the way to undoing. A ref, not state: it is a
     fact about the previous gesture, not something to draw. (What is being
     CARRIED lives in the hand now — useWsHand.) */
  const undo = useRefP(null);
  const [notice, dismissNotice] = useDbNotice(WS_NOTICE);

  /* ── the toolbar's popovers ──────────────────────────────────────────── */
  /* Creation kept its own small door — a "+" beside the tabs — and Q-5 made
     the door open onto a DRAFT rather than a write: the type is chosen from
     the Display popover while drafting, like every other display decision.
     Deletion stays at the Display popover's foot, beside the other things
     said about the current view. */

  /* W-14. One join for the whole set. Two conditions read as "all" answer a
     narrower question every time you add one, which is the only question the
     screen could ask until now — and "a Health project OR a Wealth one" was
     not askable at all. Nested groups are a query builder; this is the join
     somebody actually reaches for. */
  const flipJoin = () => patch({ conjunction: view.conjunction === 'or' ? 'and' : 'or' });
  const joinWord = view.conjunction === 'or' ? 'or' : 'and';


  /* ── the Display popover ──────────────────────────────────────────────────
     Linear's lesson, learned whole: one popover where the view TYPE is a
     segmented control and every display decision sits under it. Pressing a
     segment converts the CURRENT saved view in place — the popover stays open,
     because somebody in here is tuning, not committing. Filters are absent on
     purpose; a question you asked is not furniture. */
  const displayMenu = <DbMenu deep onClose={() => setMenu(null)}>
    <WsSegment hook="data-md-viewtype" label="View type" accent={accent} value={view.type}
      options={[{ id: 'list', label: 'List', glyph: 'list' },
        { id: 'board', label: 'Board', glyph: 'board' },
        { id: 'timeline', label: 'Timeline', glyph: 'timeline' }]}
      onChange={(type) => patch({ type })} />
    <WsControlRow label="Grouping">
      <select aria-label="Group by" value={view.groupBy || ''}
        onChange={(e) => patch({ groupBy: e.target.value || null })}
        style={{ width: '100%', margin: 0, height: 30 }}>
        <option value="">No grouping</option>
        {(window.MD_WORKSHOP_GROUP_BY || []).map((axis) =>
          <option key={axis} value={axis}>{WS_FIELD_LABEL[axis]}</option>)}
      </select>
      {/* A direction of nothing is not a control. */}
      {view.groupBy && <WsDirArrow label="Group direction" direction={view.groupDirection}
        onFlip={() => patch({ groupDirection: view.groupDirection === 'desc' ? 'asc' : 'desc' })} />}
    </WsControlRow>
    {/* Ordering, and Manual is one of its answers. It used to be a sentence
        BESIDE this dropdown while the dropdown listed only fields — so the
        state somebody is in most of the time was the one state the control
        could not say, and lifting an ordering meant hunting the × that removed
        the row. One control now: Manual, then the eight fields, and the
        direction appears when there is a direction to have.

        What the control shows is what the control writes. A stack of eight
        from an older build (the engine still honours one) shows its first here
        and is replaced whole the moment somebody chooses — a tail nobody can
        see must not go on ordering the rows. The direction survives a change
        of field: picking a new key is not a request to start over. */}
    <WsControlRow label="Ordering">
      <select aria-label="Order by" value={view.sorts.length ? view.sorts[0].field : ''}
        onChange={(e) => patch({ sorts: e.target.value
          ? [{ field: e.target.value,
            direction: view.sorts.length ? view.sorts[0].direction : 'asc' }]
          : [] })}
        style={{ width: '100%', margin: 0, height: 30 }}>
        <option value="">Manual</option>
        {(window.MD_WORKSHOP_FIELDS || []).map((f) =>
          <option key={f} value={f}>{WS_FIELD_LABEL[f]}</option>)}
      </select>
      {view.sorts.length > 0 && <WsDirArrow label="Sort direction"
        direction={view.sorts[0].direction}
        onFlip={() => patch({ sorts: [{ field: view.sorts[0].field,
          direction: view.sorts[0].direction === 'desc' ? 'asc' : 'desc' }] })} />}
    </WsControlRow>
    <WsControlRow label="Closed">
      {/* A dropdown, not a switch: it sits in the label+control column every
          row above it established, and "Shown" is an answer where a lone knob
          is only a state. */}
      <select aria-label="Show closed projects" value={view.showClosed ? 'shown' : 'hidden'}
        onChange={(e) => patch({ showClosed: e.target.value === 'shown' })}
        style={{ width: '100%', margin: 0, height: 30 }}>
        <option value="hidden">Hidden</option>
        <option value="shown">Shown</option>
      </select>
    </WsControlRow>
    <div className="md-db-menu-rule" />
    {/* Each view type owns its own little section, in its own words — one
        field (showEmptyGroups) wearing the board's word on the board, because
        a column IS what a group is called over there. */}
    <div className="md-db-field-label">
      {view.type === 'board' ? 'Board options' : view.type === 'timeline' ? 'Timeline options' : 'List options'}
    </div>
    {view.type !== 'timeline' && <WsSwitch
      label={view.type === 'board' ? 'Show empty columns' : 'Show empty groups'}
      on={view.showEmptyGroups}
      onFlip={() => patch({ showEmptyGroups: !view.showEmptyGroups })} />}
    {view.type === 'timeline' && <WsControlRow label="Zoom">
      <select aria-label="Zoom" value={view.timelineZoom}
        onChange={(e) => patch({ timelineZoom: e.target.value })}
        style={{ width: '100%', margin: 0, height: 30 }}>
        {(window.MD_WORKSHOP_ZOOMS || []).map((zoom) => <option key={zoom} value={zoom}>
          {zoom.charAt(0).toUpperCase() + zoom.slice(1)}</option>)}
      </select>
    </WsControlRow>}
    <div className="md-db-menu-rule" />
    <div className="md-db-field-label">Display properties</div>
    {/* Every fact as a pill that lights when a row wears it. Name is pressed
        and quiet rather than absent: a lock somebody can see beats a hole
        where a control should be, and a row nobody can tell apart is not a
        row. Writes the same visibleFields the old menu wrote. */}
    <div data-md-props style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '2px 8px 8px' }}>
      {(window.MD_WORKSHOP_FIELDS || []).map((field) => {
        const on = view.visibleFields.indexOf(field) !== -1;
        const locked = field === 'name';
        return <button key={field} type="button" data-md-prop={field}
          aria-pressed={on ? 'true' : 'false'} aria-disabled={locked ? 'true' : undefined}
          title={locked ? 'Always' : undefined}
          onClick={() => { if (locked) return;
            patch({ visibleFields: on ? view.visibleFields.filter((f) => f !== field)
              : [...view.visibleFields, field] }); }}
          style={{ minHeight: 22, border: 'none', borderRadius: 7, padding: '2px 9px',
            cursor: locked ? 'default' : 'pointer', font: 'inherit', fontSize: 10.5,
            fontWeight: 620, opacity: locked ? 0.6 : 1,
            background: on ? rgbaP(accent, light ? 0.12 : 0.16) : inkP(light ? 0.045 : 0.06),
            color: on ? inkP(0.88) : inkP(0.42) }}>
          {WS_FIELD_LABEL[field]}
        </button>;
      })}
    </div>
    {/* No delete here. This popover tunes a view's LOOK — grouping, ordering,
        what shows — and ending the view itself is not a display decision. It
        lives on the view's own row now, beside the mark that closes it. */}
    <div className="md-db-menu-rule" />
    {/* No "are you sure": Reset is one patch, every field of it individually
        undoable by hand, and pressed twice it writes not a byte. */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '5px 8px 3px' }}>
      <button type="button" onClick={() => patch(WS_DISPLAY_DEFAULTS(view.type))}
        style={{ height: 26, border: 'none', borderRadius: 7, padding: '0 8px',
          cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 600,
          background: 'transparent', color: inkP(0.5) }}>Reset</button>
      <button type="button" onClick={() => setMenu(null)}
        style={{ height: 26, border: 'none', borderRadius: 7, padding: '0 10px',
          cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 700,
          background: 'transparent', color: rgbaP(accent, 1) }}>Done</button>
    </div>
  </DbMenu>;

  /* ── the list ─────────────────────────────────────────────────────────────
     Header and rows are built from the SAME visibleFields list, which is the
     whole reason a hidden column leaves no gap: there is no second place that
     could still be counting eight. */
  const narrow = !L.canvas || L.overlay;
  const kept = view.visibleFields.filter((f) => WS_NARROW_FIELDS.includes(f));
  const fields = narrow ? (kept.length ? kept : ['name']) : view.visibleFields;
  /* Narrow, the name takes whatever is left rather than claiming 210px — a
     minimum is what makes a table scroll, and there is nowhere to scroll to. */
  const template = fields
    .map((f) => (narrow && f === 'name' ? 'minmax(0,1fr)' : WS_COL_WIDTH[f] || 'minmax(0,1fr)'))
    .join(' ');
  const gridMin = narrow ? 0 : fields.reduce((sum, f) => sum + (WS_COL_MIN[f] || 80), 0)
    + Math.max(0, fields.length - 1) * WS_COL_GAP;

  /* Clicking a header makes that field THE sort — ascending, then descending,
     then off. Off matters: a sort you cannot lift with the gesture that set it
     is a trap, and the menu is a long way to go to undo one click. Several
     sorts at once is still the Sort menu's job. */
  const sortBy = (field) => {
    const held = view.sorts.find((sort) => sort.field === field);
    if (!held) patch({ sorts: [{ field, direction: 'asc' }] });
    else if (held.direction === 'asc') patch({ sorts: [{ field, direction: 'desc' }] });
    else patch({ sorts: [] });
  };
  const sortState = (field) => {
    const held = view.sorts.find((sort) => sort.field === field);
    return held ? (held.direction === 'asc' ? 'ascending' : 'descending') : 'none';
  };

  const cell = (field, row) => {
    if (field === 'name') return <span dir="auto" style={{ display: 'flex', alignItems: 'center',
      gap: 9, minWidth: 0, color: inkP(0.94), fontSize: 14, fontWeight: 570 }}>
      <span aria-hidden="true" style={{ flex: 'none', width: 7, height: 7, borderRadius: 3,
        background: rgbaP(domColorP(row.domainId, light), 0.8) }} />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' }}>{row.name}</span>
    </span>;
    if (field === 'status') return <span style={{ display: 'flex', alignItems: 'center', gap: 6,
      color: inkP(0.66), fontSize: 12 }}>
      <StatusMark category={row.statusCategory} color={row.statusColor || null} size={12} />
      {row.statusName}
    </span>;
    if (field === 'priority') return <span style={{ display: 'flex', alignItems: 'center', gap: 6,
      color: inkP(row.priority ? 0.66 : 0.3), fontSize: 12 }}>
      <PriorityMark priority={row.priority} color={domColorP(row.domainId, light)} size={12} />
      {priorityLabel(row.priority)}
    </span>;
    if (field === 'pursuit') return row.pursuitName || MD_NONE;
    if (field === 'domain') return row.domainLabel || MD_NONE;
    if (field === 'start' || field === 'target') return wsDayLabel(row[field], todayKey);
    if (field === 'time') return spentLabel(row.minutes);
    if (field === 'records') return String(row.records);
    return MD_NONE;
  };

  const rowLine = (row) => { const land = wsLanding(listHand.drag, row.id); return <button key={row.id}
    type="button" data-md-row
    data-md-drag-id={row.id}
    data-md-drop-target={land ? '' : undefined}
    data-md-drop-edge={land && listHand.drag.edge ? listHand.drag.edge : undefined}
    onClick={() => { if (listHand.swallowClick()) return; openProject(row.id); }}
    onPointerDown={(event) => listHand.start(event, row.id)}
    aria-label={[row.name, row.statusName,
      row.pursuitName || 'no pursuit',
      row.target ? `due ${wsDayLabel(row.target, todayKey)}` : 'no target',
      row.minutes ? `${spentLabel(row.minutes)} given` : 'no time yet'].join(' — ')}
    style={{ display: 'grid', gridTemplateColumns: template, alignItems: 'center', gap: 10,
      width: '100%', minHeight: 40, border: 'none',
      /* No background here: the sheet holds it, so :hover can still win. */
      color: inkP(1), textAlign: 'start', padding: '7px 6px',
      cursor: 'pointer', font: 'inherit',
      /* The carried row dims; the row it would land on wears the line. */
      opacity: listHand.drag && listHand.drag.id === row.id ? 0.45 : undefined,
      boxShadow: land ? wsLandingLine(land, accent) : undefined }}>
    {fields.map((field) => <span key={field} data-md-cell={field} dir={field === 'name' ? undefined : 'auto'}
      style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        color: field === 'name' ? undefined : inkP(0.44), fontSize: field === 'name' ? undefined : 12,
        textAlign: field === 'time' || field === 'records' ? 'end' : 'start',
        fontVariantNumeric: 'tabular-nums' }}>{cell(field, row)}</span>)}
  </button>; };

  /* ── D1 · the list's one flat run ────────────────────────────────────────
     Every group's heading and every one of its rows, in the order they are
     drawn, as one array. Built only when it is going to be used: under the
     floor the plain path maps the groups directly and this stays empty.

     The heading only enters the run when there IS an axis — an ungrouped list
     draws no headings, and a run carrying invisible ones would leave a gap
     every time the window crossed a group boundary. */
  const listScroll = useRefP(null);
  const listCount = groups.reduce((sum, group) => sum + group.rows.length, 0)
    + (view.groupBy ? groups.length : 0);
  const listWindowed = listCount > WS_WINDOW_FLOOR;
  const listItems = useMeP(() => {
    if (!listWindowed) return [];
    const run = [];
    for (const group of groups) {
      if (view.groupBy) run.push({ kind: 'group', key: `g:${group.id}`, group });
      for (const row of group.rows) run.push({ kind: 'row', key: row.id, row });
    }
    return run;
  }, [groups, view.groupBy, listWindowed]);
  const listVirt = useVirtualP({
    count: listItems.length,
    getScrollElement: () => listScroll.current,
    estimateSize: (index) => (listItems[index] && listItems[index].kind === 'group' ? WS_GROUP_SEAT : WS_ROW_SEAT),
    overscan: 10,
    getItemKey: (index) => (listItems[index] ? listItems[index].key : index),
  });

  const header = <div role="row" style={{ position: 'sticky', top: 0, zIndex: 2,
    display: 'grid', gridTemplateColumns: template, alignItems: 'center', gap: 10,
    padding: '0 6px', height: 30,
    /* Opaque, because the rows run behind it — the same law the board's card
       and the timeline's name column already keep. At 92% the head let every
       row it outranked ghost through on the way past; and in the dark it is
       the dark itself rather than a near-black panel laid over it. */
    background: light ? '#f8f9fa' : '#000',
    borderBottom: `1px solid ${inkP(light ? 0.07 : 0.09)}` }}>
    {fields.map((field) => {
      const state = sortState(field);
      return <button key={field} type="button" data-md-col={field} role="columnheader"
        aria-sort={state} onClick={() => sortBy(field)}
        style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, height: '100%',
          border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit',
          fontSize: 10.5, fontWeight: 650, letterSpacing: '.04em', textTransform: 'uppercase',
          color: inkP(state === 'none' ? 0.34 : 0.7),
          justifyContent: field === 'time' || field === 'records' ? 'flex-end' : 'flex-start' }}>
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' }}>{WS_FIELD_LABEL[field]}</span>
        {state !== 'none' && <span aria-hidden="true" style={{ flex: 'none', fontSize: 9 }}>
          {state === 'ascending' ? '↑' : '↓'}</span>}
      </button>;
    })}
  </div>;

  /* ── the board ───────────────────────────────────────────────────────────
     The same rows the list draws, stacked by the axis instead of listed down
     it. A card is dragged and the fact underneath it changes — which is the
     second door onto `status`, and the first one that never asks anybody to
     open a project to say where it is.

     THE 'empty' COLUMN IS A REAL PLACE — on the axes that still have one. "No
     pursuit" is a thing a project genuinely is. The STATUS axis lost its empty
     column in Phase 5: absence resolves to the workflow's first Backlog status,
     so dropping onto Inbox is now an ordinary write to an ordinary column
     rather than a special case that CLEARS the field. The domain axis never had
     one — every project belongs to a part of life, so a "No domain" column
     would be a place nothing can ever be: the broken promise, inverted. */
  const axis = view.groupBy;
  /* WHICH VOCABULARY THE COLUMNS SPEAK — WF-13. One workflow among the cards
     in view and the columns are its statuses, wearing its names; more than one
     and they collapse to the six spine categories, because that is the only
     ladder two workflows hold in common. Asked of the pipeline rather than
     guessed from the shape of the column ids: a column named `paused` is a
     category here and a status id there, and the difference decides what a
     drop writes. */
  const voice = useMeP(() => (axis === 'status'
    ? window.mdWorkshopStatusVoice(effective, ctx) : null), [axis, effective, ctx, tick]);
  const spine = axis === 'status' && !voice;
  /* A spine column names a category, and a category means a DIFFERENT status
     id on every card under it — asked of the workflow that card reads through.
     Since WK-1 that always has an answer, because every workflow is read with
     all six categories occupied; the null branch is the guard for a shape
     nothing can now produce rather than for one that was expected. */
  const landing = (projectId, columnId) => (spine && columnId && columnId !== 'empty'
    ? ((window.mdProjectStatusLanding(projectId, columnId) || {}).id || null)
    : columnId);
  /* One rule, said once and read three times — by the columns, by the list of
     empty places, and by the drop that could aim at either. */
  const canHold = (id) => !(axis === 'domain' && id === 'empty');
  const columns = groups.filter((group) => canHold(group.id) || group.rows.length);

  /* ── what the board is holding back ──────────────────────────────────────
     The same grouping the board just did, asked a second time with the one
     switch flipped, and then subtracted: what the board WOULD have drawn, minus
     what it did. Nothing here keeps a second list of statuses, which is the only
     reason the list at the end cannot fall out of step with the columns — it is
     made of them. Add a status tomorrow and it knows without being told.

     Asked at all only when the switch is off, because with the switch on the
     answer is the grouping we already have. */
  const everyGroup = useMeP(() => (effective.type === 'board' && effective.groupBy
    && !effective.showEmptyGroups
    ? window.mdWorkshopGroups({ ...effective, showEmptyGroups: true }, ctx) : []),
  [effective, ctx, tick]);
  const places = everyGroup.filter((group) => !group.rows.length && canHold(group.id)
    && !columns.some((drawn) => drawn.id === group.id));

  /* A place's own name, whether or not the board is drawing a column for it. The
     label used to be looked up among the COLUMNS, which is fine right up until a
     card can be sent somewhere that has none — and the one place this release is
     about is exactly that, so the notice named nothing at all. */
  const placeLabel = (id) => {
    if (id === 'empty') return WS_EMPTY_LABEL[axis] || 'nothing';
    const found = columns.find((group) => group.id === id)
      || everyGroup.find((group) => group.id === id);
    return (found && found.label) || '';
  };

  const columnMark = (group) => wsAxisMark(axis, group, light, accent);

  /* A card says what the Display popover shows — the same visibleFields list
     the table reads, in the same cell vocabulary — minus the name (the card's
     first line) and the column's own axis (the fact already said above it).

     No em-dash and no bare zero here. On a table a missing value has a column
     to leave empty, and the dash says which one; on a card it is a dash
     floating in space beside every project that has not been given a day yet,
     and a floating 0 is worse — a number with no column to name it. */
  const cardCell = (field, row) => {
    if (field === 'status') return row.status
      ? <span key={field} data-md-card-cell={field} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <StatusMark category={row.statusCategory} color={row.statusColor || null} size={10} />
        {row.statusName}
      </span> : null;
    if (field === 'priority') return row.priority
      ? <span key={field} data-md-card-cell={field} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <PriorityMark priority={row.priority} color={domColorP(row.domainId, light)} size={10} />
        {priorityLabel(row.priority)}
      </span> : null;
    const said = field === 'pursuit' ? row.pursuitName
      : field === 'domain' ? row.domainLabel
        : field === 'start' || field === 'target' ? (row[field] ? wsDayLabel(row[field], todayKey) : null)
          : field === 'time' ? (row.minutes ? spentLabel(row.minutes) : null)
            : field === 'records' ? (row.records ? String(row.records) : null) : null;
    return said ? <span key={field} data-md-card-cell={field} dir="auto"
      style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
        flex: field === 'pursuit' || field === 'domain' ? undefined : 'none' }}>{said}</span> : null;
  };
  /* The column's own fact is not repeated on the cards under it — EXCEPT
     under spine, where the column says the category and the card is the only
     place the user's own word is still legible. Dropping it there is how
     customisation evaporates the moment somebody zooms out. */
  const cardMeta = (row) => view.visibleFields
    .filter((field) => field !== 'name' && (field !== axis || spine))
    .map((field) => cardCell(field, row));

  /* A drop is one change through the door that can refuse it, and the refusal
     is said in words rather than swallowed. Nothing moves optimistically: the
     columns are drawn from storage, so a refused drop leaves the card exactly
     where the person picked it up rather than snapping back from somewhere it
     never really was. */
  const dropOn = (columnId, id) => {
    if (!id) return false;
    const row = window.mdProjectById(id);
    if (!row) return false;
    const value = columnId === 'empty' ? null : columnId;
    const stood = axis === 'status'
      ? ((window.mdProjectStatusOf ? window.mdProjectStatusOf(id) : null) || {}) : {};
    /* Compared against where the card IS, not against what is stored: a project
       that has never said a word is standing in Inbox, and dropping it back
       into Inbox must be a no-op rather than a write of the same fact. Under
       spine the comparison is in the column's own vocabulary — a card already
       standing in a category must not be shunted to that category's FIRST
       status just because somebody put it back down where it was. */
    const here = axis === 'status' ? (spine ? stood.category : stood.id) || null
      : axis === 'priority' ? (row.priority || null)
        : axis === 'pursuit' ? (row.pursuitId || null) : row.domainId;
    if (here === value) return false;
    /* The status the card is leaving, always as an id — the Undo below writes
       it straight back, and a category is not something you can be set to. */
    const before = axis === 'status' ? (stood.id || null)
      : axis === 'priority' ? (row.priority || null)
        : axis === 'pursuit' ? (row.pursuitId || null) : row.domainId;
    const next = axis === 'status' ? landing(id, value) : value;
    if (axis === 'status' && value && !next) {
      wsCommit({ ok: false, reason: 'no_landing' });
      return false;
    }
    const write = (aim) => (axis === 'status' ? window.mdProjectTrySetStatus(id, aim)
      : axis === 'priority' ? window.mdProjectTrySetPriority(id, aim)
        : axis === 'pursuit' ? window.mdProjectTrySetPursuit(id, aim)
          : window.mdProjectTrySetDomain(id, aim));
    const landed = wsCommit(write(next));
    again();
    /* An Undo only where something was overwritten. A card moved out of "not
       saying" lost nothing, and a toast on every drag would narrate a gesture
       whose result is already on screen. */
    if (landed && before != null) {
      undo.current = { id, value: before };
      dbNotifyP(WS_NOTICE, `Moved to ${placeLabel(columnId)}.`, true);
    }
    return landed;
  };

  /* The hand, over the cards. A drop names a card, a column, or both; the
     column is the fact and the card is the address. Cross-column, the axis
     changes through the door that can refuse — and a refusal drops the whole
     gesture, splice included. Within a column, only the hand-made order
     moves, and only while no sort is speaking. */
  /* Returns whether the gesture was answered rather than refused — the hand
     asks so it knows whether anything has anywhere to travel to. It is a
     report, not a decision: every refusal is still the door's, said in words,
     and this only repeats what the door already returned. */
  const boardDrop = (movedId, over) => {
    const from = groupOf(movedId);
    const target = over.zone;
    if (!from || !target) return false;
    if (target !== from.id) {
      if (axis === 'domain' && target === 'empty') return false;   // a place nothing can be
      const landed = dropOn(target, movedId);
      if (landed && over.id && !view.sorts.length) {
        const order = wsReorder(flatIds, movedId, over.id, over.edge);
        if (order) patch({ order });
      }
      return landed;
    }
    if (over.id && !view.sorts.length) {
      const order = wsReorder(flatIds, movedId, over.id, over.edge);
      if (order) patch({ order });
    }
    return true;
  };
  /* The board is the surface that asked for a finger, so it is the only one
     that reads a touch differently from a cursor. */
  const boardHand = useWsHand({ canDrag: view.type === 'board', onDrop: boardDrop,
    touchHold: true, travel: true });

  /* One closure, read twice: a column header's `+` and a rail row's `+` start
     the same project standing in the same place, and the only difference between
     them is whether the board happened to be drawing that place. */
  /* The seed carries only what the column actually SAID — the sheet fills its
     own defaults for everything unmentioned, and "said null" (the + under an
     empty column) is different from "did not say" (`'status' in seed`). */
  const newIn = (columnId) => setBeginning({
    ...(axis === 'status' ? { status: wsSeedStatus(columnId, spine) } : {}),
    ...(axis === 'priority' ? { priority: columnId === 'empty' ? null : columnId } : {}),
    ...(axis === 'pursuit' && columnId !== 'empty' ? { pursuitId: columnId } : {}),
    domainId: axis === 'domain' && columnId !== 'empty' ? columnId : initialDomain });

  const board = axis
    ? <div className="md-scroll" style={{ flex: 1, minHeight: 0, display: 'flex', gap: 9,
      alignItems: 'stretch', overflowX: 'auto', overflowY: 'hidden', padding: '10px 12px 14px' }}>
      {columns.map((group) => <WsColumn key={group.id} group={group} accent={accent}
        mark={columnMark(group)} hand={boardHand} meta={cardMeta}
        droppable={canHold(group.id)}
        placed={!view.sorts.length}
        onOpen={openProject} onNew={newIn} />)}
      {/* Last, because it is the end of the board. */}
      {!!places.length && <WsEmptyRail places={places} axis={axis} accent={accent}
        hand={boardHand} onNew={newIn} />}
    </div>
    /* A board with nothing to stack by is one long column, which is a list
       wearing a border. The way out is on the screen rather than back inside
       the menu the choice was made in. */
    : <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
      <span style={{ color: inkP(0.5), fontSize: 13.5 }}>Choose how the columns stack.</span>
      <div style={{ display: 'flex', gap: 7 }}>
        {(window.MD_WORKSHOP_GROUP_BY || []).map((option) => <button key={option} type="button"
          aria-label={`Group by ${WS_FIELD_LABEL[option].toLowerCase()}`}
          onClick={() => patch({ groupBy: option })}
          style={{ height: 32, border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
            padding: '0 13px', cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 620,
            background: rgbaP(accent, light ? 0.12 : 0.16), color: inkP(0.86) }}>
          {WS_FIELD_LABEL[option]}</button>)}
      </div>
    </div>;

  /* ── the timeline ────────────────────────────────────────────────────────
     The shared surface (md-timeline.jsx), fed the same rows the list and the
     board draw. A grouped view hands it SECTIONS — the same groups, the same
     marks, the same counts, the empty bucket last, as everywhere — and the
     surface lays them on its own ladder.

     A write goes through the span door, which refuses in words — and the span
     is ONE write for both ends, so a drag is one undo step rather than a start
     and a target that can disagree if the second one fails (W-1). */
  const wsTimelineRow = (row) => ({ id: row.id, name: row.name, start: row.start || null,
    target: row.target || null, color: domColorP(row.domainId, light),
    status: row.status || null, domainId: row.domainId, pursuitId: row.pursuitId || null });
  const timelineRows = useMeP(() => groups.reduce((all, group) => all.concat(group.rows), [])
    .map(wsTimelineRow), [groups, light]);
  const timelineSections = useMeP(() => (view.groupBy ? groups.map((group) => ({
    id: group.id, label: group.label, count: group.rows.length,
    mark: wsAxisMark(view.groupBy, group, light, accent),
    rows: group.rows.map(wsTimelineRow),
  })) : null), [groups, view.groupBy, light, accent]);

  /* The rail's chips: the properties Display shows, as small live buttons.
     Only the three that ARE buttons here — a date belongs to the canvas the
     row already is, and a count is not a thing a chip can set. */
  const railFields = view.visibleFields
    .filter((field) => field === 'status' || field === 'domain' || field === 'pursuit');
  const railWrite = (field, id, value) => {
    wsCommit(field === 'status' ? window.mdProjectTrySetStatus(id, value)
      : field === 'pursuit' ? window.mdProjectTrySetPursuit(id, value)
        : window.mdProjectTrySetDomain(id, value));
    again();
  };

  const timeline = window.MdTimeline ? <window.MdTimeline rows={timelineRows} todayKey={todayKey}
    zoom={view.timelineZoom} onZoom={(next) => patch({ timelineZoom: next })}
    rtl={typeof document !== 'undefined' && document.documentElement.dir === 'rtl'}
    accent={accent} onOpen={openProject}
    onWrite={(id, span) => { wsCommit(window.mdProjectTrySetSpan(id, span.start, span.target)); again(); }}
    rail={railFields.length ? (row) => railFields.map((field) => <WsRailChip key={field}
      row={row} field={field} accent={accent} onWrite={railWrite} />) : null}
    sections={timelineSections}
    railDrag={view.sorts.length ? null : {
      start: railHand.start,
      lift: railHand.drag ? railHand.drag.id : null,
      over: railHand.drag ? railHand.drag.over : null,
      edge: railHand.drag ? railHand.drag.edge : null,
      swallow: railHand.swallowClick,
    }}
    empty="Nothing here has days yet. Drag across a row to give one a span." /> : null;

  const body = <>
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0, gap: 2,
      padding: '0 8px', flex: 'none', borderBottom: `1px solid ${inkP(light ? 0.07 : 0.09)}` }}>
      <div role="tablist" aria-label="Views"
        style={{ display: 'flex', alignItems: 'center', gap: 3, flex: '1 1 160px', minWidth: 0,
          height: 40, overflowX: 'auto' }}>
        {views.map((row) => {
          const on = row.id === view.id;
          return [<button key={row.id} type="button" role="tab" aria-selected={on ? 'true' : 'false'}
            data-md-view-type={row.type} onClick={() => chooseView(row.id)}
            style={{ height: 29, flex: 'none', border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
              padding: '0 9px', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 620,
              whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5,
              color: inkP(on ? 0.88 : 0.46),
              background: on ? rgbaP(accent, light ? 0.12 : 0.16) : 'transparent',
              boxShadow: on ? `inset 0 -1.5px ${rgbaP(accent, 0.9)}` : 'none' }}>
            {DbGlyph ? <DbGlyph name={row.type} size={13} color={inkP(on ? 0.7 : 0.4)} /> : null}
            {row.name}</button>,
          /* The active view's own words, one press away — the same committed
             grammar every rename in the Workshop already trusts. */
          on && !draft ? <button key={`${row.id}-edit`} type="button" aria-label="Edit view"
            title="Edit view" aria-pressed={editHead ? 'true' : 'false'}
            onClick={() => setEditHead(!editHead)}
            style={{ height: 22, width: 22, flex: 'none', border: 'none', borderRadius: 6,
              cursor: 'pointer', font: 'inherit', fontSize: 11, background: 'transparent',
              color: inkP(editHead ? 0.8 : 0.42) }}>
            <span aria-hidden="true">✎</span></button> : null];
        })}
        {/* The thought, standing in the strip before it exists — dashed,
            because it is not yet a thing. */}
        {draft && <span data-md-draft style={{ height: 27, flex: 'none', display: 'inline-flex',
          alignItems: 'center', gap: 5, padding: '0 9px', whiteSpace: 'nowrap',
          borderRadius: 'var(--md-radius-control, 8px)', border: `1.5px dashed ${rgbaP(accent, 0.55)}`,
          color: inkP(0.85), fontSize: 12, fontWeight: 620 }}>
          {DbGlyph ? <DbGlyph name={draft.type} size={13} color={inkP(0.6)} /> : null}
          {draft.name.trim() || 'New view'}</span>}
        <WsTool label="New view" active={!!draft} onClick={startDraft}>
          {MDIP.plus ? MDIP.plus(inkP(0.5), 13) : <span aria-hidden="true">+</span>}
        </WsTool>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 40, marginInlineStart: 'auto' }}>
        {/* An icon until it is a question, sitting before Filter. While text
            stands the field stays — a blur must not swallow a question
            mid-answer — and Escape withdraws it in one press without the
            window hearing the key. */}
        {searchOpen || search !== '' ? (
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
            aria-label="Search projects" placeholder="Search" autoFocus ref={searchField}
            onBlur={() => { if (search === '') setSearchOpen(false); }}
            style={{ width: 118, height: 29, flex: 'none', border: 'none',
              borderBottom: `1px solid ${inkP(light ? 0.12 : 0.16)}`, outline: 'none', background: 'transparent',
              color: inkP(0.86), font: 'inherit', fontSize: 12.5, padding: '0 3px' }} />
        ) : (
          <WsTool label="Search projects" onClick={() => setSearchOpen(true)}>
            {DbGlyph ? <DbGlyph name="search" size={15} /> : null}
          </WsTool>
        )}
        <WsTool label="Filter" active={!!ask || view.filters.length > 0}
          text={view.filters.length ? String(view.filters.length) : null}
          onClick={(event) => setAsk(ask ? null : { at: -1, field: null, ...wsMenuPlace(event) })}>
          {DbGlyph ? <DbGlyph name="filter" size={15} /> : null}
        </WsTool>
        <WsAnchor>
          <WsTool label="Display" active={menu === 'display'}
            onClick={() => setMenu(menu === 'display' ? null : 'display')}>
            {DbGlyph ? <DbGlyph name="sliders" size={15} /> : null}
          </WsTool>
          {menu === 'display' && displayMenu}
        </WsAnchor>
      </div>
    </div>

    {/* The view's own words. For a draft: live fields, and the two verbs —
        Cancel discards what was never written, Save writes the whole thought
        as one view. For a saved view: the same fields through the committed
        input every rename already trusts, writing in place. */}
    {(draft || editHead) && <div data-md-view-head style={{ display: 'flex', alignItems: 'center',
      gap: 9, flexWrap: 'wrap', flex: 'none', padding: '8px 12px 7px',
      borderBottom: `1px solid ${inkP(light ? 0.06 : 0.08)}`,
      animation: wsStill() ? 'none' : 'mdDbSpring .18s cubic-bezier(.22,.8,.25,1)' }}>
      {DbGlyph ? <DbGlyph name={view.type} size={15} color={inkP(0.5)} /> : null}
      {draft ? <>
        <input aria-label="View name" placeholder="New view" autoFocus value={draft.name}
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          style={{ height: 30, flex: '0 1 190px', minWidth: 120, border: 'none', outline: 'none',
            borderBottom: `1px solid ${inkP(light ? 0.14 : 0.18)}`, background: 'transparent',
            color: inkP(0.9), font: 'inherit', fontSize: 13, fontWeight: 640, padding: '0 3px' }} />
        <input aria-label="View description" placeholder="Description (optional)"
          value={draft.description}
          onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          style={{ height: 30, flex: '1 1 210px', minWidth: 150, border: 'none', outline: 'none',
            borderBottom: `1px solid ${inkP(light ? 0.09 : 0.12)}`, background: 'transparent',
            color: inkP(0.72), font: 'inherit', fontSize: 12, padding: '0 3px' }} />
        <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 6, flex: 'none' }}>
          <button type="button" aria-label="Cancel" onClick={cancelDraft}
            style={{ height: 26, border: 'none', borderRadius: 7, padding: '0 10px',
              cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 600,
              background: 'transparent', color: inkP(0.55) }}>Cancel</button>
          <button type="button" aria-label="Save" onClick={saveDraft}
            style={{ height: 26, border: 'none', borderRadius: 7, padding: '0 12px',
              cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 650,
              background: rgbaP(accent, light ? 0.14 : 0.2), color: inkP(0.9) }}>Save</button>
        </span>
      </> : <>
        <DbCommittedInput aria-label="View name" placeholder="New view" value={view.name}
          commit={(name) => { patch({ name }); return { ok: true }; }}
          style={{ height: 30, flex: '0 1 190px', minWidth: 120, border: 'none', outline: 'none',
            borderBottom: `1px solid ${inkP(light ? 0.14 : 0.18)}`, background: 'transparent',
            color: inkP(0.9), font: 'inherit', fontSize: 13, fontWeight: 640, padding: '0 3px' }} />
        <DbCommittedInput aria-label="View description" placeholder="Description (optional)"
          value={view.description || ''}
          commit={(description) => { patch({ description }); return { ok: true }; }}
          style={{ height: 30, flex: '1 1 210px', minWidth: 150, border: 'none', outline: 'none',
            borderBottom: `1px solid ${inkP(light ? 0.09 : 0.12)}`, background: 'transparent',
            color: inkP(0.72), font: 'inherit', fontSize: 12, padding: '0 3px' }} />
        {/* The row that holds a view's name is the row that can end it. The
            bin asks once — armed, it says the word instead of wearing the
            glyph, and the hand leaving it is answer enough to disarm. The
            mark closes the editing; it is the affirmative, so it sits last. */}
        <span style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center',
          gap: 10, flex: 'none' }}>
          {views.length > 1 && <button type="button" data-md-head-delete
            aria-label={armed ? 'Delete view — press again' : 'Delete view'}
            title={armed ? 'Press again to delete' : 'Delete view'}
            onBlur={() => setArmed(false)}
            onClick={() => { if (!armed) { setArmed(true); return; } deleteView(); }}
            style={{ height: 26, minWidth: 28, border: 'none', borderRadius: 7,
              padding: armed ? '0 9px' : 0, cursor: 'pointer', font: 'inherit',
              fontSize: 11, fontWeight: 620, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', flex: 'none',
              background: armed ? rgbaP(WS_DANGER(light), light ? 0.1 : 0.16) : 'transparent',
              color: armed ? WS_DANGER(light) : inkP(0.4) }}>
            {armed ? 'Delete?'
              : (MDIP.trash ? MDIP.trash(inkP(0.4), 14) : <span aria-hidden="true">🗑</span>)}
          </button>}
          <button type="button" data-md-head-done aria-label="Done" title="Done"
            onClick={() => { setArmed(false); setEditHead(false); }}
            style={{ height: 26, minWidth: 28, border: 'none', borderRadius: 7,
              padding: 0, cursor: 'pointer', font: 'inherit', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', flex: 'none',
              background: 'transparent', color: rgbaP(accent, 0.95) }}>
            {MDIP.check ? MDIP.check(rgbaP(accent, 0.95), 15)
              : <span aria-hidden="true">✓</span>}
          </button>
        </span>
      </>}
    </div>}

    {/* What is being left out, said in one line rather than left to be
        discovered by a project that is not where somebody put it. */}
    {(view.filters.length > 0 || view.showClosed || view.groupBy) &&
      <div className="md-db-chipbar" style={{ display: 'flex', alignItems: 'center', gap: 5,
        flex: 'none', padding: '7px 10px 2px', overflowX: 'auto' }}>
        {view.groupBy && <button type="button" onClick={() => setMenu('display')}
          style={{ height: 25, flex: 'none', border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
            padding: '0 8px', cursor: 'pointer', font: 'inherit', fontSize: 10.5, whiteSpace: 'nowrap',
            background: inkP(light ? 0.05 : 0.07), color: inkP(0.7) }}>
          Grouped by {WS_FIELD_LABEL[view.groupBy].toLowerCase()}</button>}
        {/* The word between two chips is where somebody looks to find out how
            they are being read, so that is where the control is — and only the
            FIRST one is a control. Two toggles that must always agree is two
            chances to be looking at one that is wrong. */}
        {view.filters.map((filter, at) => [
          at > 0 && (at === 1
            ? <button key={`j${at}`} type="button" data-md-joint onClick={flipJoin}
                aria-label="Match all or any" title={joinWord === 'or'
                  ? 'Matching any of these — press for all' : 'Matching all of these — press for any'}
                style={{ height: 25, flex: 'none', border: 'none', background: 'transparent',
                  padding: '0 2px', cursor: 'pointer', font: 'inherit', fontSize: 10.5,
                  fontStyle: 'italic', color: inkP(0.5), textDecoration: 'underline',
                  textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>{joinWord}</button>
            : <span key={`j${at}`} data-md-joint style={{ height: 25, flex: 'none', display: 'flex',
                alignItems: 'center', padding: '0 2px', fontSize: 10.5, fontStyle: 'italic',
                color: inkP(0.34) }}>{joinWord}</span>),
          <WsPill key={at} filter={filter} accent={accent}
            onAsk={(box) => setAsk({ at, field: filter.field, ...box })}
            onWrite={(op) => patch({ filters: view.filters.map((row, index) =>
              (index === at ? { ...row, operator: op } : row)) })}
            onRemove={() => { setAsk(null);
              patch({ filters: view.filters.filter((_, index) => index !== at) }); }} />,
        ])}
        {view.filters.length > 0 && <button type="button" data-md-pill-add
          aria-label="Add a filter" title="Add a filter"
          onClick={(event) => setAsk(ask ? null : { at: -1, field: null, ...wsMenuPlace(event) })}
          style={{ height: 25, width: 25, flex: 'none', border: 'none',
            borderRadius: 'var(--md-radius-control, 8px)', cursor: 'pointer', font: 'inherit',
            fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', color: inkP(0.45) }}>
          {MDIP.plus ? MDIP.plus(inkP(0.45), 12) : <span aria-hidden="true">+</span>}
        </button>}
        {view.showClosed && <span style={{ height: 25, flex: 'none', display: 'flex', alignItems: 'center',
          padding: '0 8px', fontSize: 10.5, color: inkP(0.5), whiteSpace: 'nowrap' }}>Closed included</span>}
        {/* The end of the rail takes the whole rail back. The hidden line's
            Clear is only reachable while something is hidden — a question that
            hides nothing is still a question somebody may be done with. Same
            verb, same reach: filters and the typed search, never the display.

            Sticky, not merely last: five questions overflow this row, and a
            control that scrolls out of the frame is a control somebody has to
            go looking for. It pins to the trailing edge and the pills pass
            under it — the same near-opaque panel ink the list header uses to
            let rows pass under IT. */}
        {view.filters.length > 0 && <span style={{ position: 'sticky', insetInlineEnd: 0,
          marginInlineStart: 'auto', flex: 'none', display: 'flex', alignItems: 'center',
          paddingInlineStart: 8,
          background: light ? 'rgba(248,249,250,0.92)' : 'rgba(16,18,22,0.92)' }}>
          <button type="button" data-md-rail-clear
            aria-label="Clear filters" title="Clear filters" onClick={clearAsking}
            style={{ height: 25, flex: 'none', border: 'none',
              borderRadius: 'var(--md-radius-control, 8px)', padding: '0 9px', cursor: 'pointer',
              font: 'inherit', fontSize: 10.5, fontWeight: 620, whiteSpace: 'nowrap',
              background: inkP(light ? 0.05 : 0.07), color: inkP(0.58) }}>Clear</button>
        </span>}
      </div>}

    {/* The asking menu hangs from whichever word opened it — a pill's field or
        value, the rail's +, or the toolbar's Filter — one component, several
        doors. `key` forgets the last visit: a menu opened fresh starts on the
        page it was asked for, not the page somebody else left it on. */}
    {ask && <WsAsk key={`${ask.at}:${ask.field || ''}`} view={effective} ctx={ctx} patch={patch}
      at={ask.at} field={ask.field} x={ask.x} y={ask.y} onClose={() => setAsk(null)} />}

    {view.type === 'board' ? board : view.type === 'timeline' ? timeline
      : <div className="md-scroll" ref={listScroll} style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 12px 28px' }}>
      <div style={{ minWidth: gridMin }}>
        {header}
        {/* ── D1 · one window over every group at once ────────────────────
            The list is ONE scroll container holding every group, so it cannot
            window group by group the way the board windows column by column —
            a virtualizer per group would be several lists each measuring the
            same scroller and disagreeing about where the top is.

            So the groups are flattened into one run of items, each one either a
            group's heading or a row, and that run is what gets windowed. It is
            the shape md-database.jsx arrived at for exactly the same reason
            (its `displayRows`), which is the point of borrowing its library. */}
        {listWindowed ? <div data-md-window-stack style={{ position: 'relative', width: '100%',
          height: listVirt.getTotalSize() }}>
          {listVirt.getVirtualItems().map((item) => { const entry = listItems[item.index];
            if (!entry) return null;
            return <div key={entry.key} data-md-window-seat
              style={{ position: 'absolute', top: 0, insetInlineStart: 0, insetInlineEnd: 0,
                transform: `translateY(${item.start}px)` }}>
              {entry.kind === 'group'
                ? <div data-md-group={entry.group.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 6px 7px' }}>
                  {wsAxisMark(view.groupBy, entry.group, light, accent)}
                  <span style={{ color: inkP(0.82), fontSize: 12.5, fontWeight: 640 }}>{entry.group.label}</span>
                  <span data-md-group-count style={{ color: inkP(0.3), fontSize: 11.5,
                    fontVariantNumeric: 'tabular-nums' }}>{entry.group.rows.length}</span>
                </div>
                : rowLine(entry.row)}
            </div>; })}
        </div>
        : groups.map((group) => <div key={group.id} style={{ marginTop: view.groupBy ? 14 : 2 }}>
          {view.groupBy && <div data-md-group={group.id}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 6px 7px' }}>
            {wsAxisMark(view.groupBy, group, light, accent)}
            <span style={{ color: inkP(0.82), fontSize: 12.5, fontWeight: 640 }}>{group.label}</span>
            <span data-md-group-count style={{ color: inkP(0.3), fontSize: 11.5,
              fontVariantNumeric: 'tabular-nums' }}>{group.rows.length}</span>
          </div>}
          {group.rows.map(rowLine)}
        </div>)}
        {/* Not a scolding and not a zero: the same em-dash grammar the rest of
            the screen uses for a value that is not there yet. */}
        {!total && <div style={{ padding: '34px 8px', textAlign: 'center', color: inkP(0.42), fontSize: 13.5 }}>
          {search || view.filters.length ? 'Nothing here answers that.'
            : scope ? <>
              <div>Nothing under way in these pursuits.</div>
              {/* Never a blank rectangle with no way out: the lens that
                  emptied the screen is also the way back off it. */}
              <button type="button" data-md-lens-clear onClick={() => setScope(null)}
                style={{ marginTop: 10, height: 27, border: 'none',
                  borderRadius: 'var(--md-radius-control, 8px)', padding: '0 11px', cursor: 'pointer',
                  font: 'inherit', fontSize: 12, fontWeight: 620,
                  background: rgbaP(accent, light ? 0.14 : 0.2), color: inkP(0.86) }}>
                {WS_LENS_ALL}</button>
            </> : 'Nothing under way yet.'}
        </div>}
      </div>
    </div>}

    {/* What the asking hides, said in one quiet line under whichever body is
        drawn — with the one control that takes the asking back. Rows the
        DISPLAY hides (the closed, with Show closed off) are not in this
        number: nobody asked them away. Clear touches filters and search only;
        a display decision survives it by law (WS_DISPLAY_DEFAULTS's own). */}
    {hidden > 0 && <div data-md-hidden-line style={{ display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 8, flex: 'none', padding: '7px 12px 9px',
      borderTop: `1px solid ${inkP(light ? 0.06 : 0.08)}`, color: inkP(0.45), fontSize: 11.5 }}>
      <span dir="auto">{hidden} hidden by the asking</span>
      <button type="button" aria-label="Clear filters" onClick={clearAsking}
        style={{ height: 22, flex: 'none', border: 'none', borderRadius: 7, padding: '0 8px',
          cursor: 'pointer', font: 'inherit', fontSize: 11, fontWeight: 620,
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: rgbaP(accent, light ? 0.1 : 0.14), color: inkP(0.75) }}>
        Clear filters<span aria-hidden="true">×</span>
      </button>
    </div>}
  </>;

  /* ── the record screens ─────────────────────────────────────────────────
     They are the writing, and they stayed. Domain in particular: md-projects
     is the only door in the app to mdDomainTextWrite, so deleting its screen
     while W-12 repays seven orphaned mutators would be a poor trade. */
  const record = frame && <div className="md-scroll"
    style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 16px 32px' }}>
    {frame.kind === 'domain' && <DomainView id={frame.id} ctx={ctx} accent={accent} onBack={pop}
      onAgain={again}
      onOpenProject={openProject} onOpenPursuit={(id) => push({ kind: 'pursuit', id })}
      onNewPursuit={() => push({ kind: 'new-pursuit', domainId: frame.id })}
      onNewProject={() => setBeginning({ domainId: frame.id })}
      wikiRecords={wikiRecords} wikiBacklinksFor={wikiBacklinksFor}
      onWikiOpen={onWikiOpen} onWikiCreate={onWikiCreate} tagOptions={tagOptions} />}

    {frame.kind === 'pursuit' && <PursuitView id={frame.id} ctx={ctx} accent={accent} boards={boards}
      onBack={pop} onOpenProject={openProject} onOpenBoard={onOpenBoard} onNewBoard={onNewBoard}
      onOpenDomain={(domainId) => push({ kind: 'domain', id: domainId })} onAgain={again}
      onNewProject={() => { const pursuit = pursuitByIdP(frame.id);
        setBeginning({ pursuitId: frame.id, domainId: pursuit && pursuit.domainId }); }}
      wikiRecords={wikiRecords} onWikiOpen={onWikiOpen} onWikiCreate={onWikiCreate}
      tagOptions={tagOptions} />}

    {/* A project's own page and a backlog record's are frames of the PROJECT
        window now (md-project-space.jsx), reached from its title. They are the
        same two screens, published through window.MdWs rather than rebuilt —
        what changed is which window they are inside of. */}

    {frame.kind === 'new-pursuit' && <NewPursuit accent={accent} initialDomain={frame.domainId || null}
      onBack={pop} onCreated={(id) => { again(); replaceTop({ kind: 'pursuit', id }); }} />}
  </div>;

  const space = (
    <div ref={shell} data-md-projects-space data-md-size={size}
      /* One mark for all three hands: while anything is carried the window
         says so, and the sheet takes every hover wash off the surface. */
      data-md-dragging={listHand.drag || boardHand.drag || railHand.drag ? '' : undefined}
      className={light ? 'md-db-light' : ''}
      style={{ position: 'fixed', zIndex: 140, display: 'flex', flexDirection: 'column',
        overflow: 'hidden', '--db-accent': accent, '--ws-hover': inkP(light ? 0.04 : 0.05),
        /* Dark workspace windows deliberately share Tabs' near-opaque ground
           for this trial; light mode keeps its existing frosted material. */
        ...(window.mdFrostDress ? window.mdFrostDress(light) : {}),
        ...(window.mdSpaceWindowStyle
          ? window.mdSpaceWindowStyle(size, light, L.panel, resizable)
          : { inset: 0, borderRadius: 0 }) }}>

      {/* The window's name does not change when you open something inside it —
          the record screens bring their own title and their own Back, and two
          stacked titles leave the reader guessing which one owns the name. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 12px 8px', flexShrink: 0 }}>
        <span data-md-title-icon="projects" style={{ display: 'grid', placeItems: 'center', flex: 'none' }}>
          {MDIP.project ? MDIP.project(rgbaP(accent, 1), 22) : null}
        </span>
        <span style={{ color: inkP(1), fontSize: 19, fontWeight: 680,
          paddingInlineStart: 4 }}>Projects</span>
        {/* Which life this is. It stands in the row that NAMES the window
            because that is the row of doors, and because a lens switched
            twenty times a day does not belong four presses inside a filter
            menu. Gone inside a record frame, like the plus: a project's own
            screen is not read through a lens. */}
        {!frame && <WsLens scope={scope} accent={accent} open={!!lens}
          onOpen={(box) => setLens(box)} onClose={() => setLens(null)} />}
        {/* Beside the name, where the count used to sit. The count was a fact,
            but the plus is a door, and the name's row keeps doors — on the same
            32px grid the resize and close controls stand on, accent as ink
            rather than a filled pill. Gone while a record frame is open, the
            way the count was: the frame brings its own beginnings. */}
        {!frame && <button type="button" data-md-new="" aria-label="New project"
          /* A project begun inside one pursuit belongs to it — the same law
             the board's per-column plus already keeps. Across several the
             plus stays silent rather than guessing. */
          onClick={() => setBeginning(wsLensSeed(scope))}
          style={{ width: 32, height: 32, display: 'grid', placeItems: 'center', flex: 'none',
            border: 'none', background: 'transparent', cursor: 'pointer',
            borderRadius: 'var(--md-radius-control, 8px)', color: rgbaP(accent, 0.95) }}>
          {MDIP.plus ? MDIP.plus(rgbaP(accent, 0.95), 17) : <span aria-hidden="true">+</span>}
        </button>}
        <span style={{ flex: 1 }} />
        {resizable && <button type="button" onClick={resize}
          aria-label={size === 'full' ? 'Shrink to the board' : 'Expand to full screen'}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', width: 32, height: 32,
            display: 'grid', placeItems: 'center', color: inkP(0.55) }}>
          {DbGlyph ? <DbGlyph name={size === 'full' ? 'shrink' : 'expand'} size={16} /> : null}
        </button>}
        <window.MdDismiss onClick={onClose} label="Close Projects"
          style={{ marginInlineEnd: -4 }} />
      </div>

      {/* The lens's menu hangs from the button's own box, on the same escape
          stack every other menu in the app stands on. */}
      {!frame && lens && <WsLensMenu scope={scope} onScope={setScope} view={effective} ctx={ctx}
        accent={accent} x={lens.x} y={lens.y} onClose={() => setLens(null)} />}

      {frame ? record : body}

      {/* The product finishes the gesture (PP-10). Painted here rather than
          borrowed from `.md-db-notice`, whose light twin is scoped to the
          database's own surface class and would leave a dark box on a white
          page — the trap W-5 came through. */}
      {notice && <div data-md-notice role="status"
        style={{ position: 'absolute', zIndex: 85, bottom: 14, insetInline: 0, margin: '0 auto',
          width: 'fit-content', maxWidth: 'calc(100% - 28px)', display: 'flex', alignItems: 'center',
          gap: 10, padding: '9px 13px', borderRadius: 'var(--md-radius-surface, 12px)',
          border: `1px solid ${notice.undoable ? rgbaP(accent, 0.3) : inkP(light ? 0.1 : 0.14)}`,
          background: light ? 'rgba(255,255,255,.97)' : 'rgba(28,30,38,.96)',
          boxShadow: '0 10px 28px rgba(0,0,0,.28)', color: inkP(0.86), fontSize: 11.5,
          animation: 'mdDbSpring .22s cubic-bezier(.22,.8,.25,1)' }}>
        <span>{notice.message}</span>
        {notice.undoable && undo.current && <button type="button"
          onClick={() => { const held = undo.current; undo.current = null; dismissNotice();
            if (!held) return;
            wsCommit(axis === 'status' ? window.mdProjectTrySetStatus(held.id, held.value)
              : axis === 'priority' ? window.mdProjectTrySetPriority(held.id, held.value)
                : axis === 'pursuit' ? window.mdProjectTrySetPursuit(held.id, held.value)
                  : window.mdProjectTrySetDomain(held.id, held.value));
            again(); }}
          style={{ height: 22, flex: 'none', border: 'none', borderRadius: 7, padding: '0 9px',
            cursor: 'pointer', font: 'inherit', fontSize: 10.5, fontWeight: 650,
            background: rgbaP(accent, light ? 0.14 : 0.22), color: inkP(0.9) }}>Undo</button>}
      </div>}

      {/* Whatever is in the hand, hung last so it is over everything it is
          being carried across, and inside the window so it stops at the glass:
          outside this window it is not yours to move. Only one of the three
          can be live at a time — a hand not carrying anything renders
          nothing. */}
      {listHand.carried}{boardHand.carried}{railHand.carried}

      {/* Over the window rather than instead of it. It portals to the body
          itself, so what it silences is this window — which is the difference
          between a modal and a frame, and the whole of B-5. */}
      {beginning && <NewProjectSheet accent={accent} seed={beginning}
        tagOptions={tagOptions} wikiRecords={wikiRecords} onWikiOpen={onWikiOpen}
        onWikiCreate={onWikiCreate}
        onClose={() => setBeginning(null)}
        onCreated={(id) => { setBeginning(null); again(); openProject(id); }} />}
    </div>);

  return ReactDOM.createPortal(space, document.body);
}

/* `mdWsSlide` is arithmetic, and it is published for the same reason
   `minToHHMM` is (md-grid.jsx): the thing worth proving about a slide is the
   distance, and the distance can be proved without a browser that lays
   anything out. */
/* NewProjectSheet is published because it is a SHEET now rather than a piece of
   the Projects window, and a sheet stands on <body> above everything — so any
   surface that can begin a project should be able to open it without owning
   this window (CS-8). npDraftWorkflow/npSeedStatus travel with it because they
   are the rule it keeps, and a test proves a rule best where the rule lives. */
Object.assign(window, { ProjectsSpace, ProjectStatus, ProjectPriority, mdWsSlide: wsSlide,
  mdWsScroller: wsScroller, NewProjectSheet, npDraftWorkflow, npSeedStatus });

/* ── THE PILL KIT, OUT WHERE THE RECORD SHEET CAN WEAR IT (P-2) ──────────────
   Marwan, 2026-08-21: "the New Project Sheet Looks and acts Perfect (Guard it
   please) — I want to clone it … and make it the sole layout template for
   compose sheets."

   The dangerous way to do that is to move these into a shared file. A move is
   an edit, an edit is a chance to improve one number "for both", and the sheet
   that was declared finished is the one that quietly changes. So nothing here
   moves and nothing here is touched: the parts are simply PUBLISHED, and the
   record sheet mounts the very same functions this sheet mounts. One
   implementation, two tenants — which is the only arrangement in which "same
   styles of buttons and interactions" stays true a month from now.

   md-compose.jsx loads BEFORE this file (index.html), so it reads them off
   `window` at render time — the door `MdPropsBand`, `ScheduleOverlay` and
   `MdTagInspector` already come through in both directions.

   `tests/beginning-surface.test.js` holds the freeze: the skeleton this sheet
   draws is written down there, and it must stay green untouched. */
Object.assign(window, { MdNp: {
  /* the pills */
  NpChoice, NpDay, NpTags, NpDomain, NpPursuit,
  /* the dress and the menu they wear, so a pill built elsewhere — the record's
     one date-and-duration control (P-4) — is the same object to look at and to
     press, rather than a near-miss drawn from the same description. */
  NpMenu, npBox, npPlace,
  /* the measure the crumb, the form, the writing and the ✓ all ride */
  npRail,
  /* the two vocabulary rules a draft keeps, which a record's draft keeps too */
  npDraftWorkflow, npSeedStatus,
} });

/* ── the Workshop's parts, out where a second window can reach them ─────────
   This file's own header says the right thing about md-database.jsx: its
   chrome was "published by W-0 so a second screen is built from the same parts
   rather than a copy of them." A Project window is that again, one level up —
   the same tabs strip, the same 32px control grid, the same hand, the same
   column, the same pill, with one project's records in them instead of every
   project.

   ONE DOOR, NOT TWENTY-SIX GLOBALS. `window.MdWs` is a namespace because these
   are parts of one room; scattering them across the global object would make
   the boundary invisible and the next name easy to add without noticing that a
   second window now depends on it.

   THE COMPOSITION IS NOT IN HERE. What is published is parts and arithmetic —
   no window, no shell, nothing that decides which tenant it is drawing. A
   shared shell with an `if` per difference reads as neither window, and the
   differences between these two are not small: different rows, different
   writes, different frames underneath, and three controls that are absent
   rather than disabled. */
/* Published on window rather than inside MdWs, following md-transfer.jsx: the
   words a delete says are app-wide vocabulary, not a Workshop surface, and
   stories 4.3, 4.6 and 4.8 all ask the same question. */
Object.assign(window, { WS_DELETE_KINDS, mdDeleteReach, mdDeleteSay });

Object.assign(window, { MdWs: {
  /* surfaces */
  WsColumn, WsEmptyRail, WsPill, WsAsk, WsTool, WsAnchor, WsSegment, WsControlRow,
  WsSwitch, WsDirArrow, WsMenuRow, WsRailChip, WsProp, WsPick, WsDay, WsActions,
  RecordHead, WrittenPage, StatusMark, PriorityMark, SubHeader, Tile, Quantity, Rule,
  /* The record screen the Project window shows underneath its board: the
     project's own page (the writing and the six properties, which used to BE
     the destination). Published rather than moved — it is a screen about a
     project, it belongs beside the other screens about a project, and a file
     move would be 200 lines of churn to say the same thing this line says. */
  ProjectView,
  /* the hand */
  useWsHand,
  /* the arithmetic under all of it */
  wsAxisMark, wsBoxPlace, wsMenuPlace, wsCommit, wsReorder, wsLanding, wsLandingLine,
  wsGapAt, wsWashes, wsSpaceBase, wsSeats, wsSlide, wsStill, wsScroller, wsOpen, wsRoom, wsChoices,
  wsOpLabel, wsValueLabel, wsFilterLabel, wsDayLabel,
  wsSpentLabel: spentLabel, wsStatusLabel: statusLabel, wsPriorityLabel: priorityLabel,
  /* the tables the words come out of */
  WS_FIELD_LABEL, WS_OPS_FOR, WS_MANY, WS_EMPTY_LABEL, WS_COL_WIDTH, WS_COL_MIN,
  WS_COL_GAP, WS_NARROW_FIELDS, WS_NARROW_RECORD_FIELDS, WS_DISPLAY_DEFAULTS,
  WS_DANGER, WS_NOTICE, WS_NONE: MD_NONE,
} });
